import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useModal } from '../context/ModalContext';
import { translations } from '../translations';
import api from '../utils/api';
import { compareArabic, normalizeArabic } from '../utils/arabicNormalize';
import { CalendarIcon, CheckIcon, ClockIcon, UserIcon } from '@heroicons/react/24/outline';

// Car Icon Component (not available in Heroicons)
const CarIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
  </svg>
);

const Admin = () => {
  const { language, toggleLanguage } = useLanguage();
  const { alert, confirm } = useModal();
  const t = translations[language];
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [cars, setCars] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [routePresets, setRoutePresets] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [selectedMethodId, setSelectedMethodId] = useState('');
  const [policySections, setPolicySections] = useState([]);
  const [editingPolicySection, setEditingPolicySection] = useState(null);
  const [loadingPolicySections, setLoadingPolicySections] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [cloudinaryImages, setCloudinaryImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [submittingBookingUpdate, setSubmittingBookingUpdate] = useState(false);
  const [showCarForm, setShowCarForm] = useState(false);
  const [showDriverForm, setShowDriverForm] = useState(false);
  const [showDestinationForm, setShowDestinationForm] = useState(false);
  const [showRoutePresetForm, setShowRoutePresetForm] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [editingDestination, setEditingDestination] = useState(null);
  const [editingRoutePreset, setEditingRoutePreset] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [submittingCar, setSubmittingCar] = useState(false);
  const [deletingImage, setDeletingImage] = useState(null);
  const [selectedCarImages, setSelectedCarImages] = useState([]);
  const [submittingDriver, setSubmittingDriver] = useState(false);
  const [deletingDriverImage, setDeletingDriverImage] = useState(false);
  const [selectedDriverLicenseImage, setSelectedDriverLicenseImage] = useState(null);
  const [selectedDestinationImage, setSelectedDestinationImage] = useState(null);
  const [showUnauthorizedWarning, setShowUnauthorizedWarning] = useState(false);

  // Filters and Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('newest');

  // View Mode (table or cards)
  const [viewMode, setViewMode] = useState({
    bookings: 'table',
    payments: 'table',
    reviews: 'cards',
    messages: 'cards',
    notifications: 'table'
  });

  // Notifications
  const [showCreateNotificationModal, setShowCreateNotificationModal] = useState(false);
  const [submittingNotification, setSubmittingNotification] = useState(false);
  const [notificationFormData, setNotificationFormData] = useState({
    type: 'info',
    message: '',
    isForAll: false,
    recipients: []
  });
  const [notificationUserSearchQuery, setNotificationUserSearchQuery] = useState('');
  const [notificationUsers, setNotificationUsers] = useState([]);
  const [showNotificationUserDropdown, setShowNotificationUserDropdown] = useState(false);
  const [selectedNotificationUsers, setSelectedNotificationUsers] = useState([]);
  const [loadingNotificationUsers, setLoadingNotificationUsers] = useState(false);
  const [selectedNotificationForStatus, setSelectedNotificationForStatus] = useState(null);
  const [notificationReadStatus, setNotificationReadStatus] = useState(null);

  // Create Booking Modal
  const [showCreateBookingModal, setShowCreateBookingModal] = useState(false);
  const [submittingBooking, setSubmittingBooking] = useState(false);
  const [bookingFormData, setBookingFormData] = useState({
    bookingType: 'anywhere',
    tripType: 'oneWay',
    pickupLocation: '',
    dropoffLocation: '',
    date: '',
    time: '',
    returnDate: '',
    returnTime: '',
    passengers: 1,
    luggage: 0,
    carType: '',
    carId: '', // For specific car selection
    driverId: '', // For driver selection
    totalCost: 0, // Price field
    fullName: '',
    mobile: '',
    whatsapp: '',
    notes: '',
    status: 'Pending',
    userId: '' // Optional - for linking to existing user
  });

  // User Selector
  const [users, setUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Route Presets for booking form
  const [presets, setPresets] = useState([]);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [dropoffLocations, setDropoffLocations] = useState([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);
  const [filteredPickupSuggestions, setFilteredPickupSuggestions] = useState([]);
  const [filteredDropoffSuggestions, setFilteredDropoffSuggestions] = useState([]);
  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);
  const pickupDropdownRef = useRef(null);
  const dropoffDropdownRef = useRef(null);

  // Create Payment Modal
  const [showCreatePaymentModal, setShowCreatePaymentModal] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    bookingId: '',
    paymentMethod: 'bankTransfer',
    amount: '',
    status: 'Unpaid',
    proofImage: null
  });

  // Booking Selector for Payments
  const [bookingSearchResults, setBookingSearchResults] = useState([]);
  const [bookingSearchQuery, setBookingSearchQuery] = useState('');
  const [showBookingDropdown, setShowBookingDropdown] = useState(false);
  const [selectedBookingForPayment, setSelectedBookingForPayment] = useState(null);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // Analytics
  const [analytics, setAnalytics] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    averageBookingValue: 0,
    bookingTrends: [],
    popularCarTypes: {},
    topDestinations: []
  });

  // Handle sidebar on small screens - close by default, open on large screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        // Large screens (lg and above) - sidebar should be open
        setSidebarOpen(true);
      } else {
        // Small screens - sidebar should be closed
        setSidebarOpen(false);
      }
    };

    // Set initial state based on screen size
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch cars, drivers, and route presets when create booking modal opens
  useEffect(() => {
    if (showCreateBookingModal) {
      const fetchData = async () => {
        try {
          const [carsRes, driversRes, presetsRes] = await Promise.all([
            api.get('/cars'),
            api.get('/drivers'),
            api.get('/route-presets')
          ]);
          setCars(carsRes.data);
          setDrivers(driversRes.data);
          setPresets(presetsRes.data);

          // Get unique pickup locations
          const pickups = [...new Set(presetsRes.data.map(p => p.pickupLocation))].sort();
          setPickupLocations(pickups);
          setFilteredPickupSuggestions(pickups);
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };
      fetchData();
    }
  }, [showCreateBookingModal]);

  // Fetch cars and drivers when edit booking modal opens
  useEffect(() => {
    if (editingBooking) {
      const fetchCarsAndDrivers = async () => {
        try {
          const [carsRes, driversRes] = await Promise.all([
            api.get('/cars'),
            api.get('/drivers')
          ]);
          setCars(carsRes.data);
          setDrivers(driversRes.data);
        } catch (error) {
          console.error('Error fetching cars and drivers:', error);
        }
      };
      fetchCarsAndDrivers();
    }
  }, [editingBooking]);

  // Fetch users when searching
  useEffect(() => {
    if (showCreateBookingModal && userSearchQuery.length >= 2) {
      const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
          const usersRes = await api.get(`/admin/users?search=${encodeURIComponent(userSearchQuery)}`);
          setUsers(usersRes.data);
          setShowUserDropdown(true);
        } catch (error) {
          console.error('Error fetching users:', error);
        } finally {
          setLoadingUsers(false);
        }
      };

      const debounceTimer = setTimeout(() => {
        fetchUsers();
      }, 300);

      return () => clearTimeout(debounceTimer);
    } else if (userSearchQuery.length === 0) {
      setUsers([]);
      setShowUserDropdown(false);
    }
  }, [userSearchQuery, showCreateBookingModal]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-selector-container')) {
        setShowUserDropdown(false);
      }
      if (showBookingDropdown && !event.target.closest('.booking-selector-container')) {
        setShowBookingDropdown(false);
      }
      if (showNotificationUserDropdown && !event.target.closest('.notification-user-selector-container')) {
        setShowNotificationUserDropdown(false);
      }
      if (showPickupSuggestions && !event.target.closest('.pickup-location-container')) {
        setShowPickupSuggestions(false);
      }
      if (showDropoffSuggestions && !event.target.closest('.dropoff-location-container')) {
        setShowDropoffSuggestions(false);
      }
    };

    if (showCreateBookingModal || showCreatePaymentModal || showCreateNotificationModal) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserDropdown, showBookingDropdown, showNotificationUserDropdown, showPickupSuggestions, showDropoffSuggestions, showCreateBookingModal, showCreatePaymentModal, showCreateNotificationModal]);

  // Scroll to top on tab change
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [activeTab]);

  // Scroll to top when modals open
  useEffect(() => {
    if (selectedBooking || editingBooking || showCreateBookingModal || showCreatePaymentModal) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [selectedBooking, editingBooking, showCreateBookingModal, showCreatePaymentModal]);

  // Fetch bookings when searching for payment
  useEffect(() => {
    if (showCreatePaymentModal && bookingSearchQuery.length >= 2) {
      const fetchBookings = async () => {
        setLoadingBookings(true);
        try {
          const bookingsRes = await api.get('/bookings');
          // Filter bookings by search query (name, mobile, booking ID)
          const query = bookingSearchQuery.toLowerCase();
          const filtered = bookingsRes.data.filter(b =>
            b.fullName?.toLowerCase().includes(query) ||
            b.mobile?.includes(query) ||
            b._id?.toLowerCase().includes(query) ||
            b.pickupLocation?.toLowerCase().includes(query) ||
            b.dropoffLocation?.toLowerCase().includes(query)
          ).slice(0, 10); // Limit to 10 results
          setBookingSearchResults(filtered);
          setShowBookingDropdown(true);
        } catch (error) {
          console.error('Error fetching bookings:', error);
        } finally {
          setLoadingBookings(false);
        }
      };

      const debounceTimer = setTimeout(() => {
        fetchBookings();
      }, 300);

      return () => clearTimeout(debounceTimer);
    } else if (bookingSearchQuery.length === 0) {
      setBookingSearchResults([]);
      setShowBookingDropdown(false);
    }
  }, [bookingSearchQuery, showCreatePaymentModal]);

  // Fetch users when searching for notifications
  useEffect(() => {
    if (showCreateNotificationModal && notificationUserSearchQuery.length >= 2) {
      const fetchUsers = async () => {
        setLoadingNotificationUsers(true);
        try {
          const usersRes = await api.get(`/admin/users?search=${encodeURIComponent(notificationUserSearchQuery)}`);
          setNotificationUsers(usersRes.data);
          setShowNotificationUserDropdown(true);
        } catch (error) {
          console.error('Error fetching users:', error);
        } finally {
          setLoadingNotificationUsers(false);
        }
      };

      const debounceTimer = setTimeout(() => {
        fetchUsers();
      }, 300);

      return () => clearTimeout(debounceTimer);
    } else if (notificationUserSearchQuery.length === 0) {
      setNotificationUsers([]);
      setShowNotificationUserDropdown(false);
    }
  }, [notificationUserSearchQuery, showCreateNotificationModal]);

  // Calculate analytics from bookings data
  const calculateAnalytics = (bookingsData) => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    // Filter only Confirmed bookings for revenue calculations
    const confirmedBookings = bookingsData.filter(b => b.status === 'Confirmed');

    const totalRevenue = confirmedBookings.reduce((sum, b) => sum + (b.totalCost || 0), 0);
    const monthlyBookings = confirmedBookings.filter(b => new Date(b.createdAt) >= startOfMonth);
    const monthlyRevenue = monthlyBookings.reduce((sum, b) => sum + (b.totalCost || 0), 0);
    const averageBookingValue = confirmedBookings.length > 0 ? totalRevenue / confirmedBookings.length : 0;

    // Booking trends (last 7 days)
    const trends = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayStart = new Date(date.setHours(0, 0, 0, 0));
      const dayEnd = new Date(date.setHours(23, 59, 59, 999));
      const count = bookingsData.filter(b => {
        const bookingDate = new Date(b.createdAt);
        return bookingDate >= dayStart && bookingDate <= dayEnd;
      }).length;
      trends.push({ date: dayStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), count });
    }

    // Popular car types
    const carTypes = {};
    bookingsData.forEach(b => {
      carTypes[b.carType] = (carTypes[b.carType] || 0) + 1;
    });

    // Top destinations
    const destinations = {};
    bookingsData.forEach(b => {
      const dest = b.dropoffLocation;
      destinations[dest] = (destinations[dest] || 0) + 1;
    });
    const topDestinations = Object.entries(destinations)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));

    setAnalytics({
      totalRevenue,
      monthlyRevenue,
      averageBookingValue,
      bookingTrends: trends,
      popularCarTypes: carTypes,
      topDestinations
    });
  };

  // Define fetchData after calculateAnalytics is defined
  const fetchData = useCallback(async () => {
    try {
      const [statsRes, bookingsRes, settingsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/bookings'),
        api.get('/settings')
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setSettings(settingsRes.data);

      // Calculate analytics
      calculateAnalytics(bookingsRes.data);

      if (activeTab === 'cars') {
        const carsRes = await api.get('/cars/all');
        setCars(carsRes.data);
      }
      if (activeTab === 'drivers') {
        const driversRes = await api.get('/drivers');
        setDrivers(driversRes.data);
      }
      if (activeTab === 'destinations') {
        const destRes = await api.get('/destinations/all');
        setDestinations(destRes.data);
      }
      if (activeTab === 'routePresets') {
        const presetsRes = await api.get('/route-presets/all');
        setRoutePresets(presetsRes.data);
      }
      if (activeTab === 'payments') {
        const paymentsRes = await api.get('/payments');
        setPayments(paymentsRes.data);
      }
      if (activeTab === 'reviews') {
        const reviewsRes = await api.get('/reviews/all');
        setReviews(reviewsRes.data);
      }
      if (activeTab === 'messages') {
        const messagesRes = await api.get('/messages');
        setMessages(messagesRes.data);
      }
      if (activeTab === 'notifications') {
        const notificationsRes = await api.get('/notifications');
        setNotifications(notificationsRes.data);
      }
      if (activeTab === 'settings') {
        const paymentMethodsRes = await api.get('/payment-methods/all');
        setPaymentMethods(paymentMethodsRes.data);
        // Fetch Cloudinary images when settings tab is active
        fetchCloudinaryImages();
        // Fetch policy sections
        fetchPolicySections();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        setShowUnauthorizedWarning(true);
        // Redirect after 5 seconds
        setTimeout(() => {
          navigate('/');
        }, 5000);
      }
    }
    // Note: fetchCloudinaryImages and fetchPolicySections are defined later but
    // are only called at runtime, not during useCallback creation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, navigate]);

  // Initial data fetch - must be after fetchData is defined
  useEffect(() => {
    setMounted(true);
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [navigate, fetchData]);

  // Filter functions
  const getFilteredBookings = () => {
    let filtered = [...bookings];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b =>
        b.fullName?.toLowerCase().includes(query) ||
        b.pickupLocation?.toLowerCase().includes(query) ||
        b.dropoffLocation?.toLowerCase().includes(query) ||
        b.mobile?.includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }

    if (dateRange.start) {
      filtered = filtered.filter(b => new Date(b.date) >= new Date(dateRange.start));
    }

    if (dateRange.end) {
      filtered = filtered.filter(b => new Date(b.date) <= new Date(dateRange.end));
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortBy === 'date-asc') return new Date(a.date) - new Date(b.date);
      if (sortBy === 'date-desc') return new Date(b.date) - new Date(a.date);
      if (sortBy === 'amount-asc') return (a.totalCost || 0) - (b.totalCost || 0);
      if (sortBy === 'amount-desc') return (b.totalCost || 0) - (a.totalCost || 0);
      return 0;
    });

    return filtered;
  };

  const getFilteredPayments = () => {
    let filtered = [...payments];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.bookingId?._id?.toString().includes(query) ||
        p.paymentMethod?.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const getFilteredReviews = () => {
    let filtered = [...reviews];

    if (statusFilter === 'approved') {
      filtered = filtered.filter(r => r.isApproved);
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(r => !r.isApproved);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.customerName?.toLowerCase().includes(query) ||
        r.comment?.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const getFilteredMessages = () => {
    let filtered = [...messages];

    if (statusFilter === 'unread') {
      filtered = filtered.filter(m => !m.isRead);
    } else if (statusFilter === 'read') {
      filtered = filtered.filter(m => m.isRead);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m =>
        m.name?.toLowerCase().includes(query) ||
        m.email?.toLowerCase().includes(query) ||
        m.subject?.toLowerCase().includes(query) ||
        m.message?.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  // Export functions
  const exportToCSV = async (data, filename) => {
    if (!data || data.length === 0) {
      await alert(
        language === 'en' ? 'No data to export' : 'لا توجد بيانات للتصدير',
        language === 'en' ? 'No Data' : 'لا توجد بيانات',
        'warning'
      );
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return JSON.stringify(value);
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(','))
    ].join('\n');

    // Add UTF-8 BOM to ensure Excel recognizes Arabic characters correctly
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportBookings = () => {
    const data = getFilteredBookings().map(b => ({
      [language === 'en' ? 'Booking ID' : 'رقم الحجز']: b._id,
      [language === 'en' ? 'Customer Name' : 'اسم العميل']: b.fullName,
      [language === 'en' ? 'Phone' : 'الهاتف']: b.mobile,
      [language === 'en' ? 'Pickup Location' : 'موقع الاستلام']: b.pickupLocation,
      [language === 'en' ? 'Drop-off Location' : 'موقع التسليم']: b.dropoffLocation,
      [language === 'en' ? 'Date' : 'التاريخ']: new Date(b.date).toLocaleDateString(),
      [language === 'en' ? 'Time' : 'الوقت']: b.time,
      [language === 'en' ? 'Car Type' : 'نوع السيارة']: b.carType,
      [language === 'en' ? 'Status' : 'حالة الحجز']: b.status,
      [language === 'en' ? 'Total Cost' : 'التكلفة الإجمالية']: b.totalCost,
    }));
    exportToCSV(data, 'bookings');
  };

  const exportPayments = () => {
    const data = getFilteredPayments().map(p => ({
      [language === 'en' ? 'Payment ID' : 'رقم الدفع']: p._id,
      [language === 'en' ? 'Booking ID' : 'رقم الحجز']: p.bookingId?._id || p.bookingId,
      [language === 'en' ? 'Payment Method' : 'طريقة الدفع']: p.paymentMethod,
      [language === 'en' ? 'Amount' : 'المبلغ']: p.amount,
      [language === 'en' ? 'Status' : 'حالة الدفع']: p.status,
    }));
    exportToCSV(data, 'payments');
  };

  // Handle booking form change
  // Filter pickup suggestions based on input
  const filterPickupSuggestions = (query) => {
    if (!query || query.trim() === '') {
      setFilteredPickupSuggestions(pickupLocations);
      return;
    }
    const normalizedQuery = normalizeArabic(query.toLowerCase());
    const filtered = pickupLocations.filter(location => {
      const normalizedLocation = normalizeArabic(location.toLowerCase());
      return normalizedLocation.includes(normalizedQuery);
    });
    setFilteredPickupSuggestions(filtered);
  };

  // Filter dropoff suggestions based on input and selected pickup
  const filterDropoffSuggestions = (query) => {
    if (!query || query.trim() === '') {
      setFilteredDropoffSuggestions(dropoffLocations);
      return;
    }
    const normalizedQuery = normalizeArabic(query.toLowerCase());
    const filtered = dropoffLocations.filter(location => {
      const normalizedLocation = normalizeArabic(location.toLowerCase());
      return normalizedLocation.includes(normalizedQuery);
    });
    setFilteredDropoffSuggestions(filtered);
  };

  const handlePickupSelect = (location) => {
    setBookingFormData(prev => ({
      ...prev,
      pickupLocation: location,
      dropoffLocation: '', // Clear dropoff when pickup changes
      totalCost: 0 // Clear price when pickup changes
    }));
    setShowPickupSuggestions(false);

    // Filter dropoff locations based on selected pickup
    const dropoffs = presets
      .filter(p => compareArabic(p.pickupLocation, location) && p.isActive)
      .map(p => p.dropoffLocation);
    const uniqueDropoffs = [...new Set(dropoffs)].sort();
    setDropoffLocations(uniqueDropoffs);
    setFilteredDropoffSuggestions(uniqueDropoffs);
    setShowDropoffSuggestions(false);
  };

  const handleDropoffSelect = (location) => {
    setBookingFormData(prev => ({
      ...prev,
      dropoffLocation: location,
      totalCost: 0 // Reset price, will be auto-filled if preset exists and car is selected
    }));
    setShowDropoffSuggestions(false);

    // If car is already selected, try to find preset price
    if (bookingFormData.carId && bookingFormData.pickupLocation) {
      setTimeout(() => {
        findAndSetPresetPrice();
      }, 0);
    }
  };

  // Find and set preset price when locations and car are selected
  const findAndSetPresetPrice = () => {
    // Use current state values
    setBookingFormData(prev => {
      const { pickupLocation, dropoffLocation, carId } = prev;

      // Need both locations to find preset
      if (!pickupLocation || !dropoffLocation) {
        return prev;
      }

      // Find selected car to get its type
      let selectedCar = null;
      if (carId) {
        selectedCar = cars.find(c => c._id === carId);
      }

      if (!selectedCar) {
        return prev; // Can't find preset without car type
      }

      // Find matching presets for this route
      const matchingPresets = presets.filter(p =>
        compareArabic(p.pickupLocation, pickupLocation) &&
        compareArabic(p.dropoffLocation, dropoffLocation) &&
        p.isActive
      );

      if (matchingPresets.length === 0) {
        // No preset found - keep current price
        return prev;
      }

      // Try to find preset for specific car type first
      let preset = matchingPresets.find(p => p.carType === selectedCar.type);

      // If not found, try 'all' type
      if (!preset) {
        preset = matchingPresets.find(p => p.carType === 'all');
      }

      // If still not found, use the first matching preset
      if (!preset && matchingPresets.length > 0) {
        preset = matchingPresets[0];
      }

      if (preset) {
        let price = preset.price;

        // Save base price, round trip calculation happens on submit
        return {
          ...prev,
          totalCost: price
        };
      }

      return prev;
    });
  };

  const handleBookingFormChange = (e) => {
    const { name, value } = e.target;

    // Convert totalCost to number if it's the totalCost field
    const processedValue = name === 'totalCost' ? (value === '' ? 0 : parseFloat(value) || 0) : value;

    setBookingFormData(prev => ({ ...prev, [name]: processedValue }));

    // Handle pickup location change
    if (name === 'pickupLocation') {
      // Filter suggestions as user types
      filterPickupSuggestions(value);
      setShowPickupSuggestions(true);

      // Filter dropoff locations based on pickup (using Arabic normalization for smart matching)
      const dropoffs = presets
        .filter(p => compareArabic(p.pickupLocation, value) && p.isActive)
        .map(p => p.dropoffLocation);
      const uniqueDropoffs = [...new Set(dropoffs)].sort();
      setDropoffLocations(uniqueDropoffs);
      setFilteredDropoffSuggestions(uniqueDropoffs);
      setBookingFormData(prev => ({ ...prev, dropoffLocation: '', totalCost: 0 })); // Clear dropoff and price
      setShowDropoffSuggestions(false);
    }

    // Handle dropoff location change
    if (name === 'dropoffLocation') {
      // Filter suggestions as user types
      filterDropoffSuggestions(value);
      setShowDropoffSuggestions(true);
    }
  };

  // Auto-fill price when preset is selected (pickup, dropoff, and car)
  useEffect(() => {
    if (showCreateBookingModal && bookingFormData.pickupLocation && bookingFormData.dropoffLocation && bookingFormData.carId) {
      findAndSetPresetPrice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingFormData.pickupLocation, bookingFormData.dropoffLocation, bookingFormData.carId, showCreateBookingModal, cars, presets]);

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setBookingFormData(prev => ({
      ...prev,
      userId: user._id,
      fullName: user.name || prev.fullName,
      mobile: user.phone || prev.mobile
    }));
    setUserSearchQuery(user.name || user.email || '');
    setShowUserDropdown(false);
    setUsers([]);
  };

  // Clear user selection
  const handleClearUser = () => {
    setSelectedUser(null);
    setBookingFormData(prev => ({
      ...prev,
      userId: '',
      fullName: prev.fullName,
      mobile: prev.mobile
    }));
    setUserSearchQuery('');
    setShowUserDropdown(false);
    setUsers([]);
  };

  // Calculate total cost - now uses the price field directly
  // Note: This function is kept for potential future use
  // eslint-disable-next-line no-unused-vars
  const _calculateBookingCost = () => {
    let totalCost = parseFloat(bookingFormData.totalCost) || 0;

    // If round trip, multiply by the round trip multiplier from settings
    if (bookingFormData.tripType === 'roundTrip' && settings && totalCost > 0) {
      totalCost *= settings.roundTripMultiplier || 2;
    }

    return totalCost;
  };

  // Handle booking selection for payment
  const handleBookingSelect = (booking) => {
    setSelectedBookingForPayment(booking);
    setPaymentFormData(prev => ({
      ...prev,
      bookingId: booking._id,
      amount: booking.totalCost || prev.amount
    }));
    setBookingSearchQuery(`${booking.fullName} - ${booking.pickupLocation} to ${booking.dropoffLocation}`);
    setShowBookingDropdown(false);
    setBookingSearchResults([]);
  };

  // Clear booking selection
  const handleClearBooking = () => {
    setSelectedBookingForPayment(null);
    setPaymentFormData(prev => ({
      ...prev,
      bookingId: '',
      amount: ''
    }));
    setBookingSearchQuery('');
    setShowBookingDropdown(false);
    setBookingSearchResults([]);
  };

  // Handle payment form change
  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle payment image upload
  const handlePaymentImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setPaymentFormData(prev => ({ ...prev, proofImage: e.target.files[0] }));
    }
  };

  // Handle create payment submit
  const handleCreatePayment = async (e) => {
    e.preventDefault();
    setSubmittingPayment(true);

    try {
      const formData = new FormData();
      formData.append('bookingId', paymentFormData.bookingId);
      formData.append('paymentMethod', paymentFormData.paymentMethod);
      formData.append('amount', paymentFormData.amount);
      formData.append('status', paymentFormData.status);
      if (paymentFormData.proofImage) {
        formData.append('proofImage', paymentFormData.proofImage);
      }

      await api.post('/payments', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Reset form and close modal
      setPaymentFormData({
        bookingId: '',
        paymentMethod: 'bankTransfer',
        amount: '',
        status: 'Unpaid',
        proofImage: null
      });
      setSelectedBookingForPayment(null);
      setBookingSearchQuery('');
      setShowBookingDropdown(false);
      setBookingSearchResults([]);
      setShowCreatePaymentModal(false);
      fetchData();

      await alert(
        language === 'en' ? 'Payment created successfully!' : 'تم إنشاء الدفع بنجاح!',
        language === 'en' ? 'Success' : 'نجح',
        'success'
      );
    } catch (error) {
      console.error('Error creating payment:', error);
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error creating payment' : 'خطأ في إنشاء الدفع'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setSubmittingPayment(false);
    }
  };

  // Handle create booking submit
  const handleCreateBooking = async (e) => {
    e.preventDefault();
    setSubmittingBooking(true);

    try {
      // Get totalCost directly from the form input (current state)
      // Ensure we're reading the value as a number
      let totalCost = typeof bookingFormData.totalCost === 'number'
        ? bookingFormData.totalCost
        : parseFloat(bookingFormData.totalCost) || 0;

      console.log('🔍 Booking Form Data:', {
        totalCost: bookingFormData.totalCost,
        totalCostType: typeof bookingFormData.totalCost,
        parsedTotalCost: totalCost
      });

      // If round trip, multiply by the round trip multiplier from settings
      if (bookingFormData.tripType === 'roundTrip' && settings && totalCost > 0) {
        totalCost *= settings.roundTripMultiplier || 2;
      }

      // Find the selected car by carId or carType
      let selectedCar = null;
      if (bookingFormData.carId) {
        selectedCar = cars.find(c => c._id === bookingFormData.carId);
      } else if (bookingFormData.carType) {
        selectedCar = cars.find(c => c.type === bookingFormData.carType && c.isAvailable);
      }

      // Prepare booking data
      const bookingData = {
        ...bookingFormData,
        carType: selectedCar ? selectedCar.type : bookingFormData.carType,
        carId: selectedCar ? selectedCar._id : undefined,
        driverId: bookingFormData.driverId || undefined,
        totalCost, // Use the calculated totalCost
        date: new Date(bookingFormData.date).toISOString(),
        returnDate: bookingFormData.returnDate ? new Date(bookingFormData.returnDate).toISOString() : undefined,
        userId: bookingFormData.userId || undefined // Optional - only include if provided
      };

      // Remove empty optional fields
      if (!bookingData.returnDate) delete bookingData.returnDate;
      if (!bookingData.returnTime) delete bookingData.returnTime;
      if (!bookingData.whatsapp) delete bookingData.whatsapp;
      if (!bookingData.notes) delete bookingData.notes;
      if (!bookingData.userId) delete bookingData.userId;
      if (!bookingData.driverId) delete bookingData.driverId;
      if (!bookingData.carId) delete bookingData.carId;

      await api.post('/bookings', bookingData);

      // Reset form and close modal
      setBookingFormData({
        bookingType: 'anywhere',
        tripType: 'oneWay',
        pickupLocation: '',
        dropoffLocation: '',
        date: '',
        time: '',
        returnDate: '',
        returnTime: '',
        passengers: 1,
        luggage: 0,
        carType: '',
        carId: '',
        driverId: '',
        totalCost: 0,
        fullName: '',
        mobile: '',
        whatsapp: '',
        notes: '',
        status: 'Pending',
        userId: ''
      });
      setSelectedUser(null);
      setUserSearchQuery('');
      setShowUserDropdown(false);
      setUsers([]);
      setShowCreateBookingModal(false);
      fetchData();

      await alert(
        language === 'en' ? 'Booking created successfully!' : 'تم إنشاء الحجز بنجاح!',
        language === 'en' ? 'Success' : 'نجح',
        'success'
      );
    } catch (error) {
      console.error('Error creating booking:', error);
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error creating booking' : 'خطأ في إنشاء الحجز'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setSubmittingBooking(false);
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error updating status' : 'خطأ في تحديث الحالة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const updateBooking = async (bookingId, updates) => {
    try {
      setSubmittingBookingUpdate(true);
      const res = await api.patch(`/bookings/${bookingId}`, updates);
      await fetchData();
      // Update selected booking if it's the same one
      if (selectedBooking && selectedBooking._id === bookingId) {
        setSelectedBooking(res.data);
      }
      setEditingBooking(null);
      await alert(
        language === 'en' ? 'Booking updated successfully!' : 'تم تحديث الحجز بنجاح!',
        language === 'en' ? 'Success' : 'نجح',
        'success'
      );
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error updating booking' : 'خطأ في تحديث الحجز'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setSubmittingBookingUpdate(false);
    }
  };

  const verifyPayment = async (id) => {
    try {
      await api.patch(`/payments/${id}/verify`);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error verifying payment' : 'خطأ في التحقق من الدفع'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const approveReview = async (id) => {
    try {
      await api.patch(`/reviews/${id}/approve`);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error approving review' : 'خطأ في الموافقة على التقييم'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const hideReview = async (id) => {
    try {
      await api.patch(`/reviews/${id}/hide`);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error hiding review' : 'خطأ في إخفاء التقييم'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const markMessageRead = async (id) => {
    try {
      await api.patch(`/messages/${id}/read`);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error marking message as read' : 'خطأ في تحديد الرسالة كمقروءة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const deleteCar = async (id) => {
    const confirmed = await confirm(
      language === 'en' ? 'Delete this car?' : 'حذف هذه السيارة؟',
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/cars/${id}`);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting car' : 'خطأ في حذف السيارة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const deleteDriver = async (id) => {
    const confirmed = await confirm(
      language === 'en' ? 'Delete this driver?' : 'حذف هذا السائق؟',
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/drivers/${id}`);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting driver' : 'خطأ في حذف السائق'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const deleteDestination = async (id) => {
    const confirmed = await confirm(
      language === 'en' ? 'Delete this destination?' : 'حذف هذه الوجهة؟',
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/destinations/${id}`);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting destination' : 'خطأ في حذف الوجهة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const deleteReview = async (id) => {
    const confirmed = await confirm(
      language === 'en' ? 'Delete this review?' : 'حذف هذا التقييم؟',
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/reviews/${id}`);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting review' : 'خطأ في حذف التقييم'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const deleteMessage = async (id) => {
    const confirmed = await confirm(
      language === 'en' ? 'Delete this message?' : 'حذف هذه الرسالة؟',
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/messages/${id}`);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting message' : 'خطأ في حذف الرسالة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const deleteBooking = async (id) => {
    const confirmed = await confirm(
      language === 'en' ? 'Delete this booking?' : 'حذف هذا الحجز؟',
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/bookings/${id}`);
      fetchData();
      if (selectedBooking?._id === id) {
        setSelectedBooking(null);
      }
      if (editingBooking?._id === id) {
        setEditingBooking(null);
      }
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting booking' : 'خطأ في حذف الحجز'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const deletePayment = async (id) => {
    const confirmed = await confirm(
      language === 'en' ? 'Delete this payment?' : 'حذف هذه الدفعة؟',
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/payments/${id}`);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting payment' : 'خطأ في حذف الدفعة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  // Fetch Cloudinary images
  const fetchCloudinaryImages = async () => {
    setLoadingImages(true);
    try {
      const res = await api.get('/admin/images');
      setCloudinaryImages(res.data);
    } catch (error) {
      console.error('Error fetching images:', error);
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error fetching images' : 'خطأ في جلب الصور'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setLoadingImages(false);
    }
  };

  // Fetch Policy Sections
  const fetchPolicySections = async () => {
    setLoadingPolicySections(true);
    try {
      const res = await api.get('/policy-sections/admin/all');
      setPolicySections(res.data);
    } catch (error) {
      console.error('Error fetching policy sections:', error);
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error fetching policy sections' : 'خطأ في جلب أقسام السياسة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setLoadingPolicySections(false);
    }
  };

  // Delete Policy Section
  const deletePolicySection = async (sectionId) => {
    const confirmed = await confirm(
      language === 'en'
        ? 'Are you sure you want to delete this policy section?'
        : 'هل أنت متأكد من حذف قسم السياسة هذا؟',
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;

    try {
      await api.delete(`/policy-sections/${sectionId}`);
      setPolicySections(prev => prev.filter(s => s._id !== sectionId));
      await alert(
        language === 'en' ? 'Policy section deleted successfully!' : 'تم حذف قسم السياسة بنجاح!',
        language === 'en' ? 'Success' : 'نجاح',
        'success'
      );
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting policy section' : 'خطأ في حذف قسم السياسة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  // Delete Cloudinary image
  const deleteCloudinaryImage = async (image) => {
    const confirmed = await confirm(
      language === 'en'
        ? `Are you sure you want to delete this image?\n\nLocation: ${image.location}\nType: ${image.type}`
        : `هل أنت متأكد من حذف هذه الصورة؟\n\nالموقع: ${image.location}\nالنوع: ${image.type}`,
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;

    setDeletingImageId(image.publicId);
    try {
      const params = new URLSearchParams({
        publicId: image.publicId,
        entityType: image.entityType,
        entityId: image.entityId
      });
      if (image.imageIndex !== undefined) {
        params.append('imageIndex', image.imageIndex);
      }

      await api.delete(`/admin/images?${params.toString()}`);

      // Remove from local state
      setCloudinaryImages(prev => prev.filter(img =>
        !(img.publicId === image.publicId && img.entityId === image.entityId && img.imageIndex === image.imageIndex)
      ));

      // Refresh data to update the entity
      fetchData();

      await alert(
        language === 'en' ? 'Image deleted successfully!' : 'تم حذف الصورة بنجاح!',
        language === 'en' ? 'Success' : 'نجح',
        'success'
      );
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting image' : 'خطأ في حذف الصورة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setDeletingImageId(null);
    }
  };

  // Notification functions
  const handleNotificationFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      const newValue = checked;
      setNotificationFormData(prev => ({ ...prev, [name]: newValue }));
      if (name === 'isForAll' && newValue) {
        setSelectedNotificationUsers([]);
        setNotificationFormData(prev => ({ ...prev, recipients: [] }));
      }
    } else {
      setNotificationFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCreateNotification = async (e) => {
    e.preventDefault();
    if (submittingNotification) return;

    setSubmittingNotification(true);
    try {
      const notificationData = {
        type: notificationFormData.type,
        message: notificationFormData.message,
        isForAll: notificationFormData.isForAll,
        recipients: notificationFormData.isForAll ? [] : selectedNotificationUsers.map(u => u._id)
      };

      await api.post('/notifications', notificationData);
      setShowCreateNotificationModal(false);
      setNotificationFormData({
        type: 'info',
        message: '',
        isForAll: false,
        recipients: []
      });
      setSelectedNotificationUsers([]);
      setNotificationUserSearchQuery('');
      fetchData();
      await alert(
        language === 'en' ? 'Notification sent successfully' : 'تم إرسال الإشعار بنجاح',
        language === 'en' ? 'Success' : 'نجاح',
        'success'
      );
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error sending notification' : 'خطأ في إرسال الإشعار'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setSubmittingNotification(false);
    }
  };

  const handleNotificationUserSelect = (user) => {
    if (!selectedNotificationUsers.find(u => u._id === user._id)) {
      setSelectedNotificationUsers(prev => [...prev, user]);
      setNotificationFormData(prev => ({
        ...prev,
        recipients: [...prev.recipients, user._id]
      }));
    }
    setNotificationUserSearchQuery('');
    setShowNotificationUserDropdown(false);
  };

  const handleRemoveNotificationUser = (userId) => {
    setSelectedNotificationUsers(prev => prev.filter(u => u._id !== userId));
    setNotificationFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(id => id !== userId)
    }));
  };

  const fetchNotificationReadStatus = async (notificationId) => {
    try {
      const res = await api.get(`/notifications/${notificationId}/read-status`);
      setNotificationReadStatus(res.data);
      setSelectedNotificationForStatus(notificationId);
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error fetching read status' : 'خطأ في جلب حالة القراءة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const deleteNotification = async (id) => {
    const confirmed = await confirm(
      language === 'en' ? 'Delete this notification?' : 'حذف هذا الإشعار؟',
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/notifications/${id}`);
      fetchData();
      if (selectedNotificationForStatus === id) {
        setSelectedNotificationForStatus(null);
        setNotificationReadStatus(null);
      }
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting notification' : 'خطأ في حذف الإشعار'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const deleteCarImage = async (carId, imageId) => {
    const confirmed = await confirm(
      language === 'en' ? 'Delete this image?' : 'حذف هذه الصورة؟',
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;
    setDeletingImage(imageId);
    try {
      await api.delete(`/cars/${carId}/images/${imageId}`);
      if (editingCar && editingCar._id === carId) {
        const updatedCarRes = await api.get(`/cars/${carId}`);
        setEditingCar(updatedCarRes.data);
      }
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting image' : 'خطأ في حذف الصورة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setDeletingImage(null);
    }
  };

  const deleteDriverLicenseImage = async (driverId) => {
    const confirmed = await confirm(
      language === 'en' ? 'Delete license image?' : 'حذف صورة الرخصة؟',
      language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
    );
    if (!confirmed) return;
    setDeletingDriverImage(true);
    try {
      await api.delete(`/drivers/${driverId}/license-image`);
      if (editingDriver && editingDriver._id === driverId) {
        const updatedDriverRes = await api.get(`/drivers/${driverId}`);
        setEditingDriver(updatedDriverRes.data);
      }
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error deleting license image' : 'خطأ في حذف صورة الرخصة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setDeletingDriverImage(false);
    }
  };

  const handleCarSubmit = async (e) => {
    e.preventDefault();
    if (submittingCar) return;

    setSubmittingCar(true);
    const formData = new FormData(e.target);

    // Remove any existing images entries to avoid duplicates
    formData.delete('images');

    // Add selected images from state
    if (selectedCarImages.length > 0) {
      selectedCarImages.forEach(file => {
        formData.append('images', file);
      });
    }

    try {
      if (editingCar) {
        await api.patch(`/cars/${editingCar._id}`, formData);
      } else {
        await api.post('/cars', formData);
      }
      setShowCarForm(false);
      setEditingCar(null);
      setSelectedCarImages([]);
      fetchData();
    } catch (error) {
      console.error('Error saving car:', error);
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error saving car' : 'خطأ في حفظ السيارة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setSubmittingCar(false);
    }
  };

  const handleDriverSubmit = async (e) => {
    e.preventDefault();
    if (submittingDriver || deletingDriverImage) return;

    setSubmittingDriver(true);
    const formData = new FormData(e.target);

    // Remove any existing licenseImage entry to avoid duplicates
    formData.delete('licenseImage');

    // Add selected license image from state
    if (selectedDriverLicenseImage) {
      formData.append('licenseImage', selectedDriverLicenseImage);
    }

    try {
      if (editingDriver) {
        await api.patch(`/drivers/${editingDriver._id}`, formData);
      } else {
        await api.post('/drivers', formData);
      }
      setShowDriverForm(false);
      setEditingDriver(null);
      setSelectedDriverLicenseImage(null);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error saving driver' : 'خطأ في حفظ السائق'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setSubmittingDriver(false);
    }
  };

  const handleDestinationSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    // Remove any existing image entries to avoid duplicates
    formData.delete('image');

    // Add selected image from state
    if (selectedDestinationImage) {
      formData.append('image', selectedDestinationImage);
    }

    try {
      if (editingDestination) {
        await api.patch(`/destinations/${editingDestination._id}`, formData);
      } else {
        await api.post('/destinations', formData);
      }
      setShowDestinationForm(false);
      setEditingDestination(null);
      setSelectedDestinationImage(null);
      fetchData();
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error saving destination' : 'خطأ في حفظ الوجهة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const tabs = [
    {
      id: 'dashboard',
      label: language === 'en' ? 'Dashboard' : 'لوحة التحكم',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      )
    },
    {
      id: 'bookings',
      label: language === 'en' ? 'Bookings' : 'الحجوزات',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      badge: stats?.bookings?.pending
    },
    {
      id: 'cars',
      label: language === 'en' ? 'Cars' : 'السيارات',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 17h14M7 13h10l2-4H5l2 4z" />
        </svg>
      )
    },
    {
      id: 'drivers',
      label: language === 'en' ? 'Drivers' : 'السائقون',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    {
      id: 'destinations',
      label: language === 'en' ? 'Destinations' : 'الوجهات',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    {
      id: 'routePresets',
      label: language === 'en' ? 'Price Lists' : 'قوائم الاسعار',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      )
    },
    {
      id: 'payments',
      label: language === 'en' ? 'Payments' : 'المدفوعات',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      badge: stats?.payments?.unpaid
    },
    {
      id: 'reviews',
      label: language === 'en' ? 'Reviews' : 'التقييمات',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      )
    },
    {
      id: 'messages',
      label: language === 'en' ? 'Messages' : 'الرسائل',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      badge: stats?.messages?.unread
    },
    {
      id: 'notifications',
      label: language === 'en' ? 'Notifications' : 'الإشعارات',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      )
    },
    {
      id: 'settings',
      label: language === 'en' ? 'Settings' : 'الإعدادات',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    {
      id: 'googleAnalytics',
      label: language === 'en' ? 'Google Analytics' : 'تحليلات جوجل',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      externalUrl: 'https://analytics.google.com/'
    }
  ];

  if (showUnauthorizedWarning) {
    return (
      <div className="min-h-screen bg-blackC flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-dark border border-primary/30 rounded-lg p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-cream mb-2">{t.unauthorizedAccess}</h2>
          <p className="text-gray-silver mb-4">{t.unauthorizedMessage}</p>
          <div className="w-full bg-charcoal rounded-full h-2 overflow-hidden">
            <div className="bg-primary h-full animate-progress" style={{ animation: 'progress 5s linear forwards' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-blackC flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-silver">{language === 'en' ? 'Loading dashboard...' : 'جاري تحميل لوحة التحكم...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blackC flex">
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-blackC/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 bg-[#0d0d0d] border-r border-charcoal transition-all duration-300 overflow-hidden ${sidebarOpen ? 'w-64' : 'w-20'} ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-charcoal">
            <a href="/" className="flex flex-col">
              <span className="text-xl text-cream transition-colors duration-300 leading-tight" style={{ fontFamily: '"Lobster", cursive' }}>
                Global
              </span>
              <span className="text-2xl text-cream transition-colors duration-300 leading-tight" style={{ fontFamily: '"Lobster", cursive' }}>
                Limousine
              </span>
            </a>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.externalUrl) {
                    window.open(tab.externalUrl, '_blank', 'noopener,noreferrer');
                    if (window.innerWidth < 1024) setSidebarOpen(false);
                    return;
                  }
                  setActiveTab(tab.id);
                  // Close sidebar on mobile after selecting a tab
                  if (window.innerWidth < 1024) {
                    setSidebarOpen(false);
                  }
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 group relative ${activeTab === tab.id
                  ? 'bg-primary/10 text-primary border border-primary/30'
                  : 'text-gray-light hover:bg-dark hover:text-cream'
                  }`}
              >
                {tab.icon}
                {sidebarOpen && <span className="flex-1 text-left">{tab.label}</span>}
                {tab.badge > 0 && (
                  <span className={`${sidebarOpen ? '' : 'absolute -top-1 -right-1'} min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-blackC text-xs font-bold flex items-center justify-center`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-charcoal space-y-2">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-primary border border-primary/30 hover:bg-primary/10 transition-all duration-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {sidebarOpen && <span className="font-medium">{language === 'en' ? 'العربية' : 'EN'}</span>}
            </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-light hover:bg-red-500/10 hover:text-red-400 transition-all duration-300 border border-red-500/30"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {sidebarOpen && <span>{language === 'en' ? 'Logout' : 'تسجيل الخروج'}</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className={`lg:hidden fixed top-4 ${language === 'en' ? 'right-4' : 'left-4'} z-50 w-10 h-10 bg-dark border border-gray-dark rounded-lg flex items-center justify-center text-primary`}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Main Content */}
      <main
        className="flex-1 min-h-screen overflow-x-hidden"
        onClick={() => {
          // Close sidebar on mobile when clicking main content
          if (window.innerWidth < 1024 && sidebarOpen) {
            setSidebarOpen(false);
          }
        }}
      >
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className={`mb-8 transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h1 className="font-display text-3xl lg:text-4xl text-cream mb-2">
              {tabs.find(t => t.id === activeTab)?.label}
            </h1>
            <p className="text-gray-light">
              {language === 'en' ? 'Manage your limousine service operations' : 'إدارة عمليات خدمة الليموزين'}
            </p>
          </div>

          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              {/* Quick Navigation to Frontend */}
              <div className="bg-dark border border-gray-dark rounded-xl p-6">
                <h3 className="font-display text-xl text-cream mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  {language === 'en' ? 'Quick Navigation To Website' : 'التنقل السريع داخل الموقع'}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  <a href="/" target="_blank" className="p-3 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all text-gray-silver hover:text-primary text-center text-sm">
                    <svg className="w-5 h-5 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    {language === 'en' ? 'Home' : 'الرئيسية'}
                  </a>
                  <a href="/book" target="_blank" className="p-3 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all text-gray-silver hover:text-primary text-center text-sm">
                    <svg className="w-5 h-5 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {language === 'en' ? 'Book' : 'احجز'}
                  </a>
                  <a href="/cars" target="_blank" className="p-3 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all text-gray-silver hover:text-primary text-center text-sm">
                    <CarIcon className="w-5 h-5 mx-auto mb-1" />
                    {language === 'en' ? 'Cars' : 'السيارات'}
                  </a>
                  <a href="/destinations" target="_blank" className="p-3 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all text-gray-silver hover:text-primary text-center text-sm">
                    <svg className="w-5 h-5 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    {language === 'en' ? 'Destinations' : 'الوجهات'}
                  </a>
                  <a href="/reviews" target="_blank" className="p-3 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all text-gray-silver hover:text-primary text-center text-sm">
                    <svg className="w-5 h-5 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    {language === 'en' ? 'Reviews' : 'التقييمات'}
                  </a>
                  <a href="/contact" target="_blank" className="p-3 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all text-gray-silver hover:text-primary text-center text-sm">
                    <svg className="w-5 h-5 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {language === 'en' ? 'Contact' : 'اتصل بنا'}
                  </a>
                </div>
              </div>

              {/* Revenue & Financial Analytics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { label: language === 'en' ? 'Total Revenue' : 'إجمالي الإيرادات', value: `${analytics.totalRevenue.toLocaleString()} EGP`, icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, color: '#10b981' },
                  { label: language === 'en' ? 'Monthly Revenue' : 'إيرادات الشهر', value: `${analytics.monthlyRevenue.toLocaleString()} EGP`, icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>, color: '#3b82f6' },
                  { label: language === 'en' ? 'Avg Booking Value' : 'متوسط قيمة الحجز', value: `${analytics.averageBookingValue.toFixed(0)} EGP`, icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>, color: '#8b5cf6' },
                  { label: language === 'en' ? 'Total Bookings' : 'إجمالي الحجوزات', value: stats.bookings.total, icon: <CalendarIcon className="w-5 h-5" />, color: '#c9a962' },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="relative bg-dark border border-gray-dark rounded-xl p-6 hover:border-primary/30 transition-all duration-300 group overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity" style={{ backgroundColor: stat.color }} />
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl" style={{ color: stat.color }}>{stat.icon}</span>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${stat.color}20` }}>
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stat.color }} />
                        </div>
                      </div>
                      <p className="text-gray-light text-sm mb-1">{stat.label}</p>
                      <p className="font-display text-2xl text-cream">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Booking Status Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { label: t.pending, value: stats.bookings.pending, icon: <ClockIcon className="w-4 h-4" />, color: '#fbbf24' },
                  { label: t.confirmed, value: stats.bookings.confirmed, icon: <CheckIcon className="w-4 h-4" />, color: '#60a5fa' },
                  { label: t.completed, value: stats.bookings.completed, icon: <CheckIcon className="w-4 h-4" />, color: '#4ade80' },
                  { label: t.cancelled, value: stats.bookings.cancelled, icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" /></svg>, color: '#ef4444' },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="relative bg-dark border border-gray-dark rounded-xl p-6 hover:border-primary/30 transition-all duration-300 group overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity" style={{ backgroundColor: stat.color }} />
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl" style={{ color: stat.color }}>{stat.icon}</span>
                      </div>
                      <p className="text-gray-light text-sm mb-1">{stat.label}</p>
                      <p className="font-display text-3xl text-cream">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Analytics Charts & Insights */}
              {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-dark border border-gray-dark rounded-xl p-6">
                  <h3 className="font-display text-xl text-cream mb-6 flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    {language === 'en' ? 'Booking Trends (Last 7 Days)' : 'اتجاهات الحجوزات (آخر 7 أيام)'}
                  </h3>
                  <div className="h-64 flex items-end justify-between gap-2">
                    {analytics.bookingTrends.map((trend, idx) => {
                      const maxCount = Math.max(...analytics.bookingTrends.map(t => t.count), 1);
                      const height = (trend.count / maxCount) * 100;
                      return (
                        <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                          <div className="w-full bg-charcoal rounded-t-lg relative overflow-hidden group cursor-pointer" style={{ height: `${height}%`, minHeight: '20px' }}>
                            <div className="absolute inset-0 bg-gradient-to-t from-primary to-[#d4af37] opacity-80 group-hover:opacity-100 transition-opacity" />
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-blackC text-xs font-bold">{trend.count}</div>
                          </div>
                          <span className="text-gray-light text-xs">{trend.date}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-dark border border-gray-dark rounded-xl p-6">
                  <h3 className="font-display text-xl text-cream mb-6 flex items-center gap-2">
                    <CarIcon className="w-5 h-5 text-primary" />
                    {language === 'en' ? 'Popular Car Types' : 'أنواع السيارات الأكثر طلباً'}
                  </h3>
                  <div className="space-y-4">
                    {Object.entries(analytics.popularCarTypes)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([type, count], idx) => {
                        const maxCount = Math.max(...Object.values(analytics.popularCarTypes));
                        const percentage = (count / maxCount) * 100;
                        return (
                          <div key={idx}>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-cream font-medium">{type}</span>
                              <span className="text-primary font-bold">{count}</span>
                            </div>
                            <div className="w-full bg-charcoal rounded-full h-2 overflow-hidden">
                              <div className="bg-gradient-to-r from-primary to-[#d4af37] h-2 rounded-full transition-all duration-500" style={{ width: `${percentage}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    {Object.keys(analytics.popularCarTypes).length === 0 && (
                      <p className="text-gray-light text-center py-8">{language === 'en' ? 'No booking data available' : 'لا توجد بيانات حجوزات'}</p>
                    )}
                  </div>
                </div>
              </div> */}

              {/* Resources & Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { label: language === 'en' ? 'Total Cars' : 'إجمالي السيارات', value: stats.cars.total, icon: <CarIcon className="w-4 h-4" />, color: '#c9a962' },
                  { label: language === 'en' ? 'Available Cars' : 'السيارات المتاحة', value: stats.cars.available, icon: <CheckIcon className="w-4 h-4" />, color: '#4ade80' },
                  { label: language === 'en' ? 'Total Drivers' : 'إجمالي السائقين', value: stats.drivers.total, icon: <UserIcon className="w-4 h-4" />, color: '#60a5fa' },
                  { label: language === 'en' ? 'Available Drivers' : 'السائقون المتاحون', value: stats.drivers.available, icon: <UserIcon className="w-4 h-4" />, color: '#4ade80' },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="relative bg-dark border border-gray-dark rounded-xl p-6 hover:border-primary/30 transition-all duration-300 group overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity" style={{ backgroundColor: stat.color }} />
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl" style={{ color: stat.color }}>{stat.icon}</span>
                      </div>
                      <p className="text-gray-light text-sm mb-1">{stat.label}</p>
                      <p className="font-display text-3xl text-cream">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Actions & Pending Items */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-dark border border-gray-dark rounded-xl p-6">
                  <h3 className="font-display text-xl text-cream mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#fbbf24]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {language === 'en' ? 'Pending Actions' : 'إجراءات معلقة'}
                  </h3>
                  <div className="space-y-3">
                    <button onClick={() => setActiveTab('messages')} className="w-full flex items-center justify-between p-3 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all group">
                      <span className="text-gray-silver group-hover:text-cream">{language === 'en' ? 'Unread Messages' : 'رسائل غير مقروءة'}</span>
                      <span className="text-[#fbbf24] font-bold">{stats.messages.unread}</span>
                    </button>
                    <button onClick={() => setActiveTab('payments')} className="w-full flex items-center justify-between p-3 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all group">
                      <span className="text-gray-silver group-hover:text-cream">{language === 'en' ? 'Pending Payments' : 'مدفوعات معلقة'}</span>
                      <span className="text-[#fbbf24] font-bold">{stats.payments.unpaid}</span>
                    </button>
                    <button onClick={() => setActiveTab('bookings')} className="w-full flex items-center justify-between p-3 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all group">
                      <span className="text-gray-silver group-hover:text-cream">{language === 'en' ? 'Pending Bookings' : 'حجوزات معلقة'}</span>
                      <span className="text-[#fbbf24] font-bold">{stats.bookings.pending}</span>
                    </button>
                  </div>
                </div>

                <div className="bg-dark border border-gray-dark rounded-xl p-6">
                  <h3 className="font-display text-xl text-cream mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    {language === 'en' ? 'Quick Actions' : 'إجراءات سريعة'}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => setActiveTab('bookings')} className="p-4 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all text-gray-silver hover:text-primary text-sm">
                      {language === 'en' ? 'View Bookings' : 'عرض الحجوزات'}
                    </button>
                    <button onClick={() => { setActiveTab('cars'); setShowCarForm(true); }} className="p-4 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all text-gray-silver hover:text-primary text-sm">
                      {language === 'en' ? 'Add Car' : 'إضافة سيارة'}
                    </button>
                    <button onClick={() => { setActiveTab('drivers'); setShowDriverForm(true); }} className="p-4 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all text-gray-silver hover:text-primary text-sm">
                      {language === 'en' ? 'Add Driver' : 'إضافة سائق'}
                    </button>
                    <button onClick={() => setActiveTab('messages')} className="p-4 bg-charcoal rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-all text-gray-silver hover:text-primary text-sm">
                      {language === 'en' ? 'View Messages' : 'عرض الرسائل'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bookings */}
          {activeTab === 'bookings' && (
            <div className="space-y-6">
              {/* Create Booking Button */}
              <div className="flex justify-between items-center">
                <h2 className="font-display text-2xl text-cream">
                  {language === 'en' ? 'Bookings Management' : 'إدارة الحجوزات'}
                </h2>
                <button
                  onClick={() => setShowCreateBookingModal(true)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {language === 'en' ? 'Create New Booking' : 'إنشاء حجز جديد'}
                </button>
              </div>

              {/* Filters & Search Bar */}
              <div className="bg-dark border border-gray-dark rounded-xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                  {/* Search */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Search' : 'بحث'}</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={language === 'en' ? 'Search by name, location, or phone...' : 'ابحث بالاسم، الموقع، أو الهاتف...'}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-2 pl-10 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      />
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Status' : 'الحالة'}</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-2 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                    >
                      <option value="all">{language === 'en' ? 'All Status' : 'جميع الحالات'}</option>
                      <option value="Pending">{t.pending}</option>
                      <option value="Confirmed">{t.confirmed}</option>
                      <option value="Completed">{t.completed}</option>
                      <option value="Cancelled">{t.cancelled}</option>
                    </select>
                  </div>

                  {/* Date Range Start */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'From Date' : 'من تاريخ'}</label>
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-2 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                    />
                  </div>

                  {/* Date Range End */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'To Date' : 'إلى تاريخ'}</label>
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-2 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  {/* Sort By */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-light">{language === 'en' ? 'Sort by:' : 'ترتيب حسب:'}</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="bg-charcoal border border-gray-dark text-cream px-3 py-1.5 rounded-lg focus:outline-none focus:border-primary/50 transition-colors text-sm"
                    >
                      <option value="newest">{language === 'en' ? 'Newest First' : 'الأحدث أولاً'}</option>
                      <option value="oldest">{language === 'en' ? 'Oldest First' : 'الأقدم أولاً'}</option>
                      <option value="date-asc">{language === 'en' ? 'Date (Ascending)' : 'التاريخ (تصاعدي)'}</option>
                      <option value="date-desc">{language === 'en' ? 'Date (Descending)' : 'التاريخ (تنازلي)'}</option>
                      <option value="amount-asc">{language === 'en' ? 'Amount (Low to High)' : 'المبلغ (منخفض إلى عالي)'}</option>
                      <option value="amount-desc">{language === 'en' ? 'Amount (High to Low)' : 'المبلغ (عالي إلى منخفض)'}</option>
                    </select>
                  </div>

                  {/* Clear Filters */}
                  {(searchQuery || statusFilter !== 'all' || dateRange.start || dateRange.end) && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setStatusFilter('all');
                        setDateRange({ start: '', end: '' });
                      }}
                      className="px-4 py-1.5 bg-charcoal border border-gray-dark text-gray-silver rounded-lg hover:bg-gray-dark hover:text-cream transition-colors text-sm"
                    >
                      {language === 'en' ? 'Clear Filters' : 'مسح الفلاتر'}
                    </button>
                  )}

                  {/* Export Button */}
                  <button
                    onClick={exportBookings}
                    className="ml-auto px-4 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {language === 'en' ? 'Export CSV' : 'تصدير CSV'}
                  </button>
                </div>
              </div>

              {/* Bookings Table/Cards */}
              <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                <div className="p-4 border-b border-gray-dark flex items-center justify-between">
                  <h3 className="font-display text-lg text-cream">
                    {language === 'en' ? 'Bookings' : 'الحجوزات'} ({getFilteredBookings().length})
                  </h3>
                  {/* View Toggle */}
                  <div className="flex items-center gap-2 bg-charcoal rounded-lg p-1 border border-gray-dark">
                    <button
                      onClick={() => setViewMode(prev => ({ ...prev, bookings: 'table' }))}
                      className={`p-2 rounded transition-colors ${viewMode.bookings === 'table' ? 'bg-primary text-blackC' : 'text-gray-light hover:text-cream'}`}
                      title={language === 'en' ? 'Table View' : 'عرض الجدول'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode(prev => ({ ...prev, bookings: 'cards' }))}
                      className={`p-2 rounded transition-colors ${viewMode.bookings === 'cards' ? 'bg-primary text-blackC' : 'text-gray-light hover:text-cream'}`}
                      title={language === 'en' ? 'Card View' : 'عرض البطاقات'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {viewMode.bookings === 'table' ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`border-b border-gray-dark ${language === 'en' ? 'text-left' : 'text-right'}`}>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{t.fullName}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{t.pickupLocation}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{t.dropoffLocation}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{t.date}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{t.totalCost}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{t.status}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Actions' : 'الإجراءات'}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-dark">
                        {getFilteredBookings().length === 0 ? (
                          <tr>
                            <td colSpan="7" className="px-6 py-12 text-center text-gray-light">
                              {language === 'en' ? 'No bookings found' : 'لا توجد حجوزات'}
                            </td>
                          </tr>
                        ) : (
                          getFilteredBookings().map((booking) => (
                            <tr key={booking._id} className="hover:bg-charcoal transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-cream font-medium">{booking.fullName}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-silver">{booking.pickupLocation}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-silver">{booking.dropoffLocation}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-silver">{new Date(booking.date).toLocaleDateString()}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-primary font-medium">{booking.totalCost} EGP</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${booking.status === 'Confirmed' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30' :
                                  booking.status === 'Pending' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30' :
                                    booking.status === 'Cancelled' ? 'bg-red-500/10 text-red-400 border border-red-500/30' :
                                      'bg-green-500/10 text-green-400 border border-green-500/30'
                                  }`}>
                                  {booking.status === 'Confirmed' ? t.confirmed :
                                    booking.status === 'Pending' ? t.pending :
                                      booking.status === 'Cancelled' ? t.cancelled :
                                        booking.status === 'Completed' ? t.completed : booking.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2 flex items-center gap-2">
                                <button onClick={() => setSelectedBooking(booking)} className="text-primary hover:text-[#d4af37] transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                  {language === 'en' ? 'View' : 'عرض'}
                                </button>
                                {booking.status === 'Pending' && (
                                  <>
                                    <button onClick={() => updateBookingStatus(booking._id, 'Confirmed')} className="text-green-400 hover:text-green-300 transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                      {t.confirmed}
                                    </button>
                                    <button onClick={() => updateBookingStatus(booking._id, 'Cancelled')} className="text-red-400 hover:text-red-300 transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                      {t.cancelled}
                                    </button>
                                  </>
                                )}
                                {booking.status === 'Confirmed' && (
                                  <button onClick={() => updateBookingStatus(booking._id, 'Completed')} className="text-blue-400 hover:text-blue-300 transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                    {t.completed}
                                  </button>
                                )}
                                <button onClick={() => deleteBooking(booking._id)} className="text-red-400 hover:text-red-300 transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                  {language === 'en' ? 'Delete' : 'حذف'}
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  /* Bookings Cards View */
                  <div className="p-4">
                    {getFilteredBookings().length === 0 ? (
                      <div className="text-center py-12 text-gray-light">
                        {language === 'en' ? 'No bookings found' : 'لا توجد حجوزات'}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {getFilteredBookings().map((booking) => (
                          <div key={booking._id} className="bg-charcoal border border-gray-dark rounded-xl p-4 hover:border-primary/30 transition-all">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="font-medium text-cream mb-1">{booking.fullName}</h4>
                                <p className="text-gray-light text-sm" dir="ltr">{booking.mobile}</p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${booking.status === 'Confirmed' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30' :
                                booking.status === 'Pending' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30' :
                                  booking.status === 'Cancelled' ? 'bg-red-500/10 text-red-400 border border-red-500/30' :
                                    'bg-green-500/10 text-green-400 border border-green-500/30'
                                }`}>
                                {booking.status === 'Confirmed' ? t.confirmed :
                                  booking.status === 'Pending' ? t.pending :
                                    booking.status === 'Cancelled' ? t.cancelled :
                                      booking.status === 'Completed' ? t.completed : booking.status}
                              </span>
                            </div>
                            <div className="space-y-2 text-sm mb-4">
                              <div className="flex items-center gap-2 text-gray-silver">
                                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="truncate">{booking.pickupLocation}</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-silver">
                                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="truncate">{booking.dropoffLocation}</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-silver">
                                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>{new Date(booking.date).toLocaleDateString()} {booking.time}</span>
                              </div>
                              <div className="flex items-center gap-2 text-primary font-medium">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>{booking.totalCost} EGP</span>
                              </div>
                            </div>
                            <div className="flex gap-2 pt-3 border-t border-gray-dark">
                              <button onClick={() => setSelectedBooking(booking)} className="flex-1 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm">
                                {language === 'en' ? 'View' : 'عرض'}
                              </button>
                              {booking.status === 'Pending' && (
                                <>
                                  <button onClick={() => updateBookingStatus(booking._id, 'Confirmed')} className="flex-1 py-2 bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors text-sm">
                                    {t.confirmed}
                                  </button>
                                  <button onClick={() => updateBookingStatus(booking._id, 'Cancelled')} className="flex-1 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm">
                                    {t.cancelled}
                                  </button>
                                </>
                              )}
                              {booking.status === 'Confirmed' && (
                                <button onClick={() => updateBookingStatus(booking._id, 'Completed')} className="flex-1 py-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors text-sm">
                                  {t.completed}
                                </button>
                              )}
                              <button onClick={() => deleteBooking(booking._id)} className="flex-1 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm">
                                {language === 'en' ? 'Delete' : 'حذف'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Create Booking Modal */}
          {showCreateBookingModal && (
            <div className="fixed inset-0 bg-blackC/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-dark border border-gray-dark rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8">
                <div className="p-6 border-b border-gray-dark flex justify-between items-center sticky top-0 bg-dark z-10">
                  <h2 className="font-display text-2xl text-cream">
                    {language === 'en' ? 'Create New Booking' : 'إنشاء حجز جديد'}
                  </h2>
                  <button
                    onClick={() => setShowCreateBookingModal(false)}
                    className="w-10 h-10 rounded-lg bg-charcoal flex items-center justify-center text-gray-light hover:text-cream transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleCreateBooking} className="p-6 space-y-6">
                  {/* Booking Type & Trip Type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.bookingType}</label>
                      <select
                        name="bookingType"
                        value={bookingFormData.bookingType}
                        onChange={handleBookingFormChange}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        required
                      >
                        <option value="fromAirport">{t.fromAirport}</option>
                        <option value="toAirport">{t.toAirport}</option>
                        <option value="insideCairo">{t.insideCairo}</option>
                        <option value="anywhere">{t.anywhere}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.tripType}</label>
                      <select
                        name="tripType"
                        value={bookingFormData.tripType}
                        onChange={handleBookingFormChange}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        required
                      >
                        <option value="oneWay">{t.oneWay}</option>
                        <option value="roundTrip">{t.roundTrip}</option>
                      </select>
                    </div>
                  </div>

                  {/* Locations */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Pickup Location - Autocomplete */}
                    <div className="relative pickup-location-container" ref={pickupInputRef}>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.pickupLocation}</label>
                      <input
                        type="text"
                        name="pickupLocation"
                        value={bookingFormData.pickupLocation}
                        onChange={handleBookingFormChange}
                        onFocus={() => {
                          filterPickupSuggestions(bookingFormData.pickupLocation);
                          setShowPickupSuggestions(true);
                        }}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        placeholder={language === 'en' ? 'Type or select pickup location' : 'اكتب أو اختر موقع الاستلام'}
                        required
                        autoComplete="off"
                      />
                      {showPickupSuggestions && filteredPickupSuggestions.length > 0 && (
                        <div
                          ref={pickupDropdownRef}
                          className="absolute z-50 w-full mt-1 bg-dark border border-gray-dark rounded-lg shadow-xl max-h-60 overflow-y-auto"
                        >
                          {filteredPickupSuggestions.map((location, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => handlePickupSelect(location)}
                              className="w-full px-4 py-3 text-left hover:bg-charcoal transition-colors border-b border-gray-dark last:border-b-0 text-cream"
                            >
                              {location}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Dropoff Location - Autocomplete */}
                    <div className="relative dropoff-location-container" ref={dropoffInputRef}>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.dropoffLocation}</label>
                      <input
                        type="text"
                        name="dropoffLocation"
                        value={bookingFormData.dropoffLocation}
                        onChange={handleBookingFormChange}
                        onFocus={() => {
                          if (bookingFormData.pickupLocation || dropoffLocations.length > 0) {
                            filterDropoffSuggestions(bookingFormData.dropoffLocation);
                            setShowDropoffSuggestions(true);
                          }
                        }}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder={language === 'en' ? 'Type or select dropoff location' : 'اكتب أو اختر موقع الوصول'}
                        required
                        disabled={!bookingFormData.pickupLocation}
                        autoComplete="off"
                      />
                      {showDropoffSuggestions && filteredDropoffSuggestions.length > 0 && bookingFormData.pickupLocation && (
                        <div
                          ref={dropoffDropdownRef}
                          className="absolute z-50 w-full mt-1 bg-dark border border-gray-dark rounded-lg shadow-xl max-h-60 overflow-y-auto"
                        >
                          {filteredDropoffSuggestions.map((location, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => handleDropoffSelect(location)}
                              className="w-full px-4 py-3 text-left hover:bg-charcoal transition-colors border-b border-gray-dark last:border-b-0 text-cream"
                            >
                              {location}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date & Time */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.date}</label>
                      <input
                        type="date"
                        name="date"
                        value={bookingFormData.date}
                        onChange={handleBookingFormChange}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.time}</label>
                      <input
                        type="time"
                        name="time"
                        value={bookingFormData.time}
                        onChange={handleBookingFormChange}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        required
                      />
                    </div>
                  </div>

                  {/* Return Date & Time (for round trip) */}
                  {bookingFormData.tripType === 'roundTrip' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-light mb-2">{t.returnDate}</label>
                        <input
                          type="date"
                          name="returnDate"
                          value={bookingFormData.returnDate}
                          onChange={handleBookingFormChange}
                          className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-light mb-2">{t.returnTime}</label>
                        <input
                          type="time"
                          name="returnTime"
                          value={bookingFormData.returnTime}
                          onChange={handleBookingFormChange}
                          className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                          required
                        />
                      </div>
                    </div>
                  )}

                  {/* Passengers, Luggage, Car Type */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.passengers}</label>
                      <input
                        type="number"
                        name="passengers"
                        value={bookingFormData.passengers}
                        onChange={handleBookingFormChange}
                        min="1"
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.luggage}</label>
                      <input
                        type="number"
                        name="luggage"
                        value={bookingFormData.luggage}
                        onChange={handleBookingFormChange}
                        min="0"
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.carType}</label>
                      <select
                        name="carId"
                        value={bookingFormData.carId}
                        onChange={(e) => {
                          const selectedCar = cars.find(c => c._id === e.target.value);
                          setBookingFormData(prev => ({
                            ...prev,
                            carId: e.target.value,
                            carType: selectedCar ? selectedCar.type : prev.carType,
                            totalCost: 0 // Reset price when car changes, will be auto-filled if preset exists
                          }));
                          // Trigger price lookup after state update
                          setTimeout(() => {
                            if (bookingFormData.pickupLocation && bookingFormData.dropoffLocation && e.target.value) {
                              findAndSetPresetPrice();
                            }
                          }, 0);
                        }}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        required
                      >
                        <option value="">{language === 'en' ? 'Select Car' : 'اختر السيارة'}</option>
                        {cars.filter(car => car.isAvailable).map(car => (
                          <option key={car._id} value={car._id}>
                            {car.name || car.type} - {car.type} ({car.passengerCapacity})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Driver Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">
                      {language === 'en' ? 'Select Driver (Optional)' : 'اختر السائق (اختياري)'}
                    </label>
                    <select
                      name="driverId"
                      value={bookingFormData.driverId}
                      onChange={handleBookingFormChange}
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                    >
                      <option value="">{language === 'en' ? 'No Driver Selected' : 'لم يتم اختيار سائق'}</option>
                      {drivers.filter(driver => driver.status === 'Available' || driver.status === 'available').map(driver => (
                        <option key={driver._id} value={driver._id}>
                          {driver.name} - {driver.mobile} ({driver.assignedCarType || 'N/A'})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">
                      {language === 'en' ? 'Price (EGP)' : 'السعر (جنيه)'}
                    </label>
                    <input
                      type="number"
                      name="totalCost"
                      value={bookingFormData.totalCost || ''}
                      onChange={handleBookingFormChange}
                      min="0"
                      step="0.01"
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      dir="ltr"
                      required
                    />
                    {bookingFormData.tripType === 'roundTrip' && bookingFormData.totalCost > 0 && (
                      <p className="text-gray-silver text-xs mt-1">
                        {language === 'en'
                          ? `Round trip total: ${(parseFloat(bookingFormData.totalCost) * (settings?.roundTripMultiplier || 2)).toLocaleString()} EGP`
                          : `إجمالي الرحلة ذهاباً وإياباً: ${(parseFloat(bookingFormData.totalCost) * (settings?.roundTripMultiplier || 2)).toLocaleString()} جنيه`}
                      </p>
                    )}
                    {bookingFormData.pickupLocation && bookingFormData.dropoffLocation && bookingFormData.carId && bookingFormData.totalCost > 0 && (
                      <p className="text-primary text-xs mt-1 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {language === 'en' ? 'Price auto-filled from preset' : 'تم تعبئة السعر تلقائياً من القائمة المسبقة'}
                      </p>
                    )}
                  </div>

                  {/* Customer Information */}
                  <div className="border-t border-gray-dark pt-6">
                    <h3 className="font-display text-lg text-cream mb-4">
                      {language === 'en' ? 'Customer Information' : 'معلومات العميل'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-light mb-2">{t.fullName}</label>
                        <input
                          type="text"
                          name="fullName"
                          value={bookingFormData.fullName}
                          onChange={handleBookingFormChange}
                          className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-light mb-2">{t.mobile}</label>
                        <input
                          type="tel"
                          name="mobile"
                          value={bookingFormData.mobile}
                          onChange={handleBookingFormChange}
                          className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                          dir="ltr"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-light mb-2">{t.whatsapp}</label>
                        <input
                          type="tel"
                          name="whatsapp"
                          value={bookingFormData.whatsapp}
                          onChange={handleBookingFormChange}
                          className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                          dir="ltr"
                        />
                      </div>

                      <div className="relative user-selector-container">
                        <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Link to User (Optional)' : 'ربط بمستخدم (اختياري)'}</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={userSearchQuery}
                            onChange={(e) => {
                              setUserSearchQuery(e.target.value);
                              if (e.target.value.length < 2) {
                                setShowUserDropdown(false);
                                setUsers([]);
                              }
                            }}
                            onFocus={() => {
                              if (userSearchQuery.length >= 2 && users.length > 0) {
                                setShowUserDropdown(true);
                              }
                            }}
                            placeholder={language === 'en' ? 'Search by name, email, or phone...' : 'ابحث بالاسم، البريد، أو الهاتف...'}
                            className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 pr-10 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                          />
                          {selectedUser && (
                            <button
                              type="button"
                              onClick={handleClearUser}
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 flex items-center justify-center transition-colors"
                              title={language === 'en' ? 'Clear selection' : 'مسح الاختيار'}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                          {loadingUsers && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                              <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                            </div>
                          )}
                        </div>

                        {/* Selected User Display */}
                        {selectedUser && (
                          <div className="mt-2 p-3 bg-primary/10 border border-primary/30 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-cream font-medium">{selectedUser.name || 'No Name'}</p>
                                <p className="text-gray-light text-sm">{selectedUser.email}</p>
                                {selectedUser.phone && (
                                  <p className="text-gray-light text-sm" dir="ltr">{selectedUser.phone}</p>
                                )}
                              </div>
                              <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs">
                                {language === 'en' ? 'Selected' : 'محدد'}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* User Dropdown */}
                        {showUserDropdown && users.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-dark border border-gray-dark rounded-lg shadow-xl max-h-60 overflow-y-auto">
                            {users.map((user) => (
                              <button
                                key={user._id}
                                type="button"
                                onClick={() => handleUserSelect(user)}
                                className="w-full px-4 py-3 text-left hover:bg-charcoal transition-colors border-b border-gray-dark last:border-b-0"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="text-cream font-medium">{user.name || 'No Name'}</p>
                                    <p className="text-gray-light text-sm">{user.email}</p>
                                    {user.phone && (
                                      <p className="text-gray-light text-sm" dir="ltr">{user.phone}</p>
                                    )}
                                  </div>
                                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}

                        {showUserDropdown && userSearchQuery.length >= 2 && !loadingUsers && users.length === 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-dark border border-gray-dark rounded-lg shadow-xl p-4 text-center text-gray-light">
                            {language === 'en' ? 'No users found' : 'لا يوجد مستخدمون'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Notes & Status */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.notes}</label>
                      <textarea
                        name="notes"
                        value={bookingFormData.notes}
                        onChange={handleBookingFormChange}
                        rows="3"
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Initial Status' : 'الحالة الأولية'}</label>
                      <select
                        name="status"
                        value={bookingFormData.status}
                        onChange={handleBookingFormChange}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        required
                      >
                        <option value="Pending">{t.pending}</option>
                        <option value="Confirmed">{t.confirmed}</option>
                        <option value="Completed">{t.completed}</option>
                        <option value="Cancelled">{t.cancelled}</option>
                      </select>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-dark">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateBookingModal(false);
                        setBookingFormData({
                          bookingType: 'anywhere',
                          tripType: 'oneWay',
                          pickupLocation: '',
                          dropoffLocation: '',
                          date: '',
                          time: '',
                          returnDate: '',
                          returnTime: '',
                          passengers: 1,
                          luggage: 0,
                          carType: '',
                          carId: '',
                          driverId: '',
                          totalCost: 0,
                          fullName: '',
                          mobile: '',
                          whatsapp: '',
                          notes: '',
                          status: 'Pending',
                          userId: ''
                        });
                        setSelectedUser(null);
                        setUserSearchQuery('');
                        setShowUserDropdown(false);
                        setUsers([]);
                      }}
                      className="px-6 py-3 bg-charcoal border border-gray-dark text-gray-silver rounded-lg hover:bg-gray-dark hover:text-cream transition-colors"
                    >
                      {language === 'en' ? 'Cancel' : 'إلغاء'}
                    </button>
                    <button
                      type="submit"
                      disabled={submittingBooking}
                      className="px-6 py-3 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {submittingBooking ? (
                        <>
                          <div className="w-5 h-5 border-2 border-blackC/20 border-t-blackC rounded-full animate-spin" />
                          {language === 'en' ? 'Creating...' : 'جاري الإنشاء...'}
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {language === 'en' ? 'Create Booking' : 'إنشاء الحجز'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Booking Details Modal */}
          {/* Booking View/Edit Modal */}
          {selectedBooking && (
            <div className="fixed inset-0 bg-blackC/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-dark border border-gray-dark rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-dark flex justify-between items-center sticky top-0 bg-dark z-10">
                  <h2 className="font-display text-2xl text-cream">{language === 'en' ? 'Booking Details' : 'تفاصيل الحجز'}</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingBooking(selectedBooking);
                        setSelectedBooking(null);
                      }}
                      className="px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm"
                    >
                      {language === 'en' ? 'Edit' : 'تعديل'}
                    </button>
                    <button onClick={() => setSelectedBooking(null)} className="w-10 h-10 rounded-lg bg-charcoal flex items-center justify-center text-gray-light hover:text-cream transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {/* Customer Information */}
                  <div>
                    <h3 className="font-display text-lg text-cream mb-4">{language === 'en' ? 'Customer Information' : 'معلومات العميل'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.fullName}</p>
                        <p className="text-cream">{selectedBooking.fullName}</p>
                      </div>
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.mobile}</p>
                        <p className="text-cream" dir="ltr">{selectedBooking.mobile}</p>
                      </div>
                      {selectedBooking.email && (
                        <div className="bg-charcoal p-4 rounded-lg">
                          <p className="text-gray-light text-sm mb-1">{language === 'en' ? 'Email' : 'البريد الإلكتروني'}</p>
                          <p className="text-cream" dir="ltr">{selectedBooking.email}</p>
                        </div>
                      )}
                      {selectedBooking.whatsapp && (
                        <div className="bg-charcoal p-4 rounded-lg">
                          <p className="text-gray-light text-sm mb-1">{t.whatsapp}</p>
                          <p className="text-cream" dir="ltr">{selectedBooking.whatsapp}</p>
                        </div>
                      )}
                      {selectedBooking.userId && (
                        <div className="bg-charcoal p-4 rounded-lg">
                          <p className="text-gray-light text-sm mb-1">{language === 'en' ? 'User Account' : 'حساب المستخدم'}</p>
                          <p className="text-cream">{selectedBooking.userId.name || selectedBooking.userId.email || 'N/A'}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Trip Details */}
                  <div>
                    <h3 className="font-display text-lg text-cream mb-4">{language === 'en' ? 'Trip Details' : 'تفاصيل الرحلة'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.bookingType}</p>
                        <p className="text-cream">
                          {selectedBooking.bookingType === 'fromAirport' && t.fromAirport}
                          {selectedBooking.bookingType === 'toAirport' && t.toAirport}
                          {selectedBooking.bookingType === 'insideCairo' && t.insideCairo}
                          {selectedBooking.bookingType === 'anywhere' && t.anywhere}
                        </p>
                      </div>
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.tripType}</p>
                        <p className="text-cream">{selectedBooking.tripType === 'oneWay' ? t.oneWay : t.roundTrip}</p>
                      </div>
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.pickupLocation}</p>
                        <p className="text-cream">{selectedBooking.pickupLocation}</p>
                      </div>
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.dropoffLocation}</p>
                        <p className="text-cream">{selectedBooking.dropoffLocation}</p>
                      </div>
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.date}</p>
                        <p className="text-cream">{new Date(selectedBooking.date).toLocaleDateString()}</p>
                      </div>
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.time}</p>
                        <p className="text-cream">{selectedBooking.time}</p>
                      </div>
                      {selectedBooking.returnDate && (
                        <>
                          <div className="bg-charcoal p-4 rounded-lg">
                            <p className="text-gray-light text-sm mb-1">{language === 'en' ? 'Return Date' : 'تاريخ العودة'}</p>
                            <p className="text-cream">{new Date(selectedBooking.returnDate).toLocaleDateString()}</p>
                          </div>
                          {selectedBooking.returnTime && (
                            <div className="bg-charcoal p-4 rounded-lg">
                              <p className="text-gray-light text-sm mb-1">{language === 'en' ? 'Return Time' : 'وقت العودة'}</p>
                              <p className="text-cream">{selectedBooking.returnTime}</p>
                            </div>
                          )}
                        </>
                      )}
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.passengers}</p>
                        <p className="text-cream">{selectedBooking.passengers}</p>
                      </div>
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.luggage}</p>
                        <p className="text-cream">{selectedBooking.luggage}</p>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle & Driver */}
                  <div>
                    <h3 className="font-display text-lg text-cream mb-4">{language === 'en' ? 'Vehicle & Driver' : 'السيارة والسائق'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.carType}</p>
                        <p className="text-cream">{selectedBooking.carType}</p>
                      </div>
                      {selectedBooking.carId && (
                        <div className="bg-charcoal p-4 rounded-lg">
                          <p className="text-gray-light text-sm mb-1">{language === 'en' ? 'Car' : 'السيارة'}</p>
                          <p className="text-cream">{selectedBooking.carId.name || 'N/A'}</p>
                        </div>
                      )}
                      {selectedBooking.driverId ? (
                        <>
                          <div className="bg-charcoal p-4 rounded-lg">
                            <p className="text-gray-light text-sm mb-1">{language === 'en' ? 'Driver' : 'السائق'}</p>
                            <p className="text-cream">{selectedBooking.driverId.name || 'N/A'}</p>
                          </div>
                          <div className="bg-charcoal p-4 rounded-lg">
                            <p className="text-gray-light text-sm mb-1">{language === 'en' ? 'Driver Phone' : 'هاتف السائق'}</p>
                            <p className="text-cream" dir="ltr">{selectedBooking.driverId.mobile || 'N/A'}</p>
                          </div>
                        </>
                      ) : (
                        <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-lg">
                          <p className="text-yellow-400 text-sm">{language === 'en' ? 'No driver assigned yet' : 'لم يتم تعيين سائق بعد'}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pricing & Status */}
                  <div>
                    <h3 className="font-display text-lg text-cream mb-4">{language === 'en' ? 'Pricing & Status' : 'التسعير والحالة'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.totalCost}</p>
                        <p className="text-primary font-bold text-xl">{selectedBooking.totalCost} EGP</p>
                      </div>
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{t.status}</p>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${selectedBooking.status === 'Confirmed' ? 'bg-green-500/10 text-green-400 border border-green-500/30' :
                          selectedBooking.status === 'Cancelled' ? 'bg-red-500/10 text-red-400 border border-red-500/30' :
                            selectedBooking.status === 'Completed' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30' :
                              'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'
                          }`}>
                          {selectedBooking.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  {selectedBooking.notes && (
                    <div>
                      <h3 className="font-display text-lg text-cream mb-4">{t.notes}</h3>
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-cream whitespace-pre-wrap">{selectedBooking.notes}</p>
                      </div>
                    </div>
                  )}

                  {/* Confirmation Message */}
                  {selectedBooking.confirmationMessage && (
                    <div>
                      <h3 className="font-display text-lg text-cream mb-4">{language === 'en' ? 'Confirmation Message' : 'رسالة التأكيد'}</h3>
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-cream whitespace-pre-wrap text-sm">{selectedBooking.confirmationMessage}</p>
                      </div>
                    </div>
                  )}

                  {/* Payment Information */}
                  {selectedBooking.paymentOption && (
                    <div>
                      <h3 className="font-display text-lg text-cream mb-4">{language === 'en' ? 'Payment Information' : 'معلومات الدفع'}</h3>
                      <div className="space-y-4">
                        <div className="bg-charcoal p-4 rounded-lg">
                          <p className="text-gray-light text-sm mb-2">{language === 'en' ? 'Payment Option' : 'خيار الدفع'}</p>
                          <p className="text-cream font-medium">
                            {selectedBooking.paymentOption === 'payNow' && (language === 'en' ? 'Pay Now' : 'ادفع الآن')}
                            {selectedBooking.paymentOption === 'payCash' && (language === 'en' ? 'Pay Cash' : 'الدفع نقداً')}
                            {selectedBooking.paymentOption === 'contactSupport' && (language === 'en' ? 'Contact Support' : 'اتصل بالدعم')}
                          </p>
                        </div>
                        {selectedBooking.paymentMethod && (
                          <div className="bg-charcoal p-4 rounded-lg">
                            <p className="text-gray-light text-sm mb-2">{language === 'en' ? 'Payment Method' : 'طريقة الدفع'}</p>
                            <p className="text-cream font-medium capitalize">
                              {selectedBooking.paymentMethod === 'bankTransfer' && (language === 'en' ? 'Bank Transfer' : 'تحويل بنكي')}
                              {selectedBooking.paymentMethod === 'vodafone' && (language === 'en' ? 'Vodafone Cash' : 'فودافون كاش')}
                              {selectedBooking.paymentMethod === 'orange' && (language === 'en' ? 'Orange Money' : 'أورنج ماني')}
                              {selectedBooking.paymentMethod === 'etisalat' && (language === 'en' ? 'Etisalat Cash' : 'اتصالات كاش')}
                              {selectedBooking.paymentMethod === 'instapay' && (language === 'en' ? 'InstaPay' : 'إنستاباي')}
                              {selectedBooking.paymentMethod === 'cash' && (language === 'en' ? 'Cash' : 'نقداً')}
                            </p>
                          </div>
                        )}
                        {selectedBooking.paymentProofImage && selectedBooking.paymentProofImage.url && (
                          <div className="bg-charcoal p-4 rounded-lg">
                            <p className="text-gray-light text-sm mb-3">{language === 'en' ? 'Payment Proof Image' : 'صورة إثبات الدفع'}</p>
                            <div className="relative group">
                              <img
                                src={selectedBooking.paymentProofImage.url}
                                alt={language === 'en' ? 'Payment Proof' : 'إثبات الدفع'}
                                className="w-full max-w-md rounded-lg border border-gray-dark cursor-pointer hover:border-primary/50 transition-colors"
                                onClick={() => window.open(selectedBooking.paymentProofImage.url, '_blank')}
                              />
                              <div className="absolute inset-0 bg-blackC/0 group-hover:bg-blackC/20 transition-colors rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <svg className="w-8 h-8 text-cream" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                </svg>
                              </div>
                            </div>
                            <p className="text-gray-silver text-xs mt-2">{language === 'en' ? 'Click image to view full size' : 'انقر على الصورة لعرضها بالحجم الكامل'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div>
                    <h3 className="font-display text-lg text-cream mb-4">{language === 'en' ? 'Timestamps' : 'الطوابع الزمنية'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{language === 'en' ? 'Created At' : 'تاريخ الإنشاء'}</p>
                        <p className="text-cream text-sm">{new Date(selectedBooking.createdAt).toLocaleString()}</p>
                      </div>
                      <div className="bg-charcoal p-4 rounded-lg">
                        <p className="text-gray-light text-sm mb-1">{language === 'en' ? 'Updated At' : 'تاريخ التحديث'}</p>
                        <p className="text-cream text-sm">{new Date(selectedBooking.updatedAt).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Booking Modal */}
          {editingBooking && (
            <div className="fixed inset-0 bg-blackC/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-dark border border-gray-dark rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto my-8">
                <div className="p-6 border-b border-gray-dark flex justify-between items-center sticky top-0 bg-dark z-10">
                  <h2 className="font-display text-2xl text-cream">{language === 'en' ? 'Edit Booking' : 'تعديل الحجز'}</h2>
                  <button
                    onClick={() => setEditingBooking(null)}
                    className="w-10 h-10 rounded-lg bg-charcoal flex items-center justify-center text-gray-light hover:text-cream transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const updates = {
                    status: formData.get('status'),
                    totalCost: parseFloat(formData.get('totalCost')) || 0,
                    driverId: formData.get('driverId') || null,
                    carId: formData.get('carId') || null,
                    date: formData.get('date') ? new Date(formData.get('date')).toISOString() : editingBooking.date,
                    time: formData.get('time') || editingBooking.time,
                    returnDate: formData.get('returnDate') ? new Date(formData.get('returnDate')).toISOString() : null,
                    returnTime: formData.get('returnTime') || null,
                    passengers: parseInt(formData.get('passengers')) || editingBooking.passengers,
                    luggage: parseInt(formData.get('luggage')) || editingBooking.luggage,
                    notes: formData.get('notes') || null
                  };
                  // Remove null/empty values
                  Object.keys(updates).forEach(key => {
                    if (updates[key] === null || updates[key] === '') {
                      delete updates[key];
                    }
                  });
                  await updateBooking(editingBooking._id, updates);
                }} className="p-6 space-y-6">
                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{t.status}</label>
                    <select
                      name="status"
                      defaultValue={editingBooking.status}
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      required
                    >
                      <option value="Pending">{language === 'en' ? 'Pending' : 'قيد الانتظار'}</option>
                      <option value="Confirmed">{language === 'en' ? 'Confirmed' : 'مؤكد'}</option>
                      <option value="Cancelled">{language === 'en' ? 'Cancelled' : 'ملغي'}</option>
                      <option value="Completed">{language === 'en' ? 'Completed' : 'مكتمل'}</option>
                    </select>
                  </div>

                  {/* Total Cost */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{t.totalCost}</label>
                    <input
                      type="number"
                      name="totalCost"
                      defaultValue={editingBooking.totalCost}
                      min="0"
                      step="0.01"
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      required
                    />
                  </div>

                  {/* Car Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Car' : 'السيارة'}</label>
                    <select
                      name="carId"
                      defaultValue={editingBooking.carId?._id || ''}
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                    >
                      <option value="">{language === 'en' ? 'No Car Selected' : 'لم يتم اختيار سيارة'}</option>
                      {cars.map(car => (
                        <option key={car._id} value={car._id}>
                          {car.name} - {car.type}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Driver Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Driver' : 'السائق'}</label>
                    <select
                      name="driverId"
                      defaultValue={editingBooking.driverId?._id || ''}
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                    >
                      <option value="">{language === 'en' ? 'No Driver Selected' : 'لم يتم اختيار سائق'}</option>
                      {drivers.map(driver => (
                        <option key={driver._id} value={driver._id}>
                          {driver.name} - {driver.mobile} ({driver.assignedCarType}, {driver.status})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date & Time */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.date}</label>
                      <input
                        type="date"
                        name="date"
                        defaultValue={editingBooking.date ? new Date(editingBooking.date).toISOString().split('T')[0] : ''}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.time}</label>
                      <input
                        type="time"
                        name="time"
                        defaultValue={editingBooking.time}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>
                  </div>

                  {/* Return Date & Time (if round trip) */}
                  {editingBooking.tripType === 'roundTrip' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Return Date' : 'تاريخ العودة'}</label>
                        <input
                          type="date"
                          name="returnDate"
                          defaultValue={editingBooking.returnDate ? new Date(editingBooking.returnDate).toISOString().split('T')[0] : ''}
                          className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Return Time' : 'وقت العودة'}</label>
                        <input
                          type="time"
                          name="returnTime"
                          defaultValue={editingBooking.returnTime || ''}
                          className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        />
                      </div>
                    </div>
                  )}

                  {/* Passengers & Luggage */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.passengers}</label>
                      <input
                        type="number"
                        name="passengers"
                        defaultValue={editingBooking.passengers}
                        min="1"
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.luggage}</label>
                      <input
                        type="number"
                        name="luggage"
                        defaultValue={editingBooking.luggage}
                        min="0"
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{t.notes}</label>
                    <textarea
                      name="notes"
                      defaultValue={editingBooking.notes || ''}
                      rows="4"
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                    />
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-dark">
                    <button
                      type="button"
                      onClick={() => setEditingBooking(null)}
                      className="px-6 py-3 bg-charcoal border border-gray-dark text-gray-silver rounded-lg hover:bg-gray-dark hover:text-cream transition-colors"
                    >
                      {language === 'en' ? 'Cancel' : 'إلغاء'}
                    </button>
                    <button
                      type="submit"
                      disabled={submittingBookingUpdate}
                      className="px-6 py-3 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {submittingBookingUpdate ? (
                        <>
                          <div className="w-5 h-5 border-2 border-blackC/20 border-t-blackC rounded-full animate-spin" />
                          {language === 'en' ? 'Updating...' : 'جاري التحديث...'}
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {language === 'en' ? 'Update Booking' : 'تحديث الحجز'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Cars */}
          {activeTab === 'cars' && (
            <div>
              <button
                onClick={() => { setEditingCar(null); setSelectedCarImages([]); setShowCarForm(true); }}
                className="mb-6 inline-flex items-center gap-2 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {language === 'en' ? 'Add Car' : 'إضافة سيارة'}
              </button>

              {showCarForm && (
                <div className="bg-dark border border-gray-dark rounded-xl p-6 mb-6">
                  <h2 className="font-display text-2xl text-cream mb-6">
                    {editingCar ? (language === 'en' ? 'Edit Car' : 'تعديل السيارة') : (language === 'en' ? 'Add Car' : 'إضافة سيارة')}
                  </h2>
                  <form onSubmit={handleCarSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input type="text" name="name" placeholder={language === 'en' ? 'Car Name' : 'اسم السيارة'} defaultValue={editingCar?.name} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <select name="type" defaultValue={editingCar?.type} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors">
                        <option value="Sedan">Sedan</option>
                        <option value="SUV">SUV</option>
                        <option value="Van">Van</option>
                        <option value="Mini Van">Mini Van</option>
                        <option value="Coaster">Coaster</option>
                        <option value="Mini Bus">Mini Bus</option>
                        <option value="Bus">Bus</option>
                        <option value="VIP">VIP</option>
                      </select>
                      <input type="text" name="model" placeholder={language === 'en' ? 'Model' : 'الموديل'} defaultValue={editingCar?.model} className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <input type="number" name="passengerCapacity" placeholder={language === 'en' ? 'Passenger Capacity' : 'سعة الركاب'} defaultValue={editingCar?.passengerCapacity} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <input type="number" name="luggageCapacity" placeholder={language === 'en' ? 'Luggage Capacity' : 'سعة الأمتعة'} defaultValue={editingCar?.luggageCapacity} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <select name="pricingType" defaultValue={editingCar?.pricingType} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors">
                        <option value="fixed">{language === 'en' ? 'Fixed Price' : 'سعر ثابت'}</option>
                        <option value="perKm">{language === 'en' ? 'Per Kilometer' : 'لكل كيلومتر'}</option>
                      </select>
                      <input type="number" name="fixedPrice" placeholder={language === 'en' ? 'Fixed Price' : 'السعر الثابت'} defaultValue={editingCar?.fixedPrice} className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <input type="number" name="pricePerKm" placeholder={language === 'en' ? 'Price Per Km' : 'السعر لكل كيلومتر'} defaultValue={editingCar?.pricePerKm} className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                    </div>

                    {editingCar && editingCar.images && editingCar.images.length > 0 && (
                      <div className="space-y-4">
                        <label className="block text-sm font-medium text-gray-silver">
                          {language === 'en' ? 'Current Images' : 'الصور الحالية'}
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {editingCar.images.map((image, index) => (
                            <div key={image._id || index} className="relative group">
                              <img
                                src={image.url}
                                alt={`${editingCar.name} ${index + 1}`}
                                className="w-full h-32 object-cover rounded-lg border border-gray-dark"
                              />
                              <button
                                type="button"
                                onClick={() => deleteCarImage(editingCar._id, image._id)}
                                disabled={deletingImage === image._id}
                                className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-90 hover:opacity-100 transition-opacity hover:bg-red-700 disabled:opacity-50 shadow-lg"
                                title={language === 'en' ? 'Delete image' : 'حذف الصورة'}
                              >
                                {deletingImage === image._id ? (
                                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-silver mb-2">
                        {language === 'en' ? 'Add New Images' : 'إضافة صور جديدة'}
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          name="images"
                          multiple
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files) {
                              setSelectedCarImages(Array.from(e.target.files));
                            }
                          }}
                          className="hidden"
                          id="car-images-upload"
                        />
                        <label
                          htmlFor="car-images-upload"
                          className="flex items-center justify-center gap-3 w-full p-8 border-2 border-dashed border-gray-dark hover:border-primary/50 cursor-pointer transition-colors rounded-lg bg-charcoal"
                        >
                          {selectedCarImages.length > 0 ? (
                            <div className="text-center">
                              <svg className="w-8 h-8 text-primary mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                              </svg>
                              <p className="text-cream font-medium">{selectedCarImages.length} {language === 'en' ? 'file(s) selected' : 'ملف محدد'}</p>
                              <p className="text-gray-light text-sm">{language === 'en' ? 'Click to change' : 'انقر للتغيير'}</p>
                            </div>
                          ) : (
                            <div className="text-center">
                              <svg className="w-8 h-8 text-gray-light mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <p className="text-gray-silver">{language === 'en' ? 'Click to upload car images' : 'انقر لرفع صور السيارة'}</p>
                              <p className="text-gray-light text-sm">{language === 'en' ? 'PNG, JPG up to 10MB each' : 'PNG, JPG حتى 10MB لكل صورة'}</p>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button type="submit" disabled={submittingCar} className="bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                        {submittingCar ? (language === 'en' ? 'Saving...' : 'جاري الحفظ...') : (language === 'en' ? 'Save' : 'حفظ')}
                      </button>
                      <button type="button" onClick={() => { setShowCarForm(false); setEditingCar(null); setSelectedCarImages([]); }} disabled={submittingCar} className="bg-charcoal border border-gray-dark text-gray-silver px-6 py-3 rounded-lg hover:bg-gray-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        {language === 'en' ? 'Cancel' : 'إلغاء'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cars.map((car) => (
                  <div key={car._id} className="bg-dark border border-gray-dark rounded-xl overflow-hidden hover:border-primary/30 transition-all group">
                    {car.images && car.images.length > 0 && (
                      <div className="aspect-video overflow-hidden">
                        <img src={car.images[0].url} alt={car.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="font-display text-xl text-cream mb-2">{car.name}</h3>
                      <div className="space-y-2 text-sm text-gray-silver mb-4">
                        <p>{t.type}: <span className="text-primary">{car.type}</span></p>
                        <p>{t.passengers}: <span className="text-cream">{car.passengerCapacity}</span></p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={async () => {
                          try {
                            const carRes = await api.get(`/cars/${car._id}`);
                            setEditingCar(carRes.data);
                            setShowCarForm(true);
                          } catch (error) {
                            await alert(
                              error.response?.data?.error || (language === 'en' ? 'Error loading car details' : 'خطأ في تحميل تفاصيل السيارة'),
                              language === 'en' ? 'Error' : 'خطأ',
                              'error'
                            );
                          }
                        }} className="flex-1 py-2 bg-charcoal text-primary rounded-lg hover:bg-primary/10 transition-colors">
                          {language === 'en' ? 'Edit' : 'تعديل'}
                        </button>
                        <button onClick={() => deleteCar(car._id)} className="flex-1 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors">
                          {language === 'en' ? 'Delete' : 'حذف'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Drivers */}
          {activeTab === 'drivers' && (
            <div>
              <button
                onClick={() => { setEditingDriver(null); setSelectedDriverLicenseImage(null); setShowDriverForm(true); }}
                className="mb-6 inline-flex items-center gap-2 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {language === 'en' ? 'Add Driver' : 'إضافة سائق'}
              </button>

              {showDriverForm && (
                <div className="bg-dark border border-gray-dark rounded-xl p-6 mb-6">
                  <h2 className="font-display text-2xl text-cream mb-6">
                    {editingDriver ? (language === 'en' ? 'Edit Driver' : 'تعديل السائق') : (language === 'en' ? 'Add Driver' : 'إضافة سائق')}
                  </h2>
                  <form onSubmit={handleDriverSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input type="text" name="name" placeholder={language === 'en' ? 'Driver Name' : 'اسم السائق'} defaultValue={editingDriver?.name} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <input type="tel" name="mobile" placeholder={language === 'en' ? 'Mobile' : 'الجوال'} defaultValue={editingDriver?.mobile} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" dir="ltr" />
                      <select name="assignedCarType" defaultValue={editingDriver?.assignedCarType} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors">
                        <option value="Sedan">Sedan</option>
                        <option value="SUV">SUV</option>
                        <option value="Van">Van</option>
                        <option value="Mini Van">Mini Van</option>
                        <option value="Coaster">Coaster</option>
                        <option value="Mini Bus">Mini Bus</option>
                        <option value="Bus">Bus</option>
                        <option value="VIP">VIP</option>
                      </select>
                      <select name="status" defaultValue={editingDriver?.status} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors">
                        <option value="Available">{language === 'en' ? 'Available' : 'متاح'}</option>
                        <option value="Busy">{language === 'en' ? 'Busy' : 'مشغول'}</option>
                      </select>
                    </div>

                    {editingDriver && editingDriver.licenseImage && editingDriver.licenseImage.url && (
                      <div className="space-y-4">
                        <label className="block text-sm font-medium text-gray-silver">
                          {language === 'en' ? 'Current License Image' : 'صورة الرخصة الحالية'}
                        </label>
                        <div className="relative inline-block group">
                          <img
                            src={editingDriver.licenseImage.url}
                            alt={`${editingDriver.name} License`}
                            className="max-w-full h-64 object-contain rounded-lg border border-gray-dark bg-charcoal p-2"
                          />
                          <button
                            type="button"
                            onClick={() => deleteDriverLicenseImage(editingDriver._id)}
                            disabled={deletingDriverImage}
                            className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-90 hover:opacity-100 transition-opacity hover:bg-red-700 disabled:opacity-50 shadow-lg"
                            title={language === 'en' ? 'Delete license image' : 'حذف صورة الرخصة'}
                          >
                            {deletingDriverImage ? (
                              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-silver mb-2">
                        {editingDriver && editingDriver.licenseImage && editingDriver.licenseImage.url
                          ? (language === 'en' ? 'Replace License Image' : 'استبدال صورة الرخصة')
                          : (language === 'en' ? 'Upload License Image' : 'رفع صورة الرخصة')}
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          name="licenseImage"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              setSelectedDriverLicenseImage(e.target.files[0]);
                            }
                          }}
                          className="hidden"
                          id="driver-license-upload"
                        />
                        <label
                          htmlFor="driver-license-upload"
                          className="flex items-center justify-center gap-3 w-full p-8 border-2 border-dashed border-gray-dark hover:border-primary/50 cursor-pointer transition-colors rounded-lg bg-charcoal"
                        >
                          {selectedDriverLicenseImage ? (
                            <div className="text-center">
                              <svg className="w-8 h-8 text-primary mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                              </svg>
                              <p className="text-cream font-medium">{selectedDriverLicenseImage.name}</p>
                              <p className="text-gray-light text-sm">{language === 'en' ? 'Click to change' : 'انقر للتغيير'}</p>
                            </div>
                          ) : (
                            <div className="text-center">
                              <svg className="w-8 h-8 text-gray-light mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <p className="text-gray-silver">{language === 'en' ? 'Click to upload license image' : 'انقر لرفع صورة الرخصة'}</p>
                              <p className="text-gray-light text-sm">{language === 'en' ? 'PNG, JPG up to 10MB' : 'PNG, JPG حتى 10MB'}</p>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button type="submit" disabled={submittingDriver || deletingDriverImage} className="bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                        {submittingDriver ? (language === 'en' ? 'Saving...' : 'جاري الحفظ...') : (language === 'en' ? 'Save' : 'حفظ')}
                      </button>
                      <button type="button" onClick={() => { setShowDriverForm(false); setEditingDriver(null); setSelectedDriverLicenseImage(null); }} disabled={submittingDriver || deletingDriverImage} className="bg-charcoal border border-gray-dark text-gray-silver px-6 py-3 rounded-lg hover:bg-gray-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        {language === 'en' ? 'Cancel' : 'إلغاء'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {drivers.map((driver) => (
                  <div key={driver._id} className="bg-dark border border-gray-dark rounded-xl p-6 hover:border-primary/30 transition-all">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-display text-lg text-cream">{driver.name}</h3>
                        <p className="text-gray-light text-sm" dir="ltr">{driver.mobile}</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex justify-between">
                        <span className="text-gray-light">{language === 'en' ? 'Car Type' : 'نوع السيارة'}</span>
                        <span className="text-cream">{driver.assignedCarType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-light">{t.status}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${driver.status === 'Available' ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                          {driver.status === 'Available' ? (language === 'en' ? 'Available' : 'متاح') : (language === 'en' ? 'Busy' : 'مشغول')}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={async () => {
                        try {
                          const driverRes = await api.get(`/drivers/${driver._id}`);
                          setEditingDriver(driverRes.data);
                          setShowDriverForm(true);
                        } catch (error) {
                          await alert(
                            error.response?.data?.error || (language === 'en' ? 'Error loading driver details' : 'خطأ في تحميل تفاصيل السائق'),
                            language === 'en' ? 'Error' : 'خطأ',
                            'error'
                          );
                        }
                      }} className="flex-1 py-2 bg-charcoal text-primary rounded-lg hover:bg-primary/10 transition-colors">
                        {language === 'en' ? 'Edit' : 'تعديل'}
                      </button>
                      <button onClick={() => deleteDriver(driver._id)} className="flex-1 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors">
                        {language === 'en' ? 'Delete' : 'حذف'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Destinations */}
          {activeTab === 'destinations' && (
            <div>
              <button
                onClick={() => { setEditingDestination(null); setSelectedDestinationImage(null); setShowDestinationForm(true); }}
                className="mb-6 inline-flex items-center gap-2 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {language === 'en' ? 'Add Destination' : 'إضافة وجهة'}
              </button>

              {showDestinationForm && (
                <div className="bg-dark border border-gray-dark rounded-xl p-6 mb-6">
                  <h2 className="font-display text-2xl text-cream mb-6">
                    {editingDestination ? (language === 'en' ? 'Edit Destination' : 'تعديل الوجهة') : (language === 'en' ? 'Add Destination' : 'إضافة وجهة')}
                  </h2>
                  <form onSubmit={handleDestinationSubmit} className="space-y-4" encType="multipart/form-data">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input type="text" name="name" placeholder={language === 'en' ? 'Name (EN)' : 'الاسم (إنجليزي)'} defaultValue={editingDestination?.name} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <input type="text" name="nameAr" placeholder={language === 'en' ? 'Name (AR)' : 'الاسم (عربي)'} defaultValue={editingDestination?.nameAr} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <select name="pricingType" defaultValue={editingDestination?.pricingType} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors">
                        <option value="fixed">{language === 'en' ? 'Fixed Price' : 'سعر ثابت'}</option>
                        <option value="perKm">{language === 'en' ? 'Per Kilometer' : 'لكل كيلومتر'}</option>
                      </select>
                      <input type="number" name="fixedPrice" placeholder={language === 'en' ? 'Fixed Price' : 'السعر الثابت'} defaultValue={editingDestination?.fixedPrice} className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <input type="number" name="pricePerKm" placeholder={language === 'en' ? 'Price Per Km' : 'السعر لكل كيلومتر'} defaultValue={editingDestination?.pricePerKm} className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <input type="number" name="distance" placeholder={language === 'en' ? 'Distance (km)' : 'المسافة (كم)'} defaultValue={editingDestination?.distance} className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                    </div>
                    {/* Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">
                        {language === 'en' ? 'Destination Image' : 'صورة الوجهة'}
                      </label>

                      {editingDestination?.image?.url && !selectedDestinationImage && (
                        <div className="mb-4">
                          <p className="text-xs text-gray-light mb-2">{language === 'en' ? 'Current Image:' : 'الصورة الحالية:'}</p>
                          <div className="relative inline-block group">
                            <img
                              src={editingDestination.image.url}
                              alt={editingDestination.name}
                              className="w-full h-48 object-cover rounded-lg border border-gray-dark"
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                const confirmed = await confirm(
                                  language === 'en' ? 'Delete destination image?' : 'حذف صورة الوجهة؟',
                                  language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
                                );
                                if (confirmed) {
                                  try {
                                    await api.delete(`/admin/images?publicId=${encodeURIComponent(editingDestination.image.publicId)}&entityType=destination&entityId=${editingDestination._id}`);
                                    const updatedDestRes = await api.get(`/destinations/${editingDestination._id}`);
                                    setEditingDestination(updatedDestRes.data);
                                    fetchData();
                                  } catch (error) {
                                    await alert(
                                      error.response?.data?.error || (language === 'en' ? 'Error deleting image' : 'خطأ في حذف الصورة'),
                                      language === 'en' ? 'Error' : 'خطأ',
                                      'error'
                                    );
                                  }
                                }
                              }}
                              className="absolute top-2 right-2 p-2 bg-red-500/90 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                              title={language === 'en' ? 'Delete image' : 'حذف الصورة'}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="relative">
                        <input
                          type="file"
                          name="image"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              setSelectedDestinationImage(e.target.files[0]);
                            }
                          }}
                          className="hidden"
                          id="destination-image-upload"
                        />
                        <label
                          htmlFor="destination-image-upload"
                          className="flex items-center justify-center gap-3 w-full p-8 border-2 border-dashed border-gray-dark hover:border-primary/50 cursor-pointer transition-colors rounded-lg bg-charcoal"
                        >
                          {selectedDestinationImage ? (
                            <div className="text-center">
                              <svg className="w-8 h-8 text-primary mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                              </svg>
                              <p className="text-cream font-medium">{selectedDestinationImage.name}</p>
                              <p className="text-gray-light text-sm">{language === 'en' ? 'Click to change' : 'انقر للتغيير'}</p>
                            </div>
                          ) : (
                            <div className="text-center">
                              <svg className="w-8 h-8 text-gray-light mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <p className="text-gray-silver">{language === 'en' ? 'Click to upload destination image' : 'انقر لرفع صورة الوجهة'}</p>
                              <p className="text-gray-light text-sm">{language === 'en' ? 'PNG, JPG up to 10MB' : 'PNG, JPG حتى 10MB'}</p>
                            </div>
                          )}
                        </label>
                      </div>

                      {selectedDestinationImage && (
                        <div className="mt-4">
                          <p className="text-xs text-gray-light mb-2">{language === 'en' ? 'Preview:' : 'معاينة:'}</p>
                          <img
                            src={URL.createObjectURL(selectedDestinationImage)}
                            alt="Preview"
                            className="w-full h-48 object-cover rounded-lg border border-gray-dark"
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button type="submit" className="bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all">
                        {language === 'en' ? 'Save' : 'حفظ'}
                      </button>
                      <button type="button" onClick={() => { setShowDestinationForm(false); setEditingDestination(null); setSelectedDestinationImage(null); }} className="bg-charcoal border border-gray-dark text-gray-silver px-6 py-3 rounded-lg hover:bg-gray-dark transition-colors">
                        {language === 'en' ? 'Cancel' : 'إلغاء'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {destinations.map((dest) => (
                  <div key={dest._id} className="bg-dark border border-gray-dark rounded-xl p-6 hover:border-primary/30 transition-all">
                    {dest.image?.url ? (
                      <div className="mb-4">
                        <img src={dest.image.url} alt={dest.name} className="w-full h-48 object-cover rounded-lg mb-4" />
                      </div>
                    ) : (
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-display text-lg text-cream">{dest.name}</h3>
                          <p className="text-gray-light text-sm">{dest.nameAr}</p>
                        </div>
                      </div>
                    )}
                    {dest.image?.url && (
                      <div className="mb-4">
                        <h3 className="font-display text-lg text-cream">{dest.name}</h3>
                        <p className="text-gray-light text-sm">{dest.nameAr}</p>
                      </div>
                    )}
                    <div className="space-y-2 text-sm mb-4">
                      {dest.fixedPrice && (
                        <div className="flex justify-between">
                          <span className="text-gray-light">{t.price}</span>
                          <span className="text-primary font-medium">{dest.fixedPrice} EGP</span>
                        </div>
                      )}
                      {dest.pricePerKm && (
                        <div className="flex justify-between">
                          <span className="text-gray-light">{t.price}</span>
                          <span className="text-primary font-medium">{dest.pricePerKm} EGP/km</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingDestination(dest); setSelectedDestinationImage(null); setShowDestinationForm(true); }} className="flex-1 py-2 bg-charcoal text-primary rounded-lg hover:bg-primary/10 transition-colors">
                        {language === 'en' ? 'Edit' : 'تعديل'}
                      </button>
                      <button onClick={() => deleteDestination(dest._id)} className="flex-1 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors">
                        {language === 'en' ? 'Delete' : 'حذف'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Route Presets */}
          {activeTab === 'routePresets' && (
            <div>
              <button
                onClick={() => { setEditingRoutePreset(null); setShowRoutePresetForm(true); }}
                className="mb-6 inline-flex items-center gap-2 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {language === 'en' ? 'Add Route Preset' : 'إضافة مسار مسبق'}
              </button>

              {showRoutePresetForm && (
                <div className="bg-dark border border-gray-dark rounded-xl p-6 mb-6">
                  <h2 className="font-display text-2xl text-cream mb-6">
                    {editingRoutePreset ? (language === 'en' ? 'Edit Route Preset' : 'تعديل المسار المسبق') : (language === 'en' ? 'Add Route Preset' : 'إضافة مسار مسبق')}
                  </h2>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const data = {
                      pickupLocation: formData.get('pickupLocation'),
                      dropoffLocation: formData.get('dropoffLocation'),
                      price: parseFloat(formData.get('price')),
                      carType: formData.get('carType') || 'all'
                    };
                    try {
                      if (editingRoutePreset) {
                        await api.patch(`/route-presets/${editingRoutePreset._id}`, data);
                      } else {
                        await api.post('/route-presets', data);
                      }
                      setShowRoutePresetForm(false);
                      setEditingRoutePreset(null);
                      const presetsRes = await api.get('/route-presets/all');
                      setRoutePresets(presetsRes.data);
                    } catch (error) {
                      await alert(
                        error.response?.data?.error || (language === 'en' ? 'Error saving preset' : 'خطأ في حفظ المسار المسبق'),
                        language === 'en' ? 'Error' : 'خطأ',
                        'error'
                      );
                    }
                  }} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input type="text" name="pickupLocation" placeholder={language === 'en' ? 'Pickup Location' : 'موقع الاستلام'} defaultValue={editingRoutePreset?.pickupLocation} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <input type="text" name="dropoffLocation" placeholder={language === 'en' ? 'Dropoff Location' : 'موقع الوصول'} defaultValue={editingRoutePreset?.dropoffLocation} required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <input type="number" name="price" placeholder={language === 'en' ? 'Price (EGP)' : 'السعر (جنيه)'} defaultValue={editingRoutePreset?.price} min="0" step="0.01" required className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors" />
                      <select name="carType" defaultValue={editingRoutePreset?.carType || 'all'} className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors">
                        <option value="all">{language === 'en' ? 'All Car Types' : 'جميع أنواع السيارات'}</option>
                        {['Sedan', 'SUV', 'Van', 'Mini Van', 'Coaster', 'Mini Bus', 'Bus', 'VIP'].map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-3">
                      <button type="submit" className="bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all">
                        {language === 'en' ? 'Save' : 'حفظ'}
                      </button>
                      <button type="button" onClick={() => { setShowRoutePresetForm(false); setEditingRoutePreset(null); }} className="bg-charcoal border border-gray-dark text-gray-silver px-6 py-3 rounded-lg hover:bg-gray-dark transition-colors">
                        {language === 'en' ? 'Cancel' : 'إلغاء'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {routePresets.map((preset) => (
                  <div key={preset._id} className="bg-dark border border-gray-dark rounded-xl p-6 hover:border-primary/30 transition-all">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-display text-lg text-cream">{preset.pickupLocation}</h3>
                        <p className="text-gray-light text-sm">→ {preset.dropoffLocation}</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex justify-between">
                        <span className="text-gray-light">{t.price}</span>
                        <span className="text-primary font-medium">{preset.price} EGP</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-light">{language === 'en' ? 'Car Type' : 'نوع السيارة'}</span>
                        <span className="text-cream">{preset.carType === 'all' ? (language === 'en' ? 'All' : 'الكل') : preset.carType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-light">{t.status}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${preset.isActive ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-gray-500/10 text-gray-400 border border-gray-500/30'}`}>
                          {preset.isActive ? (language === 'en' ? 'Active' : 'نشط') : (language === 'en' ? 'Inactive' : 'غير نشط')}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingRoutePreset(preset); setShowRoutePresetForm(true); }} className="flex-1 py-2 bg-charcoal text-primary rounded-lg hover:bg-primary/10 transition-colors">
                        {language === 'en' ? 'Edit' : 'تعديل'}
                      </button>
                      <button onClick={async () => {
                        const confirmed = await confirm(
                          language === 'en' ? 'Delete this preset?' : 'حذف هذا المسار المسبق؟',
                          language === 'en' ? 'Confirm Delete' : 'تأكيد الحذف'
                        );
                        if (confirmed) {
                          try {
                            await api.delete(`/route-presets/${preset._id}`);
                            const presetsRes = await api.get('/route-presets/all');
                            setRoutePresets(presetsRes.data);
                          } catch (error) {
                            await alert(
                              error.response?.data?.error || (language === 'en' ? 'Error deleting preset' : 'خطأ في حذف المسار المسبق'),
                              language === 'en' ? 'Error' : 'خطأ',
                              'error'
                            );
                          }
                        }
                      }} className="flex-1 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors">
                        {language === 'en' ? 'Delete' : 'حذف'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payments */}
          {activeTab === 'payments' && (
            <div className="space-y-6">
              {/* Create Payment Button */}
              <div className="flex justify-between items-center">
                <h2 className="font-display text-2xl text-cream">
                  {language === 'en' ? 'Payments Management' : 'إدارة المدفوعات'}
                </h2>
                <button
                  onClick={() => setShowCreatePaymentModal(true)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {language === 'en' ? 'Create New Payment' : 'إنشاء دفعة جديدة'}
                </button>
              </div>

              {/* Filters & Search Bar */}
              <div className="bg-dark border border-gray-dark rounded-xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Search' : 'بحث'}</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={language === 'en' ? 'Search by booking ID or method...' : 'ابحث برقم الحجز أو طريقة الدفع...'}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-2 pl-10 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      />
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Status' : 'الحالة'}</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-2 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                    >
                      <option value="all">{language === 'en' ? 'All Status' : 'جميع الحالات'}</option>
                      <option value="Unpaid">{t.unpaid}</option>
                      <option value="Paid">{t.paid}</option>
                      <option value="Verified">{t.verified}</option>
                    </select>
                  </div>

                  {/* Clear Filters & Export */}
                  <div className="flex items-end gap-2">
                    {(searchQuery || statusFilter !== 'all') && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setStatusFilter('all');
                        }}
                        className="flex-1 px-4 py-2 bg-charcoal border border-gray-dark text-gray-silver rounded-lg hover:bg-gray-dark hover:text-cream transition-colors text-sm"
                      >
                        {language === 'en' ? 'Clear' : 'مسح'}
                      </button>
                    )}
                    <button
                      onClick={exportPayments}
                      className="flex-1 px-4 py-2 bg-primary/10 border border-primary/30 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {language === 'en' ? 'Export' : 'تصدير'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Payments Table/Cards */}
              <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                <div className="p-4 border-b border-gray-dark flex items-center justify-between">
                  <h3 className="font-display text-lg text-cream">
                    {language === 'en' ? 'Payments' : 'المدفوعات'} ({getFilteredPayments().length})
                  </h3>
                  {/* View Toggle */}
                  <div className="flex items-center gap-2 bg-charcoal rounded-lg p-1 border border-gray-dark">
                    <button
                      onClick={() => setViewMode(prev => ({ ...prev, payments: 'table' }))}
                      className={`p-2 rounded transition-colors ${viewMode.payments === 'table' ? 'bg-primary text-blackC' : 'text-gray-light hover:text-cream'}`}
                      title={language === 'en' ? 'Table View' : 'عرض الجدول'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode(prev => ({ ...prev, payments: 'cards' }))}
                      className={`p-2 rounded transition-colors ${viewMode.payments === 'cards' ? 'bg-primary text-blackC' : 'text-gray-light hover:text-cream'}`}
                      title={language === 'en' ? 'Card View' : 'عرض البطاقات'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {viewMode.payments === 'table' ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-dark">
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Booking ID' : 'رقم الحجز'}</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-light uppercase tracking-wider">{t.paymentMethods}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{t.totalCost}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{t.status}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Actions' : 'الإجراءات'}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-dark">
                        {getFilteredPayments().length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-12 text-center text-gray-light">
                              {language === 'en' ? 'No payments found' : 'لا توجد مدفوعات'}
                            </td>
                          </tr>
                        ) : (
                          getFilteredPayments().map((payment) => (
                            <tr key={payment._id} className="hover:bg-charcoal transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-cream font-mono">{(payment.bookingId?._id || payment.bookingId || '').slice(-8)}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-silver">{payment.paymentMethod}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-primary font-medium">{payment.amount} EGP</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${payment.status === 'Verified' ? 'bg-green-500/10 text-green-400 border border-green-500/30' :
                                  payment.status === 'Paid' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30' :
                                    'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'
                                  }`}>
                                  {payment.status === 'Verified' ? t.verified : payment.status === 'Paid' ? t.paid : t.unpaid}
                                </span>
                              </td>
                              <td className={`px-6 py-4 whitespace-nowrap text-sm space-x-2 flex items-center gap-2 ${payment.status !== 'Verified' ? '' : ''}`}>
                                {payment.proofImage && (
                                  <a href={payment.proofImage.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-[#d4af37] transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                    {language === 'en' ? 'View Proof' : 'عرض الإثبات'}
                                  </a>
                                )}
                                {payment.status !== 'Verified' && (
                                  <button onClick={() => verifyPayment(payment._id)} className="text-green-400 hover:text-green-300 transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                    {language === 'en' ? 'Approve' : 'قبول'}
                                  </button>
                                )}
                                <button onClick={() => deletePayment(payment._id)} className="text-red-400 hover:text-red-300 transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                  {language === 'en' ? 'Delete' : 'حذف'}
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  /* Payments Cards View */
                  <div className="p-4">
                    {getFilteredPayments().length === 0 ? (
                      <div className="text-center py-12 text-gray-light">
                        {language === 'en' ? 'No payments found' : 'لا توجد مدفوعات'}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {getFilteredPayments().map((payment) => (
                          <div key={payment._id} className="bg-charcoal border border-gray-dark rounded-xl p-4 hover:border-primary/30 transition-all">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className="text-gray-light text-xs mb-1">{language === 'en' ? 'Booking ID' : 'رقم الحجز'}</p>
                                <p className="text-cream font-mono text-sm">{(payment.bookingId?._id || payment.bookingId || '').slice(-8)}</p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${payment.status === 'Verified' ? 'bg-green-500/10 text-green-400 border border-green-500/30' :
                                payment.status === 'Paid' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30' :
                                  'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'
                                }`}>
                                {payment.status === 'Verified' ? t.verified : payment.status === 'Paid' ? t.paid : t.unpaid}
                              </span>
                            </div>
                            <div className="space-y-2 text-sm mb-4">
                              <div className="flex items-center justify-between">
                                <span className="text-gray-light">{t.paymentMethods}</span>
                                <span className="text-cream">{payment.paymentMethod}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-gray-light">{t.totalCost}</span>
                                <span className="text-primary font-medium">{payment.amount} EGP</span>
                              </div>
                              {payment.createdAt && (
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-light">{language === 'en' ? 'Date' : 'التاريخ'}</span>
                                  <span className="text-gray-silver text-xs">{new Date(payment.createdAt).toLocaleDateString()}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 pt-3 border-t border-gray-dark">
                              {payment.proofImage && (
                                <a href={payment.proofImage.url} target="_blank" rel="noopener noreferrer" className="flex-1 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm text-center">
                                  {language === 'en' ? 'View Proof' : 'عرض الإثبات'}
                                </a>
                              )}
                              {payment.status !== 'Verified' && (
                                <button onClick={() => verifyPayment(payment._id)} className="flex-1 py-2 bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors text-sm">
                                  {language === 'en' ? 'Approve' : 'قبول'}
                                </button>
                              )}
                              <button onClick={() => deletePayment(payment._id)} className="flex-1 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm">
                                {language === 'en' ? 'Delete' : 'حذف'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Create Payment Modal */}
          {showCreatePaymentModal && (
            <div className="fixed inset-0 bg-blackC/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-dark border border-gray-dark rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
                <div className="p-6 border-b border-gray-dark flex justify-between items-center sticky top-0 bg-dark z-10">
                  <h2 className="font-display text-2xl text-cream">
                    {language === 'en' ? 'Create New Payment' : 'إنشاء دفعة جديدة'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowCreatePaymentModal(false);
                      setPaymentFormData({
                        bookingId: '',
                        paymentMethod: 'bankTransfer',
                        amount: '',
                        status: 'Unpaid',
                        proofImage: null
                      });
                      setSelectedBookingForPayment(null);
                      setBookingSearchQuery('');
                      setShowBookingDropdown(false);
                      setBookingSearchResults([]);
                    }}
                    className="w-10 h-10 rounded-lg bg-charcoal flex items-center justify-center text-gray-light hover:text-cream transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleCreatePayment} className="p-6 space-y-6">
                  {/* Booking Selector */}
                  <div className="relative booking-selector-container">
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Select Booking' : 'اختر الحجز'}</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={bookingSearchQuery}
                        onChange={(e) => {
                          setBookingSearchQuery(e.target.value);
                          if (e.target.value.length < 2) {
                            setShowBookingDropdown(false);
                            setBookingSearchResults([]);
                          }
                        }}
                        onFocus={() => {
                          if (bookingSearchQuery.length >= 2 && bookingSearchResults.length > 0) {
                            setShowBookingDropdown(true);
                          }
                        }}
                        placeholder={language === 'en' ? 'Search by customer name, mobile, or booking ID...' : 'ابحث باسم العميل، الجوال، أو رقم الحجز...'}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 pr-10 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        required
                      />
                      {selectedBookingForPayment && (
                        <button
                          type="button"
                          onClick={handleClearBooking}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 flex items-center justify-center transition-colors"
                          title={language === 'en' ? 'Clear selection' : 'مسح الاختيار'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                      {loadingBookings && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        </div>
                      )}
                    </div>

                    {/* Selected Booking Display */}
                    {selectedBookingForPayment && (
                      <div className="mt-2 p-3 bg-primary/10 border border-primary/30 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-cream font-medium">{selectedBookingForPayment.fullName}</p>
                            <p className="text-gray-light text-sm" dir="ltr">{selectedBookingForPayment.mobile}</p>
                            <p className="text-gray-light text-sm">{selectedBookingForPayment.pickupLocation} → {selectedBookingForPayment.dropoffLocation}</p>
                            <p className="text-primary font-medium mt-1">{selectedBookingForPayment.totalCost} EGP</p>
                          </div>
                          <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs">
                            {language === 'en' ? 'Selected' : 'محدد'}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Booking Dropdown */}
                    {showBookingDropdown && bookingSearchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-dark border border-gray-dark rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {bookingSearchResults.map((booking) => (
                          <button
                            key={booking._id}
                            type="button"
                            onClick={() => handleBookingSelect(booking)}
                            className="w-full px-4 py-3 text-left hover:bg-charcoal transition-colors border-b border-gray-dark last:border-b-0"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="text-cream font-medium">{booking.fullName}</p>
                                <p className="text-gray-light text-sm" dir="ltr">{booking.mobile}</p>
                                <p className="text-gray-light text-sm">{booking.pickupLocation} → {booking.dropoffLocation}</p>
                                <p className="text-primary font-medium text-sm mt-1">{booking.totalCost} EGP</p>
                              </div>
                              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {showBookingDropdown && bookingSearchQuery.length >= 2 && !loadingBookings && bookingSearchResults.length === 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-dark border border-gray-dark rounded-lg shadow-xl p-4 text-center text-gray-light">
                        {language === 'en' ? 'No bookings found' : 'لا توجد حجوزات'}
                      </div>
                    )}
                  </div>

                  {/* Payment Method & Amount */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.paymentMethods}</label>
                      <select
                        name="paymentMethod"
                        value={paymentFormData.paymentMethod}
                        onChange={handlePaymentFormChange}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        required
                      >
                        <option value="bankTransfer">{language === 'en' ? 'Bank Transfer' : 'تحويل بنكي'}</option>
                        <option value="vodafone">{language === 'en' ? 'Vodafone Cash' : 'فودافون كاش'}</option>
                        <option value="orange">{language === 'en' ? 'Orange Money' : 'أورنج موني'}</option>
                        <option value="etisalat">{language === 'en' ? 'Etisalat Cash' : 'اتصالات كاش'}</option>
                        <option value="instapay">{language === 'en' ? 'InstaPay' : 'إنستاباي'}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">{t.totalCost}</label>
                      <input
                        type="number"
                        name="amount"
                        value={paymentFormData.amount}
                        onChange={handlePaymentFormChange}
                        min="0"
                        step="0.01"
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        required
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Initial Status' : 'الحالة الأولية'}</label>
                    <select
                      name="status"
                      value={paymentFormData.status}
                      onChange={handlePaymentFormChange}
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      required
                    >
                      <option value="Unpaid">{t.unpaid}</option>
                      <option value="Paid">{t.paid}</option>
                      <option value="Verified">{t.verified}</option>
                    </select>
                  </div>

                  {/* Proof Image */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Payment Proof Image (Optional)' : 'صورة إثبات الدفع (اختياري)'}</label>
                    <div className="relative">
                      <input
                        type="file"
                        name="proofImage"
                        accept="image/*"
                        onChange={handlePaymentImageChange}
                        className="hidden"
                        id="payment-proof-upload"
                      />
                      <label
                        htmlFor="payment-proof-upload"
                        className="flex items-center justify-center gap-3 w-full p-8 border-2 border-dashed border-gray-dark hover:border-primary/50 cursor-pointer transition-colors rounded-lg bg-charcoal"
                      >
                        {paymentFormData.proofImage ? (
                          <div className="text-center">
                            <svg className="w-8 h-8 text-primary mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                            </svg>
                            <p className="text-cream font-medium">{paymentFormData.proofImage.name}</p>
                            <p className="text-gray-light text-sm">{language === 'en' ? 'Click to change' : 'انقر للتغيير'}</p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <svg className="w-8 h-8 text-gray-light mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-gray-silver">{language === 'en' ? 'Click to upload payment proof' : 'انقر لرفع إثبات الدفع'}</p>
                            <p className="text-gray-light text-sm">{language === 'en' ? 'PNG, JPG up to 10MB' : 'PNG, JPG حتى 10MB'}</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-dark">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreatePaymentModal(false);
                        setPaymentFormData({
                          bookingId: '',
                          paymentMethod: 'bankTransfer',
                          amount: '',
                          status: 'Unpaid',
                          proofImage: null
                        });
                        setSelectedBookingForPayment(null);
                        setBookingSearchQuery('');
                        setShowBookingDropdown(false);
                        setBookingSearchResults([]);
                      }}
                      className="px-6 py-3 bg-charcoal border border-gray-dark text-gray-silver rounded-lg hover:bg-gray-dark hover:text-cream transition-colors"
                    >
                      {language === 'en' ? 'Cancel' : 'إلغاء'}
                    </button>
                    <button
                      type="submit"
                      disabled={submittingPayment || !paymentFormData.bookingId}
                      className="px-6 py-3 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {submittingPayment ? (
                        <>
                          <div className="w-5 h-5 border-2 border-blackC/20 border-t-blackC rounded-full animate-spin" />
                          {language === 'en' ? 'Creating...' : 'جاري الإنشاء...'}
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {language === 'en' ? 'Create Payment' : 'إنشاء الدفعة'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Reviews */}
          {activeTab === 'reviews' && (
            <div className="space-y-6">
              {/* Filters & Search Bar */}
              <div className="bg-dark border border-gray-dark rounded-xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Search' : 'بحث'}</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={language === 'en' ? 'Search by name or comment...' : 'ابحث بالاسم أو التعليق...'}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-2 pl-10 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      />
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Status' : 'الحالة'}</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-2 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                    >
                      <option value="all">{language === 'en' ? 'All Reviews' : 'جميع التقييمات'}</option>
                      <option value="approved">{language === 'en' ? 'Approved' : 'موافق عليها'}</option>
                      <option value="pending">{language === 'en' ? 'Pending' : 'معلقة'}</option>
                    </select>
                  </div>

                  {/* Clear Filters */}
                  {(searchQuery || statusFilter !== 'all') && (
                    <div className="flex items-end">
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setStatusFilter('all');
                        }}
                        className="w-full px-4 py-2 bg-charcoal border border-gray-dark text-gray-silver rounded-lg hover:bg-gray-dark hover:text-cream transition-colors text-sm"
                      >
                        {language === 'en' ? 'Clear Filters' : 'مسح الفلاتر'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Reviews View Toggle */}
              <div className="flex items-center justify-end mb-4">
                <div className="flex items-center gap-2 bg-charcoal rounded-lg p-1 border border-gray-dark">
                  <button
                    onClick={() => setViewMode(prev => ({ ...prev, reviews: 'cards' }))}
                    className={`p-2 rounded transition-colors ${viewMode.reviews === 'cards' ? 'bg-primary text-blackC' : 'text-gray-light hover:text-cream'}`}
                    title={language === 'en' ? 'Card View' : 'عرض البطاقات'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode(prev => ({ ...prev, reviews: 'table' }))}
                    className={`p-2 rounded transition-colors ${viewMode.reviews === 'table' ? 'bg-primary text-blackC' : 'text-gray-light hover:text-cream'}`}
                    title={language === 'en' ? 'Table View' : 'عرض الجدول'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Reviews List */}
              {viewMode.reviews === 'cards' ? (
                <div className="space-y-4">
                  {getFilteredReviews().length === 0 ? (
                    <div className="bg-dark border border-gray-dark rounded-xl p-12 text-center">
                      <p className="text-gray-light">{language === 'en' ? 'No reviews found' : 'لا توجد تقييمات'}</p>
                    </div>
                  ) : (
                    getFilteredReviews().map((review) => (
                      <div key={review._id} className="bg-dark border border-gray-dark rounded-xl p-6 hover:border-primary/30 transition-all">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-4 mb-3">
                              <div className="flex text-primary">
                                {[...Array(5)].map((_, i) => (
                                  <svg key={i} className={`w-5 h-5 ${i < review.rating ? 'fill-current' : 'stroke-current fill-none'}`} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                  </svg>
                                ))}
                              </div>
                              <span className="font-medium text-cream">{review.customerName}</span>
                              {!review.isApproved && (
                                <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
                                  {language === 'en' ? 'Pending' : 'معلق'}
                                </span>
                              )}
                            </div>
                            {review.comment && <p className="text-gray-silver mb-2">{review.comment}</p>}
                            <p className="text-gray-light text-sm">{new Date(review.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div className="flex gap-2">
                            {!review.isApproved ? (
                              <button onClick={() => approveReview(review._id)} className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors text-sm">
                                {language === 'en' ? 'Approve' : 'موافقة'}
                              </button>
                            ) : (
                              <button onClick={() => hideReview(review._id)} className="px-4 py-2 bg-yellow-500/10 text-yellow-400 rounded-lg hover:bg-yellow-500/20 transition-colors text-sm">
                                {language === 'en' ? 'Hide' : 'إخفاء'}
                              </button>
                            )}
                            <button onClick={() => deleteReview(review._id)} className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm">
                              {language === 'en' ? 'Delete' : 'حذف'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-dark">
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Customer' : 'العميل'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Rating' : 'التقييم'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Comment' : 'التعليق'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{t.status}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Actions' : 'الإجراءات'}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-dark">
                        {getFilteredReviews().length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-12 text-center text-gray-light">
                              {language === 'en' ? 'No reviews found' : 'لا توجد تقييمات'}
                            </td>
                          </tr>
                        ) : (
                          getFilteredReviews().map((review) => (
                            <tr key={review._id} className="hover:bg-charcoal transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-cream font-medium">{review.customerName}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <div className="flex text-primary">
                                  {[...Array(5)].map((_, i) => (
                                    <svg key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-current' : 'stroke-current fill-none'}`} viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                    </svg>
                                  ))}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-silver max-w-xs truncate">{review.comment || '-'}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${review.isApproved ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'}`}>
                                  {review.isApproved ? (language === 'en' ? 'Approved' : 'موافق عليها') : (language === 'en' ? 'Pending' : 'معلق')}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm flex items-center gap-2">
                                {!review.isApproved ? (
                                  <button onClick={() => approveReview(review._id)} className="text-green-400 hover:text-green-300 transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                    {language === 'en' ? 'Approve' : 'موافقة'}
                                  </button>
                                ) : (
                                  <button onClick={() => hideReview(review._id)} className="text-yellow-400 hover:text-yellow-300 transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                    {language === 'en' ? 'Hide' : 'إخفاء'}
                                  </button>
                                )}
                                <button onClick={() => deleteReview(review._id)} className="text-red-400 hover:text-red-300 transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                  {language === 'en' ? 'Delete' : 'حذف'}
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          {activeTab === 'messages' && (
            <div className="space-y-6">
              {/* Filters & Search Bar */}
              <div className="bg-dark border border-gray-dark rounded-xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Search' : 'بحث'}</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={language === 'en' ? 'Search by name, email, or message...' : 'ابحث بالاسم، البريد، أو الرسالة...'}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-2 pl-10 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      />
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Status' : 'الحالة'}</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-2 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                    >
                      <option value="all">{language === 'en' ? 'All Messages' : 'جميع الرسائل'}</option>
                      <option value="unread">{language === 'en' ? 'Unread' : 'غير مقروءة'}</option>
                      <option value="read">{language === 'en' ? 'Read' : 'مقروءة'}</option>
                    </select>
                  </div>

                  {/* Clear Filters */}
                  {(searchQuery || statusFilter !== 'all') && (
                    <div className="flex items-end">
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setStatusFilter('all');
                        }}
                        className="w-full px-4 py-2 bg-charcoal border border-gray-dark text-gray-silver rounded-lg hover:bg-gray-dark hover:text-cream transition-colors text-sm"
                      >
                        {language === 'en' ? 'Clear Filters' : 'مسح الفلاتر'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Messages View Toggle */}
              <div className="flex items-center justify-end mb-4">
                <div className="flex items-center gap-2 bg-charcoal rounded-lg p-1 border border-gray-dark">
                  <button
                    onClick={() => setViewMode(prev => ({ ...prev, messages: 'cards' }))}
                    className={`p-2 rounded transition-colors ${viewMode.messages === 'cards' ? 'bg-primary text-blackC' : 'text-gray-light hover:text-cream'}`}
                    title={language === 'en' ? 'Card View' : 'عرض البطاقات'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode(prev => ({ ...prev, messages: 'table' }))}
                    className={`p-2 rounded transition-colors ${viewMode.messages === 'table' ? 'bg-primary text-blackC' : 'text-gray-light hover:text-cream'}`}
                    title={language === 'en' ? 'Table View' : 'عرض الجدول'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Messages List */}
              {viewMode.messages === 'cards' ? (
                <div className="space-y-4">
                  {getFilteredMessages().length === 0 ? (
                    <div className="bg-dark border border-gray-dark rounded-xl p-12 text-center">
                      <p className="text-gray-light">{language === 'en' ? 'No messages found' : 'لا توجد رسائل'}</p>
                    </div>
                  ) : (
                    getFilteredMessages().map((message) => (
                      <div key={message._id} className={`bg-dark border rounded-xl p-6 transition-all ${!message.isRead ? 'border-primary/50' : 'border-gray-dark hover:border-primary/30'}`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <div>
                                <h3 className="font-medium text-cream">{message.name}</h3>
                                <p className="text-gray-light text-sm">{message.email}</p>
                              </div>
                              {!message.isRead && (
                                <span className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/30">
                                  {language === 'en' ? 'New' : 'جديد'}
                                </span>
                              )}
                            </div>
                            {message.subject && <p className="font-medium text-cream mb-2">{message.subject}</p>}
                            <p className="text-gray-silver mb-3">{message.message}</p>
                            <p className="text-gray-light text-sm">{new Date(message.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div className="flex gap-2">
                            {!message.isRead && (
                              <button onClick={() => markMessageRead(message._id)} className="px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm">
                                {language === 'en' ? 'Mark Read' : 'تعليم كمقروء'}
                              </button>
                            )}
                            <button onClick={() => deleteMessage(message._id)} className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm">
                              {language === 'en' ? 'Delete' : 'حذف'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-dark">
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Name' : 'الاسم'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Email' : 'البريد'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Subject' : 'الموضوع'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Message' : 'الرسالة'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{t.status}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Actions' : 'الإجراءات'}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-dark">
                        {getFilteredMessages().length === 0 ? (
                          <tr>
                            <td colSpan="6" className="px-6 py-12 text-center text-gray-light">
                              {language === 'en' ? 'No messages found' : 'لا توجد رسائل'}
                            </td>
                          </tr>
                        ) : (
                          getFilteredMessages().map((message) => (
                            <tr key={message._id} className="hover:bg-charcoal transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-cream font-medium">{message.name}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-silver">{message.email}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-silver">{message.subject || '-'}</td>
                              <td className="px-6 py-4 text-sm text-gray-silver max-w-xs truncate">{message.message}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${message.isRead ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'}`}>
                                  {message.isRead ? (language === 'en' ? 'Read' : 'مقروءة') : (language === 'en' ? 'Unread' : 'غير مقروءة')}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm flex items-center gap-2">
                                {!message.isRead && (
                                  <button onClick={() => markMessageRead(message._id)} className="text-primary hover:text-[#d4af37] transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                    {language === 'en' ? 'Mark Read' : 'تعليم كمقروء'}
                                  </button>
                                )}
                                <button onClick={() => deleteMessage(message._id)} className="text-red-400 hover:text-red-300 transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs">
                                  {language === 'en' ? 'Delete' : 'حذف'}
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              {/* Create Notification Button */}
              <div className="flex justify-between items-center">
                <h2 className="font-display text-2xl text-cream">
                  {language === 'en' ? 'Notifications Management' : 'إدارة الإشعارات'}
                </h2>
                <button
                  onClick={() => setShowCreateNotificationModal(true)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {language === 'en' ? 'Send Notification' : 'إرسال إشعار'}
                </button>
              </div>

              {/* Notifications List */}
              <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                <div className="p-4 border-b border-gray-dark flex items-center justify-between">
                  <h3 className="font-display text-lg text-cream">
                    {language === 'en' ? 'All Notifications' : 'جميع الإشعارات'} ({notifications.length})
                  </h3>
                  {/* View Toggle */}
                  <div className="flex items-center gap-2 bg-charcoal rounded-lg p-1 border border-gray-dark">
                    <button
                      onClick={() => setViewMode(prev => ({ ...prev, notifications: 'table' }))}
                      className={`p-2 rounded transition-colors ${viewMode.notifications === 'table' ? 'bg-primary text-blackC' : 'text-gray-light hover:text-cream'}`}
                      title={language === 'en' ? 'Table View' : 'عرض الجدول'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode(prev => ({ ...prev, notifications: 'cards' }))}
                      className={`p-2 rounded transition-colors ${viewMode.notifications === 'cards' ? 'bg-primary text-blackC' : 'text-gray-light hover:text-cream'}`}
                      title={language === 'en' ? 'Card View' : 'عرض البطاقات'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                  </div>
                </div>

                {viewMode.notifications === 'table' ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-dark">
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Type' : 'النوع'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Message' : 'الرسالة'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Recipients' : 'المستلمون'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Read Status' : 'حالة القراءة'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Date' : 'التاريخ'}</th>
                          <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Actions' : 'الإجراءات'}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-dark">
                        {notifications.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="px-6 py-12 text-center text-gray-light">
                              {language === 'en' ? 'No notifications found' : 'لا توجد إشعارات'}
                            </td>
                          </tr>
                        ) : (
                          notifications.map((notification) => {
                            const readCount = notification.readBy?.length || 0;
                            const totalRecipients = notification.isForAll ? 'All' : (notification.recipients?.length || 0);
                            const readStatusText = notification.isForAll
                              ? `${readCount} ${language === 'en' ? 'read' : 'مقروء'}`
                              : `${readCount}/${totalRecipients} ${language === 'en' ? 'read' : 'مقروء'}`;

                            return (
                              <tr key={notification._id} className="hover:bg-charcoal transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${notification.type === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/30' :
                                    notification.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/30' :
                                      notification.type === 'warning' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30' :
                                        notification.type === 'announcement' ? 'bg-primary/10 text-primary border border-primary/30' :
                                          'bg-blue-500/10 text-blue-400 border border-blue-500/30'
                                    }`}>
                                    {notification.type}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-silver max-w-xs truncate">{notification.message}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-cream">
                                  {notification.isForAll
                                    ? (language === 'en' ? 'All Users' : 'جميع المستخدمين')
                                    : `${notification.recipients?.length || 0} ${language === 'en' ? 'users' : 'مستخدم'}`
                                  }
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-silver">{readStatusText}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-silver">{new Date(notification.createdAt).toLocaleDateString()}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm flex items-center gap-2">
                                  <button
                                    onClick={() => fetchNotificationReadStatus(notification._id)}
                                    className="text-primary hover:text-[#d4af37] transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs"
                                  >
                                    {language === 'en' ? 'View Status' : 'عرض الحالة'}
                                  </button>
                                  <button
                                    onClick={() => deleteNotification(notification._id)}
                                    className="text-red-400 hover:text-red-300 transition-colors border border-primary/30 rounded-full px-2 py-1 text-xs"
                                  >
                                    {language === 'en' ? 'Delete' : 'حذف'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4">
                    {notifications.length === 0 ? (
                      <div className="text-center py-12 text-gray-light">
                        {language === 'en' ? 'No notifications found' : 'لا توجد إشعارات'}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {notifications.map((notification) => {
                          const readCount = notification.readBy?.length || 0;
                          const totalRecipients = notification.isForAll ? 'All' : (notification.recipients?.length || 0);
                          const readStatusText = notification.isForAll
                            ? `${readCount} ${language === 'en' ? 'read' : 'مقروء'}`
                            : `${readCount}/${totalRecipients} ${language === 'en' ? 'read' : 'مقروء'}`;

                          return (
                            <div key={notification._id} className="bg-charcoal border border-gray-dark rounded-xl p-4 hover:border-primary/30 transition-all">
                              <div className="flex items-start justify-between mb-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${notification.type === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/30' :
                                  notification.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/30' :
                                    notification.type === 'warning' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30' :
                                      notification.type === 'announcement' ? 'bg-primary/10 text-primary border border-primary/30' :
                                        'bg-blue-500/10 text-blue-400 border border-blue-500/30'
                                  }`}>
                                  {notification.type}
                                </span>
                                <span className="text-gray-light text-xs">{new Date(notification.createdAt).toLocaleDateString()}</span>
                              </div>
                              <p className="text-cream mb-3">{notification.message}</p>
                              <div className="space-y-2 text-sm mb-4">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-light">{language === 'en' ? 'Recipients' : 'المستلمون'}</span>
                                  <span className="text-cream">
                                    {notification.isForAll
                                      ? (language === 'en' ? 'All Users' : 'جميع المستخدمين')
                                      : `${notification.recipients?.length || 0} ${language === 'en' ? 'users' : 'مستخدم'}`
                                    }
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-light">{language === 'en' ? 'Read Status' : 'حالة القراءة'}</span>
                                  <span className="text-gray-silver">{readStatusText}</span>
                                </div>
                              </div>
                              <div className="flex gap-2 pt-3 border-t border-gray-dark">
                                <button
                                  onClick={() => fetchNotificationReadStatus(notification._id)}
                                  className="flex-1 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm"
                                >
                                  {language === 'en' ? 'View Status' : 'عرض الحالة'}
                                </button>
                                <button
                                  onClick={() => deleteNotification(notification._id)}
                                  className="flex-1 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
                                >
                                  {language === 'en' ? 'Delete' : 'حذف'}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Read Status Modal */}
              {selectedNotificationForStatus && notificationReadStatus && (
                <div className="fixed inset-0 bg-blackC/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                  <div className="bg-dark border border-gray-dark rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8">
                    <div className="p-6 border-b border-gray-dark flex justify-between items-center sticky top-0 bg-dark z-10">
                      <h2 className="font-display text-2xl text-cream">
                        {language === 'en' ? 'Read Status' : 'حالة القراءة'}
                      </h2>
                      <button
                        onClick={() => {
                          setSelectedNotificationForStatus(null);
                          setNotificationReadStatus(null);
                        }}
                        className="w-10 h-10 rounded-lg bg-charcoal flex items-center justify-center text-gray-light hover:text-cream transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="p-6 space-y-6">
                      {/* Notification Info */}
                      <div className="bg-charcoal border border-gray-dark rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${notificationReadStatus.notification.type === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/30' :
                            notificationReadStatus.notification.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/30' :
                              notificationReadStatus.notification.type === 'warning' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30' :
                                notificationReadStatus.notification.type === 'announcement' ? 'bg-primary/10 text-primary border border-primary/30' :
                                  'bg-blue-500/10 text-blue-400 border border-blue-500/30'
                            }`}>
                            {notificationReadStatus.notification.type}
                          </span>
                          <span className="text-gray-light text-sm">
                            {new Date(notificationReadStatus.notification.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-cream mb-4">{notificationReadStatus.notification.message}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <div>
                            <span className="text-gray-light">{language === 'en' ? 'Total Recipients:' : 'إجمالي المستلمين:'}</span>
                            <span className="text-cream ml-2">{notificationReadStatus.totalRecipients}</span>
                          </div>
                          <div>
                            <span className="text-gray-light">{language === 'en' ? 'Read:' : 'مقروء:'}</span>
                            <span className="text-green-400 ml-2">{notificationReadStatus.readCount}</span>
                          </div>
                          <div>
                            <span className="text-gray-light">{language === 'en' ? 'Unread:' : 'غير مقروء:'}</span>
                            <span className="text-yellow-400 ml-2">{notificationReadStatus.unreadCount}</span>
                          </div>
                        </div>
                      </div>

                      {/* Read Status List */}
                      <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-gray-dark">
                                <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'User' : 'المستخدم'}</th>
                                <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Email' : 'البريد'}</th>
                                <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Phone' : 'الهاتف'}</th>
                                <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Status' : 'الحالة'}</th>
                                <th className={`px-6 py-4 text-xs font-medium text-gray-light uppercase tracking-wider ${language === 'en' ? 'text-left' : 'text-right'}`}>{language === 'en' ? 'Read At' : 'وقت القراءة'}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-dark">
                              {notificationReadStatus.readStatus.map((status, idx) => (
                                <tr key={idx} className="hover:bg-charcoal transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-cream font-medium">{status.user.name || '-'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-silver">{status.user.email}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-silver" dir="ltr">{status.user.phone || '-'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.hasRead
                                      ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                                      : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'
                                      }`}>
                                      {status.hasRead
                                        ? (language === 'en' ? 'Read' : 'مقروء')
                                        : (language === 'en' ? 'Unread' : 'غير مقروء')
                                      }
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-silver">
                                    {status.readAt ? new Date(status.readAt).toLocaleString() : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Create Notification Modal */}
              {showCreateNotificationModal && (
                <div className="fixed inset-0 bg-blackC/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                  <div className="bg-dark border border-gray-dark rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
                    <div className="p-6 border-b border-gray-dark flex justify-between items-center sticky top-0 bg-dark z-10">
                      <h2 className="font-display text-2xl text-cream">
                        {language === 'en' ? 'Send Notification' : 'إرسال إشعار'}
                      </h2>
                      <button
                        onClick={() => {
                          setShowCreateNotificationModal(false);
                          setNotificationFormData({
                            type: 'info',
                            message: '',
                            isForAll: false,
                            recipients: []
                          });
                          setSelectedNotificationUsers([]);
                          setNotificationUserSearchQuery('');
                        }}
                        className="w-10 h-10 rounded-lg bg-charcoal flex items-center justify-center text-gray-light hover:text-cream transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <form onSubmit={handleCreateNotification} className="p-6 space-y-6">
                      {/* Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Type' : 'النوع'}</label>
                        <select
                          name="type"
                          value={notificationFormData.type}
                          onChange={handleNotificationFormChange}
                          className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                          required
                        >
                          <option value="info">{language === 'en' ? 'Info' : 'معلومات'}</option>
                          <option value="success">{language === 'en' ? 'Success' : 'نجاح'}</option>
                          <option value="warning">{language === 'en' ? 'Warning' : 'تحذير'}</option>
                          <option value="error">{language === 'en' ? 'Error' : 'خطأ'}</option>
                          <option value="announcement">{language === 'en' ? 'Announcement' : 'إعلان'}</option>
                        </select>
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Message' : 'الرسالة'}</label>
                        <textarea
                          name="message"
                          value={notificationFormData.message}
                          onChange={handleNotificationFormChange}
                          rows="4"
                          className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                          required
                          placeholder={language === 'en' ? 'Enter notification message...' : 'أدخل رسالة الإشعار...'}
                        />
                      </div>

                      {/* Send To */}
                      <div>
                        <label className="block text-sm font-medium text-gray-light mb-2">{language === 'en' ? 'Send To' : 'إرسال إلى'}</label>
                        <button
                          type="button"
                          onClick={() => {
                            const newValue = !notificationFormData.isForAll;
                            setNotificationFormData(prev => ({ ...prev, isForAll: newValue }));
                            if (newValue) {
                              setSelectedNotificationUsers([]);
                              setNotificationFormData(prev => ({ ...prev, recipients: [] }));
                            }
                          }}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all duration-200 mb-3 ${notificationFormData.isForAll
                            ? 'bg-primary/20 border-primary text-primary'
                            : 'bg-charcoal border-gray-dark text-gray-silver hover:border-primary/50 hover:text-cream'
                            }`}
                        >
                          <span className="font-medium">
                            {language === 'en' ? 'Send to all users' : 'إرسال لجميع المستخدمين'}
                          </span>
                          <div className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${notificationFormData.isForAll ? 'bg-primary' : 'bg-gray-dark'
                            }`}>
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${notificationFormData.isForAll ? 'translate-x-6' : 'translate-x-0'
                              }`} />
                          </div>
                        </button>

                        {!notificationFormData.isForAll && (
                          <div className="relative notification-user-selector-container">
                            <input
                              type="text"
                              value={notificationUserSearchQuery}
                              onChange={(e) => {
                                setNotificationUserSearchQuery(e.target.value);
                                if (e.target.value.length < 2) {
                                  setShowNotificationUserDropdown(false);
                                  setNotificationUsers([]);
                                }
                              }}
                              onFocus={() => {
                                if (notificationUserSearchQuery.length >= 2 && notificationUsers.length > 0) {
                                  setShowNotificationUserDropdown(true);
                                }
                              }}
                              placeholder={language === 'en' ? 'Search users by name, email, or phone...' : 'ابحث عن المستخدمين بالاسم، البريد، أو الهاتف...'}
                              className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                            />
                            {loadingNotificationUsers && (
                              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                              </div>
                            )}

                            {/* User Dropdown */}
                            {showNotificationUserDropdown && notificationUsers.length > 0 && (
                              <div className="absolute z-50 w-full mt-1 bg-dark border border-gray-dark rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                {notificationUsers
                                  .filter(user => !selectedNotificationUsers.find(u => u._id === user._id))
                                  .map((user) => (
                                    <button
                                      key={user._id}
                                      type="button"
                                      onClick={() => handleNotificationUserSelect(user)}
                                      className="w-full px-4 py-3 text-left hover:bg-charcoal transition-colors border-b border-gray-dark last:border-b-0"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <p className="text-cream font-medium">{user.name || 'No Name'}</p>
                                          <p className="text-gray-light text-sm">{user.email}</p>
                                          {user.phone && (
                                            <p className="text-gray-light text-sm" dir="ltr">{user.phone}</p>
                                          )}
                                        </div>
                                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                      </div>
                                    </button>
                                  ))}
                              </div>
                            )}

                            {/* Selected Users */}
                            {selectedNotificationUsers.length > 0 && (
                              <div className="mt-3 space-y-2">
                                <p className="text-sm text-gray-light">{language === 'en' ? 'Selected Users:' : 'المستخدمون المحددون:'}</p>
                                <div className="flex flex-wrap gap-2">
                                  {selectedNotificationUsers.map((user) => (
                                    <div
                                      key={user._id}
                                      className="flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-lg px-3 py-2"
                                    >
                                      <span className="text-cream text-sm">{user.name || user.email}</span>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveNotificationUser(user._id)}
                                        className="text-red-400 hover:text-red-300 transition-colors"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Submit Buttons */}
                      <div className="flex justify-end gap-3 pt-4 border-t border-gray-dark">
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateNotificationModal(false);
                            setNotificationFormData({
                              type: 'info',
                              message: '',
                              isForAll: false,
                              recipients: []
                            });
                            setSelectedNotificationUsers([]);
                            setNotificationUserSearchQuery('');
                          }}
                          className="px-6 py-3 bg-charcoal border border-gray-dark text-gray-silver rounded-lg hover:bg-gray-dark hover:text-cream transition-colors"
                        >
                          {language === 'en' ? 'Cancel' : 'إلغاء'}
                        </button>
                        <button
                          type="submit"
                          disabled={submittingNotification || (!notificationFormData.isForAll && selectedNotificationUsers.length === 0)}
                          className="px-6 py-3 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {submittingNotification ? (
                            <>
                              <div className="w-5 h-5 border-2 border-blackC/20 border-t-blackC rounded-full animate-spin" />
                              {language === 'en' ? 'Sending...' : 'جاري الإرسال...'}
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                              </svg>
                              {language === 'en' ? 'Send Notification' : 'إرسال الإشعار'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* Settings Sidebar Navigation */}
              <div className="w-full lg:w-64 flex-shrink-0">
                <div className="lg:sticky lg:top-6">
                  <nav className="flex flex-row lg:flex-col gap-2 lg:gap-1 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
                    <button
                      onClick={() => setSelectedMethodId('booking')}
                      className={`flex-shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-start transition-all whitespace-nowrap ${selectedMethodId === 'booking' || selectedMethodId === ''
                        ? 'bg-primary/10 text-primary border border-primary/30'
                        : 'text-gray-light hover:bg-charcoal hover:text-cream border border-transparent'
                        }`}
                    >
                      <svg className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium text-sm lg:text-base">
                        {language === 'en' ? 'Booking Settings' : 'إعدادات الحجز'}
                      </span>
                    </button>

                    <button
                      onClick={() => setSelectedMethodId('contact')}
                      className={`flex-shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-start transition-all whitespace-nowrap ${selectedMethodId === 'contact'
                        ? 'bg-primary/10 text-primary border border-primary/30'
                        : 'text-gray-light hover:bg-charcoal hover:text-cream border border-transparent'
                        }`}
                    >
                      <svg className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium text-sm lg:text-base">
                        {language === 'en' ? 'Contact Info' : 'معلومات الاتصال'}
                      </span>
                    </button>

                    <button
                      onClick={() => setSelectedMethodId('payment')}
                      className={`flex-shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-start transition-all whitespace-nowrap ${selectedMethodId === 'payment'
                        ? 'bg-primary/10 text-primary border border-primary/30'
                        : 'text-gray-light hover:bg-charcoal hover:text-cream border border-transparent'
                        }`}
                    >
                      <svg className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span className="font-medium text-sm lg:text-base">
                        {language === 'en' ? 'Payment Methods' : 'طرق الدفع'}
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        setSelectedMethodId('images');
                        fetchCloudinaryImages();
                      }}
                      className={`flex-shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-start transition-all whitespace-nowrap ${selectedMethodId === 'images'
                        ? 'bg-primary/10 text-primary border border-primary/30'
                        : 'text-gray-light hover:bg-charcoal hover:text-cream border border-transparent'
                        }`}
                    >
                      <svg className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium text-sm lg:text-base">
                        {language === 'en' ? 'Cloudinary Images' : 'صور Cloudinary'}
                      </span>
                    </button>

                    <button
                      onClick={() => {
                        setSelectedMethodId('policy');
                        fetchPolicySections();
                      }}
                      className={`flex-shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-start transition-all whitespace-nowrap ${selectedMethodId === 'policy'
                        ? 'bg-primary/10 text-primary border border-primary/30'
                        : 'text-gray-light hover:bg-charcoal hover:text-cream border border-transparent'
                        }`}
                    >
                      <svg className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="font-medium text-sm lg:text-base">
                        {language === 'en' ? 'Policy Sections' : 'أقسام السياسة'}
                      </span>
                    </button>

                    <button
                      onClick={() => setSelectedMethodId('tracking')}
                      className={`flex-shrink-0 lg:w-full flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-start transition-all whitespace-nowrap ${selectedMethodId === 'tracking'
                        ? 'bg-primary/10 text-primary border border-primary/30'
                        : 'text-gray-light hover:bg-charcoal hover:text-cream border border-transparent'
                        }`}
                    >
                      <svg className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <span className="font-medium text-sm lg:text-base">
                        {language === 'en' ? 'Facebook Pixel' : 'تتبع فيسبوك'}
                      </span>
                    </button>
                  </nav>

                  {/* Quick Stats Card - Hidden on small screens */}
                  <div className="hidden lg:block mt-8 p-4 bg-charcoal border border-gray-dark rounded-xl">
                    <p className="text-gray-silver text-xs uppercase tracking-wider mb-3">
                      {language === 'en' ? 'Quick Info' : 'معلومات سريعة'}
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-light text-sm">
                          {language === 'en' ? 'Payment Methods' : 'طرق الدفع'}
                        </span>
                        <span className="text-cream font-medium">{paymentMethods.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-light text-sm">
                          {language === 'en' ? 'Active Methods' : 'الطرق النشطة'}
                        </span>
                        <span className="text-primary font-medium">
                          {paymentMethods.filter(m => m.isActive).length}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-light text-sm">
                          {language === 'en' ? 'Total Images' : 'إجمالي الصور'}
                        </span>
                        <span className="text-cream font-medium">{cloudinaryImages.length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Settings Content Area */}
              <div className="flex-1 min-w-0">
                {/* Booking Settings Section */}
                {(selectedMethodId === 'booking' || selectedMethodId === '') && (
                  <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                    {/* Section Header */}
                    <div className="px-6 py-5 border-b border-gray-dark bg-charcoal/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-display text-xl text-cream">
                            {language === 'en' ? 'Booking Settings' : 'إعدادات الحجز'}
                          </h3>
                          <p className="text-gray-silver text-sm mt-0.5">
                            {language === 'en' ? 'Configure pricing and booking rules' : 'تكوين قواعد التسعير والحجز'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Section Content */}
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          const formData = new FormData(e.target);
                          const roundTripMultiplier = parseFloat(formData.get('roundTripMultiplier'));
                          const res = await api.patch('/settings', { roundTripMultiplier });
                          setSettings(res.data);
                          await alert(
                            language === 'en' ? 'Settings updated successfully!' : 'تم تحديث الإعدادات بنجاح!',
                            language === 'en' ? 'Success' : 'نجح',
                            'success'
                          );
                        } catch (error) {
                          await alert(
                            error.response?.data?.error || (language === 'en' ? 'Error updating settings' : 'خطأ في تحديث الإعدادات'),
                            language === 'en' ? 'Error' : 'خطأ',
                            'error'
                          );
                        }
                      }}
                      className="p-6"
                    >
                      <div className="max-w-2xl">
                        {/* Setting Item */}
                        <div className="p-5 bg-charcoal/50 border border-gray-dark rounded-xl">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-lg bg-primary/5 border border-primary/20 flex items-center justify-center flex-shrink-0">
                              <svg className="w-6 h-6 text-primary/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <label className="block text-cream font-medium mb-1">
                                {language === 'en' ? 'Round Trip Multiplier' : 'مضاعف الرحلة ذهاباً وإياباً'}
                              </label>
                              <p className="text-gray-silver text-sm mb-4">
                                {language === 'en'
                                  ? 'Set the multiplier for round trip prices. For example, if set to 2, a round trip will cost 2x the one-way price.'
                                  : 'قم بتعيين المضاعف لأسعار الرحلات ذهاباً وإياباً. على سبيل المثال، إذا تم تعيينه إلى 2، فستكلف الرحلة ذهاباً وإياباً ضعف سعر الرحلة في اتجاه واحد.'}
                              </p>

                              <div className="flex items-center gap-4">
                                <div className="relative">
                                  <input
                                    type="number"
                                    name="roundTripMultiplier"
                                    min="0.1"
                                    max="10"
                                    step="0.1"
                                    defaultValue={settings?.roundTripMultiplier || 2}
                                    className="w-28 bg-dark border border-gray-dark text-cream text-center text-lg font-medium px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                                    required
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-silver text-sm">×</span>
                                </div>

                                <svg className="w-5 h-5 text-gray-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>

                                {/* Live Preview */}
                                <div className="flex-1 bg-dark border border-gray-dark rounded-lg p-4">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-gray-silver text-xs uppercase tracking-wider mb-1">
                                        {language === 'en' ? 'Example Preview' : 'معاينة المثال'}
                                      </p>
                                      <p className="text-gray-light text-sm">
                                        {language === 'en' ? 'One-way: 2,000 EGP' : 'اتجاه واحد: 2,000 جنيه'}
                                      </p>
                                    </div>
                                    <div className="text-end">
                                      <p className="text-gray-silver text-xs uppercase tracking-wider mb-1">
                                        {language === 'en' ? 'Round Trip' : 'ذهاب وعودة'}
                                      </p>
                                      <p className="text-primary font-display text-lg">
                                        {(2000 * (settings?.roundTripMultiplier || 2)).toLocaleString()} {language === 'en' ? 'EGP' : 'جنيه'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end mt-6 pt-6 border-t border-gray-dark">
                        <button
                          type="submit"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {language === 'en' ? 'Save Settings' : 'حفظ الإعدادات'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Contact Information Section */}
                {selectedMethodId === 'contact' && (
                  <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                    {/* Section Header */}
                    <div className="px-6 py-5 border-b border-gray-dark bg-charcoal/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-display text-xl text-cream">
                            {language === 'en' ? 'Contact Information' : 'معلومات الاتصال'}
                          </h3>
                          <p className="text-gray-silver text-sm mt-0.5">
                            {language === 'en' ? 'Manage your business contact details' : 'إدارة تفاصيل الاتصال بعملك'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Section Content */}
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          const formData = new FormData(e.target);

                          const contactInfo = {};

                          const addressEn = formData.get('address_en')?.trim();
                          const addressAr = formData.get('address_ar')?.trim();
                          if (addressEn || addressAr) {
                            contactInfo.address = {};
                            if (addressEn) contactInfo.address.en = addressEn;
                            if (addressAr) contactInfo.address.ar = addressAr;
                          }

                          const phone = formData.get('phone')?.trim();
                          if (phone) contactInfo.phone = phone;

                          const email = formData.get('email')?.trim();
                          if (email) contactInfo.email = email;

                          const liveChatHoursEn = formData.get('liveChatHours_en')?.trim();
                          const liveChatHoursAr = formData.get('liveChatHours_ar')?.trim();
                          if (liveChatHoursEn || liveChatHoursAr) {
                            contactInfo.liveChatHours = {};
                            if (liveChatHoursEn) contactInfo.liveChatHours.en = liveChatHoursEn;
                            if (liveChatHoursAr) contactInfo.liveChatHours.ar = liveChatHoursAr;
                          }

                          const googleMapsUrl = formData.get('googleMapsUrl')?.trim();
                          if (googleMapsUrl) contactInfo.googleMapsUrl = googleMapsUrl;

                          // Build socialMedia object
                          const socialMedia = {};
                          const socialFields = ['facebook', 'instagram', 'twitter', 'linkedin', 'youtube', 'whatsapp', 'tiktok'];
                          socialFields.forEach(field => {
                            const value = formData.get(field)?.trim();
                            if (value) socialMedia[field] = value;
                          });

                          const res = await api.patch('/settings', { contactInfo, socialMedia });
                          setSettings(res.data);
                          await alert(
                            language === 'en' ? 'Contact information updated successfully!' : 'تم تحديث معلومات الاتصال بنجاح!',
                            language === 'en' ? 'Success' : 'نجح',
                            'success'
                          );
                        } catch (error) {
                          if (error.response?.data?.errors) {
                            const errorMessages = error.response.data.errors.map(err =>
                              `${err.path}: ${err.msg}`
                            ).join('\n');
                            await alert(
                              errorMessages,
                              language === 'en' ? 'Validation Error' : 'خطأ في التحقق',
                              'error'
                            );
                          } else {
                            await alert(
                              error.response?.data?.error || (language === 'en' ? 'Error updating contact information' : 'خطأ في تحديث معلومات الاتصال'),
                              language === 'en' ? 'Error' : 'خطأ',
                              'error'
                            );
                          }
                        }
                      }}
                      className="p-6 space-y-6"
                    >
                      {/* Address Group */}
                      <div className="p-5 bg-charcoal/50 border border-gray-dark rounded-xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                          <h4 className="text-cream font-medium">
                            {language === 'en' ? 'Business Address' : 'عنوان العمل'}
                          </h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-light mb-2">
                              {language === 'en' ? 'English' : 'إنجليزي'}
                            </label>
                            <input
                              type="text"
                              name="address_en"
                              defaultValue={settings?.contactInfo?.address?.en || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="New Cairo Business District, Cairo, Egypt"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-light mb-2">
                              {language === 'en' ? 'Arabic' : 'عربي'}
                            </label>
                            <input
                              type="text"
                              name="address_ar"
                              defaultValue={settings?.contactInfo?.address?.ar || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="حي القاهرة الجديدة للأعمال، القاهرة، مصر"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Contact Details Group */}
                      <div className="p-5 bg-charcoal/50 border border-gray-dark rounded-xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                          </div>
                          <h4 className="text-cream font-medium">
                            {language === 'en' ? 'Contact Details' : 'تفاصيل الاتصال'}
                          </h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-light mb-2">
                              {language === 'en' ? 'Phone Number' : 'رقم الهاتف'}
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              defaultValue={settings?.contactInfo?.phone || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="+20 100 000 0000"
                              dir="ltr"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-light mb-2">
                              {language === 'en' ? 'Email Address' : 'البريد الإلكتروني'}
                            </label>
                            <input
                              type="email"
                              name="email"
                              defaultValue={settings?.contactInfo?.email || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="info@limousine.com"
                              dir="ltr"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Live Chat Hours Group */}
                      <div className="p-5 bg-charcoal/50 border border-gray-dark rounded-xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </div>
                          <h4 className="text-cream font-medium">
                            {language === 'en' ? 'Live Chat Availability' : 'توفر الدردشة المباشرة'}
                          </h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-light mb-2">
                              {language === 'en' ? 'Hours (English)' : 'الساعات (إنجليزي)'}
                            </label>
                            <input
                              type="text"
                              name="liveChatHours_en"
                              defaultValue={settings?.contactInfo?.liveChatHours?.en || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="Available 9am - 11pm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-light mb-2">
                              {language === 'en' ? 'Hours (Arabic)' : 'الساعات (عربي)'}
                            </label>
                            <input
                              type="text"
                              name="liveChatHours_ar"
                              defaultValue={settings?.contactInfo?.liveChatHours?.ar || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="متاح من 9 ص - 11 م"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Google Maps Group */}
                      <div className="p-5 bg-charcoal/50 border border-gray-dark rounded-xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                          </div>
                          <h4 className="text-cream font-medium">
                            {language === 'en' ? 'Location Link' : 'رابط الموقع'}
                          </h4>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-light mb-2">
                            {language === 'en' ? 'Google Maps URL' : 'رابط خرائط جوجل'}
                          </label>
                          <input
                            type="url"
                            name="googleMapsUrl"
                            defaultValue={settings?.contactInfo?.googleMapsUrl || ''}
                            className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                            placeholder="https://maps.google.com/..."
                            dir="ltr"
                          />
                        </div>
                      </div>

                      {/* Social Media Group */}
                      <div className="p-5 bg-charcoal/50 border border-gray-dark rounded-xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                          </div>
                          <h4 className="text-cream font-medium">
                            {language === 'en' ? 'Social Media Links' : 'روابط وسائل التواصل الاجتماعي'}
                          </h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="flex items-center gap-2 text-sm text-gray-light mb-2">
                              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
                              </svg>
                              Facebook
                            </label>
                            <input
                              type="url"
                              name="facebook"
                              defaultValue={settings?.socialMedia?.facebook || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="https://facebook.com/..."
                              dir="ltr"
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-2 text-sm text-gray-light mb-2">
                              <svg className="w-4 h-4 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                              </svg>
                              Instagram
                            </label>
                            <input
                              type="url"
                              name="instagram"
                              defaultValue={settings?.socialMedia?.instagram || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="https://instagram.com/..."
                              dir="ltr"
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-2 text-sm text-gray-light mb-2">
                              <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                              </svg>
                              Twitter
                            </label>
                            <input
                              type="url"
                              name="twitter"
                              defaultValue={settings?.socialMedia?.twitter || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="https://twitter.com/..."
                              dir="ltr"
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-2 text-sm text-gray-light mb-2">
                              <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                              </svg>
                              LinkedIn
                            </label>
                            <input
                              type="url"
                              name="linkedin"
                              defaultValue={settings?.socialMedia?.linkedin || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="https://linkedin.com/..."
                              dir="ltr"
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-2 text-sm text-gray-light mb-2">
                              <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                              </svg>
                              YouTube
                            </label>
                            <input
                              type="url"
                              name="youtube"
                              defaultValue={settings?.socialMedia?.youtube || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="https://youtube.com/..."
                              dir="ltr"
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-2 text-sm text-gray-light mb-2">
                              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                              </svg>
                              WhatsApp
                            </label>
                            <input
                              type="text"
                              name="whatsapp"
                              defaultValue={settings?.socialMedia?.whatsapp || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="https://wa.me/... or +201234567890"
                              dir="ltr"
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-2 text-sm text-gray-light mb-2">
                              <svg className="w-4 h-4 text-black dark:text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.08 4.58.08.18.21.26.39.27.28.01.54.05.81.08 1.4.15 2.79.32 4.19.51.19.02.3.1.37.29.06.19.05.39-.01.58-.19.61-.39 1.22-.61 1.82-.18.47-.38.93-.59 1.38-.03.08-.08.15-.13.22-.05.07-.12.13-.2.18-.16.1-.34.15-.52.17-.18.02-.36.02-.54.01-.36-.01-.72-.05-1.08-.1-.18-.02-.35-.05-.53-.08-.18-.03-.35-.08-.52-.13-.17-.05-.33-.12-.48-.2-.15-.08-.29-.17-.42-.27-.13-.1-.25-.21-.36-.33-.11-.12-.21-.25-.3-.38-.09-.13-.17-.27-.24-.41-.07-.14-.13-.29-.18-.44-.05-.15-.09-.3-.12-.46-.03-.16-.05-.32-.06-.48-.01-.16-.01-.32 0-.48.01-.16.03-.32.06-.48.03-.16.07-.31.12-.46.05-.15.11-.3.18-.44.07-.14.15-.28.24-.41.09-.13.19-.26.3-.38.11-.12.23-.23.36-.33.13-.1.27-.19.42-.27.15-.08.31-.15.48-.2.17-.05.34-.1.52-.13.18-.03.35-.06.53-.08.36-.05.72-.09 1.08-.1.18-.01.36-.01.54.01.18.02.36.07.52.17.08.05.15.11.2.18.05.07.1.14.13.22.21.45.41.91.59 1.38.22.6.42 1.21.61 1.82.06.19.07.39.01.58-.07.19-.18.27-.37.29-1.4.19-2.79.36-4.19.51-.27.03-.53.07-.81.08-.18.01-.31.09-.39.27-.45 1.49-.99 3.05-1.08 4.58-.02.01-.02.02-.02.03-.01 1.31-.01 2.61-.02 3.91-.01.01-.02.01-.03.02-1.31.01-2.61.02-3.91.02-.01-.01-.02-.02-.03-.02-1.31-.01-2.61-.01-3.91-.02-.01-.01-.02-.02-.03-.02-1.31-.01-2.61-.02-3.91-.02.01.01.02.02.03.02z" />
                              </svg>
                              TikTok
                            </label>
                            <input
                              type="url"
                              name="tiktok"
                              defaultValue={settings?.socialMedia?.tiktok || ''}
                              className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder="https://tiktok.com/..."
                              dir="ltr"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end pt-6 border-t border-gray-dark">
                        <button
                          type="submit"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {language === 'en' ? 'Save Contact Info' : 'حفظ معلومات الاتصال'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Cloudinary Images Section */}
                {selectedMethodId === 'images' && (
                  <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                    {/* Section Header */}
                    <div className="px-6 py-5 border-b border-gray-dark bg-charcoal/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-display text-xl text-cream">
                              {language === 'en' ? 'Cloudinary Images' : 'صور Cloudinary'}
                            </h3>
                            <p className="text-gray-silver text-sm mt-0.5">
                              {language === 'en' ? 'Manage all images stored in Cloudinary' : 'إدارة جميع الصور المخزنة في Cloudinary'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={fetchCloudinaryImages}
                          disabled={loadingImages}
                          className="inline-flex items-center gap-2 bg-charcoal border border-gray-dark text-cream font-medium px-4 py-2 rounded-lg hover:bg-gray-dark transition-colors disabled:opacity-50"
                        >
                          <svg className={`w-5 h-5 ${loadingImages ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          {language === 'en' ? 'Refresh' : 'تحديث'}
                        </button>
                      </div>
                    </div>

                    {/* Images List */}
                    <div className="p-6">
                      {loadingImages ? (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                          <p className="text-gray-silver">{language === 'en' ? 'Loading images...' : 'جاري تحميل الصور...'}</p>
                        </div>
                      ) : cloudinaryImages.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 rounded-full bg-charcoal border border-gray-dark mx-auto mb-4 flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="text-gray-light mb-2">
                            {language === 'en' ? 'No images found' : 'لا توجد صور'}
                          </p>
                          <p className="text-gray-silver text-sm">
                            {language === 'en' ? 'Images uploaded to Cloudinary will appear here' : 'ستظهر الصور المرفوعة إلى Cloudinary هنا'}
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {cloudinaryImages.map((image, index) => (
                            <div
                              key={`${image.publicId}-${image.entityId}-${image.imageIndex || index}`}
                              className="group relative bg-charcoal/50 border border-gray-dark rounded-xl overflow-hidden hover:border-primary/30 transition-all"
                            >
                              {/* Image Preview */}
                              <div className="relative h-48 bg-dark overflow-hidden">
                                <img
                                  src={image.url}
                                  alt={image.description || image.type}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23333" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                                {/* Delete Button Overlay */}
                                <div className="absolute inset-0 bg-blackC/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <button
                                    onClick={() => deleteCloudinaryImage(image)}
                                    disabled={deletingImageId === image.publicId}
                                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                  >
                                    {deletingImageId === image.publicId ? (
                                      <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {language === 'en' ? 'Deleting...' : 'جاري الحذف...'}
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        {language === 'en' ? 'Delete' : 'حذف'}
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>

                              {/* Image Info */}
                              <div className="p-4">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`px-2 py-0.5 text-xs rounded-md font-medium ${image.type === 'Car Image' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                                        image.type === 'Driver License' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                                          image.type === 'Booking Payment Proof' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                                            image.type === 'Destination Image' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' :
                                              'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                                        }`}>
                                        {image.type === 'Car Image' ? t.imageTypeCar :
                                          image.type === 'Driver License' ? t.imageTypeDriverLicense :
                                            image.type === 'Booking Payment Proof' ? t.imageTypeBookingPaymentProof :
                                              image.type === 'Payment Proof' ? t.imageTypePaymentProof :
                                                image.type === 'Destination Image' ? t.imageTypeDestination :
                                                  image.type}
                                      </span>
                                    </div>
                                    <p className="text-cream font-medium text-sm truncate" title={image.location}>
                                      {image.location}
                                    </p>
                                  </div>
                                </div>

                                <p className="text-gray-silver text-xs mb-3 line-clamp-2">
                                  {image.description}
                                </p>

                                {/* Metadata */}
                                <div className="space-y-1 text-xs">
                                  <div className="flex items-center justify-between text-gray-silver">
                                    <span>{language === 'en' ? 'Public ID' : 'معرف الصورة'}:</span>
                                    <span className="font-mono text-gray-light truncate ml-2" dir="ltr" title={image.publicId}>
                                      {image.publicId.split('/').pop()}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between text-gray-silver">
                                    <span>{language === 'en' ? 'Uploaded' : 'تاريخ الرفع'}:</span>
                                    <span className="text-gray-light">
                                      {new Date(image.createdAt).toLocaleDateString(language === 'en' ? 'en-US' : 'ar-EG', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Policy Sections */}
                {/* Policy Sections */}
                {selectedMethodId === 'policy' && (
                  <div className="space-y-6">
                    {/* Main Card */}
                    <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                      {/* Section Header */}
                      <div className="px-6 py-5 border-b border-gray-dark bg-charcoal/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="font-display text-xl text-cream">
                                {language === 'en' ? 'Policy Sections' : 'أقسام السياسة'}
                              </h3>
                              <p className="text-gray-silver text-sm mt-0.5">
                                {language === 'en' ? 'Manage service request policy sections' : 'إدارة أقسام سياسة طلب الخدمة'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* View Policy Page Link */}
                            <a
                              href="/policy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2.5 bg-charcoal border border-gray-dark text-gray-light rounded-lg hover:border-primary/30 hover:text-cream transition-all text-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              {language === 'en' ? 'View Page' : 'عرض الصفحة'}
                            </a>
                            <button
                              onClick={() => {
                                setEditingPolicySection({ isNew: true, subsections: [{ subtitle: '', description: '', order: 0 }] });
                              }}
                              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-5 py-2.5 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              {language === 'en' ? 'Add Section' : 'إضافة قسم'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Quick Stats Bar */}
                      {!loadingPolicySections && policySections.length > 0 && (
                        <div className="px-6 py-4 bg-charcoal/20 border-b border-gray-dark flex items-center gap-6">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-primary" />
                            <span className="text-gray-silver text-sm">
                              {policySections.length} {language === 'en' ? 'sections' : 'أقسام'}
                            </span>
                          </div>
                          <div className="w-px h-4 bg-gray-dark" />
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-500" />
                            <span className="text-gray-silver text-sm">
                              {policySections.filter(s => s.isActive).length} {language === 'en' ? 'active' : 'نشط'}
                            </span>
                          </div>
                          <div className="w-px h-4 bg-gray-dark" />
                          <div className="flex items-center gap-2">
                            <span className="text-gray-silver text-sm">
                              {policySections.reduce((acc, s) => acc + (s.subsections?.length || 0), 0)} {language === 'en' ? 'total clauses' : 'إجمالي البنود'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Policy Sections List */}
                      <div className="p-6">
                        {loadingPolicySections ? (
                          <div className="text-center py-16">
                            <div className="relative w-16 h-16 mx-auto mb-4">
                              <div className="absolute inset-0 border-2 border-primary/20 rounded-full" />
                              <div className="absolute inset-0 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                            <p className="text-gray-silver">{language === 'en' ? 'Loading policy sections...' : 'جاري تحميل أقسام السياسة...'}</p>
                          </div>
                        ) : policySections.length === 0 ? (
                          <div className="text-center py-16">
                            <div className="w-20 h-20 rounded-2xl bg-charcoal border border-gray-dark mx-auto mb-5 flex items-center justify-center">
                              <svg className="w-10 h-10 text-gray-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <p className="text-cream text-lg font-medium mb-2">
                              {language === 'en' ? 'No policy sections found' : 'لا توجد أقسام سياسة'}
                            </p>
                            <p className="text-gray-silver text-sm mb-6">
                              {language === 'en' ? 'Add your first policy section to get started' : 'أضف قسم السياسة الأول للبدء'}
                            </p>
                            <button
                              onClick={() => {
                                setEditingPolicySection({ isNew: true, subsections: [{ subtitle: '', description: '', order: 0 }] });
                              }}
                              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary/10 text-primary border border-primary/30 rounded-lg hover:bg-primary/20 transition-all"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              {language === 'en' ? 'Create First Section' : 'إنشاء القسم الأول'}
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {policySections
                              .sort((a, b) => (a.order || 0) - (b.order || 0))
                              .map((section, index) => (
                                <div
                                  key={section._id}
                                  className={`group relative border rounded-xl transition-all overflow-hidden ${section.isActive
                                    ? 'bg-charcoal/30 border-gray-dark hover:border-primary/30'
                                    : 'bg-blackC/30 border-gray-dark/50'
                                    }`}
                                >
                                  {/* Main Section Content */}
                                  <div className="p-5">
                                    <div className="flex items-start gap-4">
                                      {/* Order Number & Drag Handle Area */}
                                      <div className="flex flex-col items-center gap-2">
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                                          <span className="text-primary font-display text-lg">{index + 1}</span>
                                        </div>
                                        {/* Visual drag indicator */}
                                        <div className="flex flex-col gap-0.5 opacity-30 group-hover:opacity-60 transition-opacity cursor-grab">
                                          <div className="flex gap-0.5">
                                            <div className="w-1 h-1 rounded-full bg-gray-silver" />
                                            <div className="w-1 h-1 rounded-full bg-gray-silver" />
                                          </div>
                                          <div className="flex gap-0.5">
                                            <div className="w-1 h-1 rounded-full bg-gray-silver" />
                                            <div className="w-1 h-1 rounded-full bg-gray-silver" />
                                          </div>
                                          <div className="flex gap-0.5">
                                            <div className="w-1 h-1 rounded-full bg-gray-silver" />
                                            <div className="w-1 h-1 rounded-full bg-gray-silver" />
                                          </div>
                                        </div>
                                      </div>

                                      {/* Section Info */}
                                      <div className="flex-1 min-w-0">
                                        {/* Header Row */}
                                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                                          <h4 className="text-cream font-display text-lg">{section.title}</h4>
                                          <span className="px-2.5 py-1 text-xs rounded-md bg-primary/10 text-primary border border-primary/20 font-medium">
                                            {section.category}
                                          </span>
                                          {section.isActive ? (
                                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-md bg-green-500/10 text-green-400 border border-green-500/20">
                                              <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                                              {language === 'en' ? 'Active' : 'نشط'}
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-md bg-gray-dark text-gray-silver border border-gray-dark">
                                              <div className="w-1.5 h-1.5 rounded-full bg-gray-silver" />
                                              {language === 'en' ? 'Inactive' : 'غير نشط'}
                                            </span>
                                          )}
                                        </div>

                                        {/* Subsections Preview */}
                                        {section.subsections && section.subsections.length > 0 && (
                                          <div className="space-y-2">
                                            {section.subsections
                                              .sort((a, b) => (a.order || 0) - (b.order || 0))
                                              .slice(0, 3) // Show max 3 previews
                                              .map((subsection, idx) => (
                                                <div
                                                  key={idx}
                                                  className="flex items-start gap-3 pl-4 border-l-2 border-primary/20"
                                                >
                                                  <div className="flex-1 min-w-0">
                                                    <p className="text-cream text-sm font-medium truncate">{subsection.subtitle}</p>
                                                    <p className="text-gray-light text-sm line-clamp-1">{subsection.description}</p>
                                                  </div>
                                                </div>
                                              ))}
                                            {section.subsections.length > 3 && (
                                              <p className="text-gray-silver text-xs pl-4">
                                                +{section.subsections.length - 3} {language === 'en' ? 'more clauses' : 'بنود أخرى'}
                                              </p>
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      {/* Actions */}
                                      <div className={`flex items-center gap-2 transition-opacity ${section.isActive ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
                                        }`}>
                                        <button
                                          onClick={() => setEditingPolicySection(section)}
                                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-all text-sm font-medium"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                          {language === 'en' ? 'Edit' : 'تعديل'}
                                        </button>
                                        <button
                                          onClick={() => deletePolicySection(section._id)}
                                          className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-all text-sm font-medium"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                          {language === 'en' ? 'Delete' : 'حذف'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Bottom Stats Bar */}
                                  <div className="px-5 py-3 bg-blackC/30 border-t border-gray-dark/50 flex items-center gap-4 text-xs">
                                    <div className="flex items-center gap-2 text-gray-silver">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                                      </svg>
                                      {section.subsections?.length || 0} {language === 'en' ? 'clauses' : 'بنود'}
                                    </div>
                                    <div className="w-px h-3 bg-gray-dark" />
                                    <div className="flex items-center gap-2 text-gray-silver">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                      </svg>
                                      {language === 'en' ? 'Order:' : 'الترتيب:'} {(section.order + 1) || 0}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Help Card */}
                    <div className="bg-charcoal/30 border border-gray-dark rounded-xl p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-cream font-medium mb-1">
                            {language === 'en' ? 'Organizing Policy Sections' : 'تنظيم أقسام السياسة'}
                          </h4>
                          <p className="text-gray-silver text-sm leading-relaxed">
                            {language === 'en'
                              ? 'Group related policies under categories like "Terms", "Conditions", or "Cancellation Policy". Each section can have multiple clauses with subtitles and descriptions. Use the order field to control how sections appear on the public policy page.'
                              : 'قم بتجميع السياسات المتعلقة تحت فئات مثل "الشروط" أو "الأحكام" أو "سياسة الإلغاء". يمكن أن يحتوي كل قسم على عدة بنود مع عناوين فرعية وأوصاف. استخدم حقل الترتيب للتحكم في كيفية ظهور الأقسام على صفحة السياسة العامة.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Facebook Pixel Tracking Section */}
                {selectedMethodId === 'tracking' && (
                  <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                    {/* Section Header */}
                    <div className="px-6 py-5 border-b border-gray-dark bg-charcoal/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-600/10 border border-blue-600/30 flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-display text-xl text-cream">
                            {language === 'en' ? 'Facebook Pixel Tracking' : 'تتبع فيسبوك بيكسل'}
                          </h3>
                          <p className="text-gray-silver text-sm mt-0.5">
                            {language === 'en' ? 'Configure Facebook Pixel for website analytics and ad tracking' : 'تكوين فيسبوك بيكسل لتحليلات الموقع وتتبع الإعلانات'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Section Content */}
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          const formData = new FormData(e.target);
                          const facebookPixelId = formData.get('facebookPixelId')?.trim() || '';
                          const res = await api.patch('/settings', { facebookPixelId });
                          setSettings(res.data);
                          await alert(
                            language === 'en' ? 'Facebook Pixel settings updated successfully!' : 'تم تحديث إعدادات فيسبوك بيكسل بنجاح!',
                            language === 'en' ? 'Success' : 'نجح',
                            'success'
                          );
                        } catch (error) {
                          await alert(
                            error.response?.data?.error || (language === 'en' ? 'Error updating Facebook Pixel settings' : 'خطأ في تحديث إعدادات فيسبوك بيكسل'),
                            language === 'en' ? 'Error' : 'خطأ',
                            'error'
                          );
                        }
                      }}
                      className="p-6 space-y-6"
                    >
                      {/* Pixel ID Input */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-light mb-2">
                            {language === 'en' ? 'Facebook Pixel ID' : 'معرف فيسبوك بيكسل'}
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              name="facebookPixelId"
                              defaultValue={settings?.facebookPixelId || ''}
                              placeholder={language === 'en' ? 'Enter your Facebook Pixel ID (e.g., 1234567890123456)' : 'أدخل معرف فيسبوك بيكسل (مثال: 1234567890123456)'}
                              className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors font-mono"
                              pattern="^\d{10,20}$"
                              title={language === 'en' ? 'Pixel ID should be 10-20 digits' : 'معرف البيكسل يجب أن يكون 10-20 رقم'}
                              dir="ltr"
                            />
                          </div>
                          <p className="text-gray-silver text-xs mt-2">
                            {language === 'en'
                              ? 'Your Facebook Pixel ID is a 15-16 digit number found in your Facebook Events Manager.'
                              : 'معرف فيسبوك بيكسل هو رقم مكون من 15-16 رقم موجود في مدير أحداث فيسبوك.'}
                          </p>
                        </div>

                        {/* Status Indicator */}
                        <div className={`p-4 rounded-lg border ${settings?.facebookPixelId
                          ? 'bg-green-500/5 border-green-500/20'
                          : 'bg-yellow-500/5 border-yellow-500/20'}`}>
                          <div className="flex items-center gap-3">
                            {settings?.facebookPixelId ? (
                              <>
                                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                                <div>
                                  <p className="text-green-400 font-medium text-sm">
                                    {language === 'en' ? 'Pixel Active' : 'البيكسل نشط'}
                                  </p>
                                  <p className="text-gray-silver text-xs">
                                    {language === 'en'
                                      ? `Tracking enabled with ID: ${settings.facebookPixelId}`
                                      : `التتبع مفعل بالمعرف: ${settings.facebookPixelId}`}
                                  </p>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="w-8 h-8 rounded-full bg-yellow-500/10 flex items-center justify-center">
                                  <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                </div>
                                <div>
                                  <p className="text-yellow-400 font-medium text-sm">
                                    {language === 'en' ? 'Pixel Not Configured' : 'البيكسل غير مكون'}
                                  </p>
                                  <p className="text-gray-silver text-xs">
                                    {language === 'en'
                                      ? 'Enter your Facebook Pixel ID to enable tracking'
                                      : 'أدخل معرف فيسبوك بيكسل لتفعيل التتبع'}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Info Card */}
                      <div className="bg-charcoal/50 border border-gray-dark rounded-lg p-5">
                        <h4 className="text-cream font-medium mb-3 flex items-center gap-2">
                          <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {language === 'en' ? 'How to find your Pixel ID' : 'كيفية إيجاد معرف البيكسل'}
                        </h4>
                        <ol className="text-gray-silver text-sm space-y-2 list-decimal list-inside">
                          <li>{language === 'en' ? 'Go to Facebook Events Manager' : 'اذهب إلى مدير أحداث فيسبوك'}</li>
                          <li>{language === 'en' ? 'Select your pixel from the data sources' : 'اختر البيكسل من مصادر البيانات'}</li>
                          <li>{language === 'en' ? 'Copy the Pixel ID (15-16 digit number)' : 'انسخ معرف البيكسل (رقم من 15-16 خانة)'}</li>
                          <li>{language === 'en' ? 'Paste it in the field above and save' : 'الصقه في الحقل أعلاه واحفظ'}</li>
                        </ol>
                      </div>

                      {/* Events Tracked Info */}
                      <div className="bg-charcoal/50 border border-gray-dark rounded-lg p-5">
                        <h4 className="text-cream font-medium mb-3 flex items-center gap-2">
                          <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          {language === 'en' ? 'Events Automatically Tracked' : 'الأحداث المتتبعة تلقائياً'}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="flex items-center gap-2 text-gray-silver text-sm">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span><strong className="text-cream">PageView</strong> - {language === 'en' ? 'On every page visit' : 'عند زيارة كل صفحة'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-silver text-sm">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span><strong className="text-cream">Lead</strong> - {language === 'en' ? 'Form submissions' : 'إرسال النماذج'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-silver text-sm">
                            <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                            <span><strong className="text-cream">Contact</strong> - {language === 'en' ? 'Contact form' : 'نموذج الاتصال'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-silver text-sm">
                            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                            <span><strong className="text-cream">InitiateCheckout</strong> - {language === 'en' ? 'Booking start' : 'بدء الحجز'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Clear Pixel Button & Save */}
                      <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6 border-t border-gray-dark">
                        {settings?.facebookPixelId && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const res = await api.patch('/settings', { facebookPixelId: '' });
                                setSettings(res.data);
                                // Clear the input field
                                const input = document.querySelector('input[name="facebookPixelId"]');
                                if (input) input.value = '';
                                await alert(
                                  language === 'en' ? 'Facebook Pixel has been disabled' : 'تم تعطيل فيسبوك بيكسل',
                                  language === 'en' ? 'Success' : 'نجح',
                                  'success'
                                );
                              } catch (error) {
                                await alert(
                                  language === 'en' ? 'Error disabling pixel' : 'خطأ في تعطيل البيكسل',
                                  language === 'en' ? 'Error' : 'خطأ',
                                  'error'
                                );
                              }
                            }}
                            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                            {language === 'en' ? 'Disable Pixel' : 'تعطيل البيكسل'}
                          </button>
                        )}
                        <button
                          type="submit"
                          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all ml-auto"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {language === 'en' ? 'Save Pixel Settings' : 'حفظ إعدادات البيكسل'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Payment Methods Section */}
                {selectedMethodId === 'payment' && (
                  <div className="bg-dark border border-gray-dark rounded-xl overflow-hidden">
                    {/* Section Header */}
                    <div className="px-6 py-5 border-b border-gray-dark bg-charcoal/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-display text-xl text-cream">
                              {language === 'en' ? 'Payment Methods' : 'طرق الدفع'}
                            </h3>
                            <p className="text-gray-silver text-sm mt-0.5">
                              {language === 'en' ? 'Configure available payment options' : 'تكوين خيارات الدفع المتاحة'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setEditingPaymentMethod({ isNew: true });
                            setSelectedMethodId('payment');
                          }}
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold px-5 py-2.5 rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          {language === 'en' ? 'Add Method' : 'إضافة طريقة'}
                        </button>
                      </div>
                    </div>

                    {/* Payment Methods List */}
                    <div className="p-6">
                      {paymentMethods.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 rounded-full bg-charcoal border border-gray-dark mx-auto mb-4 flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                          </div>
                          <p className="text-gray-light mb-2">
                            {language === 'en' ? 'No payment methods found' : 'لا توجد طرق دفع'}
                          </p>
                          <p className="text-gray-silver text-sm">
                            {language === 'en' ? 'Add your first payment method to get started' : 'أضف طريقة الدفع الأولى للبدء'}
                          </p>
                        </div>
                      ) : (
                        <div className="grid gap-4">
                          {paymentMethods.map((method, index) => (
                            <div
                              key={method._id}
                              className={`group relative p-5 border rounded-xl transition-all ${method.isActive
                                ? 'bg-charcoal/50 border-gray-dark hover:border-primary/30'
                                : 'bg-blackC/30 border-gray-dark/50 opacity-70'
                                }`}
                            >
                              {/* Status Indicator */}
                              <div className={`absolute top-5 ${language === 'ar' ? 'left-5' : 'right-5'}`}>
                                <div className={`w-2.5 h-2.5 rounded-full ${method.isActive ? 'bg-green-500' : 'bg-gray-silver'}`} />
                              </div>

                              <div className="flex items-start gap-4">
                                {/* Method Number */}
                                <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                                  <span className="text-primary font-display text-lg">{index + 1}</span>
                                </div>

                                <div className="flex-1 min-w-0">
                                  {/* Header */}
                                  <div className="flex items-center gap-3 mb-2">
                                    <h4 className="text-cream font-medium truncate">
                                      {language === 'en' ? method.title.en : method.title.ar}
                                    </h4>
                                    <span className="px-2 py-0.5 text-xs rounded-md bg-primary/10 text-primary border border-primary/20 font-mono">
                                      {method.id}
                                    </span>
                                    {!method.isActive && (
                                      <span className="px-2 py-0.5 text-xs rounded-md bg-gray-dark text-gray-silver border border-gray-dark">
                                        {language === 'en' ? 'Inactive' : 'غير نشط'}
                                      </span>
                                    )}
                                  </div>

                                  {/* Description */}
                                  <p className="text-gray-light text-sm mb-3">
                                    {language === 'en' ? method.description.en : method.description.ar}
                                  </p>

                                  {/* Details */}
                                  <div className="p-3 bg-dark/50 border border-gray-dark/50 rounded-lg">
                                    <pre className="text-primary/80 text-sm whitespace-pre-wrap font-body" dir="ltr">
                                      {language === 'en' ? method.details.en : method.details.ar}
                                    </pre>
                                  </div>
                                </div>

                                {/* Actions */}
                                <button
                                  onClick={() => {
                                    setEditingPaymentMethod(method);
                                    setSelectedMethodId('payment');
                                  }}
                                  className="opacity-0 group-hover:opacity-100 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-all text-sm font-medium"
                                >
                                  {language === 'en' ? 'Edit' : 'تعديل'}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add/Edit Payment Method Modal */}
          {editingPaymentMethod && (
            <div className="fixed inset-0 bg-blackC/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-dark border border-gray-dark rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8">
                {/* Header */}
                <div className="p-6 border-b border-gray-dark flex justify-between items-center sticky top-0 bg-dark z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="font-display text-2xl text-cream">
                        {editingPaymentMethod.isNew
                          ? (language === 'en' ? 'Add Payment Method' : 'إضافة طريقة دفع')
                          : (language === 'en' ? 'Edit Payment Method' : 'تعديل طريقة الدفع')
                        }
                      </h2>
                      <p className="text-gray-silver text-sm">
                        {language === 'en' ? 'Configure payment method details' : 'تكوين تفاصيل طريقة الدفع'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditingPaymentMethod(null)}
                    className="w-10 h-10 rounded-lg bg-charcoal flex items-center justify-center text-gray-light hover:text-cream hover:bg-gray-dark transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const formData = new FormData(e.target);
                      const data = {
                        title: {
                          en: formData.get('title_en'),
                          ar: formData.get('title_ar')
                        },
                        description: {
                          en: formData.get('description_en'),
                          ar: formData.get('description_ar')
                        },
                        details: {
                          en: formData.get('details_en'),
                          ar: formData.get('details_ar')
                        },
                        isActive: formData.get('isActive') === 'on',
                        order: parseInt(formData.get('order')) || 0
                      };

                      if (editingPaymentMethod.isNew) {
                        data.id = selectedMethodId || formData.get('method_id');
                        if (!data.id) {
                          await alert(
                            language === 'en' ? 'Please select a payment method type' : 'يرجى اختيار نوع طريقة الدفع',
                            language === 'en' ? 'Required' : 'مطلوب',
                            'warning'
                          );
                          return;
                        }
                        const res = await api.post('/payment-methods', data);
                        setPaymentMethods(prev => [...prev, res.data]);
                        await alert(
                          language === 'en' ? 'Payment method created successfully!' : 'تم إنشاء طريقة الدفع بنجاح!',
                          language === 'en' ? 'Success' : 'نجاح',
                          'success'
                        );
                      } else {
                        const res = await api.patch(`/payment-methods/${editingPaymentMethod.id}`, data);
                        setPaymentMethods(prev => prev.map(m => m._id === res.data._id ? res.data : m));
                        await alert(
                          language === 'en' ? 'Payment method updated successfully!' : 'تم تحديث طريقة الدفع بنجاح!',
                          language === 'en' ? 'Success' : 'نجاح',
                          'success'
                        );
                      }
                      setEditingPaymentMethod(null);
                      setSelectedMethodId('');
                    } catch (error) {
                      await alert(
                        error.response?.data?.error || (language === 'en'
                          ? (editingPaymentMethod.isNew ? 'Error creating payment method' : 'Error updating payment method')
                          : (editingPaymentMethod.isNew ? 'خطأ في إنشاء طريقة الدفع' : 'خطأ في تحديث طريقة الدفع')
                        ),
                        language === 'en' ? 'Error' : 'خطأ',
                        'error'
                      );
                    }
                  }}
                  className="p-6"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Main Info */}
                    <div className="lg:col-span-2 space-y-6">
                      {/* Payment Method Type (only for new) */}
                      {editingPaymentMethod.isNew && (
                        <div className="bg-charcoal border border-gray-dark rounded-lg p-4">
                          <label className="flex items-center gap-2 text-sm font-semibold text-cream mb-3">
                            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            {language === 'en' ? 'Payment Method Type' : 'نوع طريقة الدفع'}
                          </label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                            {['bankTransfer', 'vodafone', 'orange', 'etisalat', 'instapay', 'cash']
                              .filter(id => !paymentMethods.find(m => m.id === id))
                              .map(id => {
                                const getIcon = (methodId) => {
                                  const icons = {
                                    bankTransfer: (
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                      </svg>
                                    ),
                                    vodafone: (
                                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#E60000' }}>
                                        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
                                        <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                        <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                      </svg>
                                    ),
                                    orange: (
                                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#FF6600' }}>
                                        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
                                        <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                        <circle cx="12" cy="12" r="4" fill="currentColor" opacity="0.4" />
                                      </svg>
                                    ),
                                    etisalat: (
                                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#00ADEF' }}>
                                        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
                                        <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                        <rect x="8" y="10" width="8" height="4" fill="currentColor" />
                                      </svg>
                                    ),
                                    instapay: (
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                      </svg>
                                    ),
                                    cash: (
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                      </svg>
                                    )
                                  };
                                  return icons[methodId] || icons.bankTransfer;
                                };
                                return (
                                  <label
                                    key={id}
                                    onClick={() => setSelectedMethodId(id)}
                                    className={`relative flex items-center gap-2 p-3 bg-blackC border rounded-lg cursor-pointer transition-all group ${selectedMethodId === id ? 'border-primary bg-primary/10' : 'border-gray-dark hover:border-primary/50'}`}
                                  >
                                    <input
                                      type="radio"
                                      name="method_id"
                                      value={id}
                                      checked={selectedMethodId === id}
                                      onChange={() => setSelectedMethodId(id)}
                                      className="sr-only"
                                    />
                                    <div className="text-primary group-hover:text-primary-dark transition-colors">
                                      {getIcon(id)}
                                    </div>
                                    <span className="text-cream text-sm font-medium flex-1">
                                      {id.charAt(0).toUpperCase() + id.slice(1).replace(/([A-Z])/g, ' $1')}
                                    </span>
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${selectedMethodId === id ? 'border-primary bg-primary' : 'border-gray-dark'}`}>
                                      {selectedMethodId === id && (
                                        <div className="w-2 h-2 rounded-full bg-blackC" />
                                      )}
                                    </div>
                                  </label>
                                );
                              })
                            }
                          </div>
                          <p className="text-gray-silver text-xs">
                            {language === 'en'
                              ? 'Select a payment method type. Each type can only be added once.'
                              : 'اختر نوع طريقة الدفع. يمكن إضافة كل نوع مرة واحدة فقط.'
                            }
                          </p>
                        </div>
                      )}

                      {/* English Section */}
                      <div className="bg-charcoal border border-gray-dark rounded-lg p-5">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-dark">
                          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <h3 className="font-display text-lg text-cream">English</h3>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-light mb-2">
                              {language === 'en' ? 'Title' : 'العنوان'}
                            </label>
                            <input
                              type="text"
                              name="title_en"
                              defaultValue={editingPaymentMethod.isNew ? '' : editingPaymentMethod.title?.en}
                              className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder={language === 'en' ? 'e.g., Bank Transfer' : 'مثال: تحويل بنكي'}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-light mb-2">
                              {language === 'en' ? 'Description' : 'الوصف'}
                            </label>
                            <input
                              type="text"
                              name="description_en"
                              defaultValue={editingPaymentMethod.isNew ? '' : editingPaymentMethod.description?.en}
                              className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder={language === 'en' ? 'e.g., Direct bank transfer to our account' : 'مثال: تحويل مباشر لحسابنا البنكي'}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-light mb-2">
                              {language === 'en' ? 'Payment Details' : 'تفاصيل الدفع'}
                            </label>
                            <textarea
                              name="details_en"
                              defaultValue={editingPaymentMethod.isNew ? '' : editingPaymentMethod.details?.en}
                              rows="4"
                              className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors font-mono text-sm"
                              placeholder={language === 'en' ? 'Bank: CIB\nAccount: 123456789\nIBAN: EG12...' : 'البنك: البنك التجاري\nالحساب: 123456789\nالآيبان: EG12...'}
                              required
                            />
                            <p className="text-gray-silver text-xs mt-1">
                              {language === 'en' ? 'Enter payment details (account numbers, phone numbers, etc.)' : 'أدخل تفاصيل الدفع (أرقام الحسابات، أرقام الهواتف، إلخ)'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Arabic Section */}
                      <div className="bg-charcoal border border-gray-dark rounded-lg p-5">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-dark">
                          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <h3 className="font-display text-lg text-cream">العربية</h3>
                        </div>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-light mb-2">
                              {language === 'en' ? 'Title' : 'العنوان'}
                            </label>
                            <input
                              type="text"
                              name="title_ar"
                              defaultValue={editingPaymentMethod.isNew ? '' : editingPaymentMethod.title?.ar}
                              className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder={language === 'en' ? 'e.g., تحويل بنكي' : 'مثال: تحويل بنكي'}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-light mb-2">
                              {language === 'en' ? 'Description' : 'الوصف'}
                            </label>
                            <input
                              type="text"
                              name="description_ar"
                              defaultValue={editingPaymentMethod.isNew ? '' : editingPaymentMethod.description?.ar}
                              className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              placeholder={language === 'en' ? 'e.g., تحويل مباشر لحسابنا البنكي' : 'مثال: تحويل مباشر لحسابنا البنكي'}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-light mb-2">
                              {language === 'en' ? 'Payment Details' : 'تفاصيل الدفع'}
                            </label>
                            <textarea
                              name="details_ar"
                              defaultValue={editingPaymentMethod.isNew ? '' : editingPaymentMethod.details?.ar}
                              rows="4"
                              className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors font-mono text-sm"
                              placeholder={language === 'en' ? 'البنك: البنك التجاري\nالحساب: 123456789\nالآيبان: EG12...' : 'البنك: البنك التجاري\nالحساب: 123456789\nالآيبان: EG12...'}
                              required
                            />
                            <p className="text-gray-silver text-xs mt-1">
                              {language === 'en' ? 'Enter payment details in Arabic' : 'أدخل تفاصيل الدفع بالعربية'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Settings */}
                    <div className="space-y-6">
                      {/* Status & Order */}
                      <div className="bg-charcoal border border-gray-dark rounded-lg p-5">
                        <h3 className="font-display text-lg text-cream mb-4 flex items-center gap-2">
                          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {language === 'en' ? 'Settings' : 'الإعدادات'}
                        </h3>
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center justify-between p-3 bg-blackC border border-gray-dark rounded-lg hover:border-primary/30 transition-colors">
                              <span className="text-gray-light font-medium">
                                {language === 'en' ? 'Active' : 'نشط'}
                              </span>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  name="isActive"
                                  defaultChecked={editingPaymentMethod.isNew ? true : editingPaymentMethod.isActive}
                                  className="sr-only peer"
                                />
                                <div className="w-12 h-6 bg-gray-dark peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                              </label>
                            </div>
                            <p className="text-gray-silver text-xs mt-2">
                              {language === 'en' ? 'Only active methods will be shown to customers' : 'سيتم عرض الطرق النشطة فقط للعملاء'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-light mb-2">
                              {language === 'en' ? 'Display Order' : 'ترتيب العرض'}
                            </label>
                            <input
                              type="number"
                              name="order"
                              defaultValue={editingPaymentMethod.isNew ? paymentMethods.length : (editingPaymentMethod.order || 0)}
                              min="0"
                              className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                            />
                            <p className="text-gray-silver text-xs mt-1">
                              {language === 'en' ? 'Lower numbers appear first' : 'الأرقام الأقل تظهر أولاً'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Info Box */}
                      <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="text-primary font-medium text-sm mb-1">
                              {language === 'en' ? 'Tips' : 'نصائح'}
                            </p>
                            <ul className="text-gray-silver text-xs space-y-1 list-disc list-inside">
                              <li>{language === 'en' ? 'Use clear, concise titles' : 'استخدم عناوين واضحة ومختصرة'}</li>
                              <li>{language === 'en' ? 'Include all necessary payment details' : 'قم بتضمين جميع تفاصيل الدفع اللازمة'}</li>
                              <li>{language === 'en' ? 'Keep details format consistent' : 'حافظ على تنسيق التفاصيل متسقاً'}</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-dark">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingPaymentMethod(null);
                        setSelectedMethodId('');
                      }}
                      className="px-6 py-3 bg-charcoal border border-gray-dark text-gray-silver rounded-lg hover:bg-gray-dark hover:text-cream transition-colors"
                    >
                      {language === 'en' ? 'Cancel' : 'إلغاء'}
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-3 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all flex items-center gap-2"
                    >
                      {editingPaymentMethod.isNew ? (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          {language === 'en' ? 'Create Payment Method' : 'إنشاء طريقة الدفع'}
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {language === 'en' ? 'Save Changes' : 'حفظ التغييرات'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Add/Edit Policy Section Modal */}
          {editingPolicySection && (
            <div className="fixed inset-0 bg-blackC/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-dark border border-gray-dark rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8">
                {/* Header */}
                <div className="p-6 border-b border-gray-dark flex justify-between items-center sticky top-0 bg-dark z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                      <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="font-display text-2xl text-cream">
                        {editingPolicySection.isNew
                          ? (language === 'en' ? 'Add Policy Section' : 'إضافة قسم سياسة')
                          : (language === 'en' ? 'Edit Policy Section' : 'تعديل قسم السياسة')
                        }
                      </h2>
                      <p className="text-gray-silver text-sm">
                        {language === 'en' ? 'Configure policy section details' : 'تكوين تفاصيل قسم السياسة'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditingPolicySection(null)}
                    className="w-10 h-10 rounded-lg bg-charcoal flex items-center justify-center text-gray-light hover:text-cream hover:bg-gray-dark transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                      const formData = new FormData(e.target);
                      const subsections = [];

                      // Collect all subsections
                      let index = 0;
                      while (formData.get(`subsection_subtitle_${index}`)) {
                        subsections.push({
                          subtitle: formData.get(`subsection_subtitle_${index}`),
                          description: formData.get(`subsection_description_${index}`),
                          order: parseInt(formData.get(`subsection_order_${index}`)) || index
                        });
                        index++;
                      }

                      const data = {
                        title: formData.get('title'),
                        category: formData.get('category'),
                        order: parseInt(formData.get('order')) || 0,
                        isActive: formData.get('isActive') === 'on',
                        subsections
                      };

                      if (editingPolicySection.isNew) {
                        const res = await api.post('/policy-sections', data);
                        setPolicySections(prev => [...prev, res.data]);
                        await alert(
                          language === 'en' ? 'Policy section created successfully!' : 'تم إنشاء قسم السياسة بنجاح!',
                          language === 'en' ? 'Success' : 'نجاح',
                          'success'
                        );
                      } else {
                        const res = await api.patch(`/policy-sections/${editingPolicySection._id}`, data);
                        setPolicySections(prev => prev.map(s => s._id === res.data._id ? res.data : s));
                        await alert(
                          language === 'en' ? 'Policy section updated successfully!' : 'تم تحديث قسم السياسة بنجاح!',
                          language === 'en' ? 'Success' : 'نجاح',
                          'success'
                        );
                      }
                      setEditingPolicySection(null);
                    } catch (error) {
                      await alert(
                        error.response?.data?.error || (language === 'en'
                          ? (editingPolicySection.isNew ? 'Error creating policy section' : 'Error updating policy section')
                          : (editingPolicySection.isNew ? 'خطأ في إنشاء قسم السياسة' : 'خطأ في تحديث قسم السياسة')
                        ),
                        language === 'en' ? 'Error' : 'خطأ',
                        'error'
                      );
                    }
                  }}
                  className="p-6 space-y-6"
                >
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">
                        {language === 'en' ? 'Title' : 'العنوان'} *
                      </label>
                      <input
                        type="text"
                        name="title"
                        defaultValue={editingPolicySection.isNew ? '' : editingPolicySection.title}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        placeholder={language === 'en' ? 'e.g., Booking Terms' : 'مثال: شروط الحجز'}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">
                        {language === 'en' ? 'Category' : 'الفئة'} *
                      </label>
                      <input
                        type="text"
                        name="category"
                        defaultValue={editingPolicySection.isNew ? '' : editingPolicySection.category}
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                        placeholder={language === 'en' ? 'e.g., Terms & Conditions' : 'مثال: الشروط والأحكام'}
                        required
                      />
                    </div>
                  </div>

                  {/* Subsections */}
                  <div className="bg-charcoal border border-gray-dark rounded-lg p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-display text-lg text-cream">
                        {language === 'en' ? 'Subsections' : 'الأقسام الفرعية'}
                      </h3>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingPolicySection(prev => ({
                            ...prev,
                            subsections: [...(prev.subsections || []), { subtitle: '', description: '', order: prev.subsections?.length || 0 }]
                          }));
                        }}
                        className="px-4 py-2 bg-primary/10 text-primary border border-primary/30 rounded-lg hover:bg-primary/20 transition-colors text-sm"
                      >
                        {language === 'en' ? '+ Add Subsection' : '+ إضافة قسم فرعي'}
                      </button>
                    </div>
                    <div className="space-y-4">
                      {(editingPolicySection.subsections || []).map((subsection, index) => (
                        <div key={index} className="bg-dark border border-gray-dark rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm text-gray-light font-medium">
                              {language === 'en' ? `Subsection ${index + 1}` : `القسم الفرعي ${index + 1}`}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingPolicySection(prev => ({
                                  ...prev,
                                  subsections: prev.subsections.filter((_, i) => i !== index)
                                }));
                              }}
                              className="text-red-400 hover:text-red-300 transition-colors"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-light mb-2">
                                {language === 'en' ? 'Subtitle' : 'العنوان الفرعي'} *
                              </label>
                              <input
                                type="text"
                                name={`subsection_subtitle_${index}`}
                                defaultValue={subsection.subtitle}
                                className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                                placeholder={language === 'en' ? 'e.g., Cancellation Policy' : 'مثال: سياسة الإلغاء'}
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-light mb-2">
                                {language === 'en' ? 'Description' : 'الوصف'} *
                              </label>
                              <textarea
                                name={`subsection_description_${index}`}
                                defaultValue={subsection.description}
                                rows="3"
                                className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                                placeholder={language === 'en' ? 'Enter description...' : 'أدخل الوصف...'}
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-light mb-2">
                                {language === 'en' ? 'Order' : 'الترتيب'}
                              </label>
                              <input
                                type="number"
                                name={`subsection_order_${index}`}
                                defaultValue={subsection.order || index}
                                min="0"
                                className="w-full bg-blackC border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      {(!editingPolicySection.subsections || editingPolicySection.subsections.length === 0) && (
                        <p className="text-gray-silver text-sm text-center py-4">
                          {language === 'en' ? 'No subsections added yet. Click "Add Subsection" to add one.' : 'لم تتم إضافة أقسام فرعية بعد. انقر على "إضافة قسم فرعي" لإضافة واحد.'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-light mb-2">
                        {language === 'en' ? 'Display Order' : 'ترتيب العرض'}
                      </label>
                      <input
                        type="number"
                        name="order"
                        defaultValue={editingPolicySection.isNew ? policySections.length : (editingPolicySection.order || 0)}
                        min="0"
                        className="w-full bg-charcoal border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-charcoal border border-gray-dark rounded-lg">
                      <span className="text-gray-light font-medium">
                        {language === 'en' ? 'Active' : 'نشط'}
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="isActive"
                          defaultChecked={editingPolicySection.isNew ? true : editingPolicySection.isActive}
                          className="sr-only peer"
                        />
                        <div className="w-12 h-6 bg-gray-dark peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex justify-end gap-3 pt-6 border-t border-gray-dark">
                    <button
                      type="button"
                      onClick={() => setEditingPolicySection(null)}
                      className="px-6 py-3 bg-charcoal border border-gray-dark text-gray-silver rounded-lg hover:bg-gray-dark hover:text-cream transition-colors"
                    >
                      {language === 'en' ? 'Cancel' : 'إلغاء'}
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-3 bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all flex items-center gap-2"
                    >
                      {editingPolicySection.isNew ? (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          {language === 'en' ? 'Create Section' : 'إنشاء القسم'}
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {language === 'en' ? 'Save Changes' : 'حفظ التغييرات'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Admin;
